import { useState } from "react";
import type { ReactNode } from "react";
import { ANALYSIS_CONFIDENCE_LABEL } from "@/lib/confidence/confidence-vocabulary";
import { chapterRef, getChapterByInternalId, TOTAL_CHAPTERS } from "@/lib/report/chapter-registry";
import { usePdfPackageIdentity } from "@/lib/report/package-context";


import {
  ShieldCheck,
  BadgeCheck,
  Scale,
  BookOpen,
  Building2,
  BarChart3,
  Landmark,
  FileWarning,
  Image as ImageIcon,
  MapPin,
  Clock,
  Sparkles,
  UserCheck,
  ArrowUpRight,
  Info,
  Wallet,
  TrendingDown,
  TrendingUp,
  Gauge,
  AlertTriangle,
  CheckCircle2,
  Percent,
  ReceiptText,
  ChevronDown,
  MinusCircle,
} from "lucide-react";
import {
  PackagePage,
  StatTile,
  ConfidenceGauge,
  DonutRing,
  SealMark,
  Eyebrow,
} from "./package-ui";
import type { VendorSnapshot } from "@/hooks/use-vendor-snapshot";
import { displayStatusLabel } from "@/lib/report/status-vocabulary";

/* Vendor Intelligence presentation vocabulary. The report-wide absence
   vocabulary stays unchanged everywhere else; inside this chapter the four
   customer-facing investigation states are the only wording shown. */
const VENDOR_STATUS_WORDS: Record<string, string> = {
  "Coverage Boundary": "Awaiting Provider",
  "Verification Pending": "Awaiting Provider",
  "Not Established": "No Evidence Found",
  "None Identified": "No Evidence Found",
  "Not Evidenced in File": "No Evidence Found",
};
function vendorStatusLabel(status: string): string {
  const shown = displayStatusLabel(status);
  return VENDOR_STATUS_WORDS[shown] ?? VENDOR_STATUS_WORDS[status] ?? shown;
}
import { ReportAccordion, AccordionMetric } from "@/components/report/report-accordion";
import {
  InvestigationStatusBadge,
  InvestigationStatusIcon,
  InvestigationCoverageNotice,
  investigationStatusLabel,
  investigationStatusMeaning,
  type InvestigationState,
} from "@/lib/vendor-intel/investigation-status";
import {
  ConclusionBasis,
  InvestigationLegend,
  legendEntriesFor,
} from "@/lib/vendor-intel/investigation-legend";
import {
  disclosureSentence,
  resolveDisclosure,
} from "@/lib/vendor-intel/provider-disclosure";

/* ─────────────────────────────────────────────────────────── */
/* Section shell + Empty state                                 */
/* ─────────────────────────────────────────────────────────── */

export function CGSection({
  id,
  number,
  eyebrow,
  title,
  intro,
  children,
  bare = false,
}: {
  id?: string;
  number?: string;
  eyebrow?: string;
  title: ReactNode;
  intro?: string;
  children: ReactNode;
  bare?: boolean;
}) {
  return (
    <PackagePage id={id} number={number} eyebrow={eyebrow ?? (number ? "Section" : undefined)} title={title} subtitle={intro} bare={bare}>

      {children}
    </PackagePage>
  );
}


export function EmptyStateCard({
  icon: Icon = Info,
  title,
  message,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title?: string;
  message: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-10 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-card shadow-card">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      {title && (
        <div className="mt-4 text-sm font-semibold text-navy">{title}</div>
      )}
      <p className="mt-1 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 1) Executive Verdict™                                       */
/* ─────────────────────────────────────────────────────────── */

export type ExecutiveVerdictData = {
  invoiceSubmitted: string;
  recommendedPayment: string;
  potentialSavings: string;
  savingsPct?: string;
  confidence: number;
  financialRisk: "Low" | "Medium" | "High" | "Unknown";
  recommendation: string;
};

export function ExecutiveVerdict({ v }: { v: ExecutiveVerdictData }) {
  const riskMeta =
    v.financialRisk === "High"
      ? {
          tone: "border-destructive/30 bg-destructive/5 text-destructive",
          caption: "Immediate action advised",
        }
      : v.financialRisk === "Medium"
      ? {
          tone: "border-warn/40 bg-fasttrack-soft text-warn",
          caption: "Negotiation recommended",
        }
      : v.financialRisk === "Low"
      ? {
          tone: "border-savings/30 bg-savings-soft text-savings",
          caption: "Minimal exposure",
        }
      : {
          tone: "border-border bg-secondary text-muted-foreground",
          caption: "Under review",
        };

  return (
    <PackagePage
      id="section-verdict"
      number="01"
      eyebrow="Section"
      title="Executive Verdict™"
      subtitle="A ten-second summary of what we found, how much you may save, and what to do next — signed off by the ClaimGuard Professional Review Team."
      aside={
        <div className="flex items-center gap-4">
          <div className="hidden text-right md:block">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Certified
            </div>
            <div className="mt-1 font-display text-lg text-navy">ClaimGuard™</div>
          </div>
          <SealMark label="Certified · ClaimGuard" size={96} tone="gold" />
        </div>
      }
    >
      {/* Hero verdict banner */}
      <div className="grid gap-6 rounded-2xl border border-border bg-gradient-to-br from-navy to-[oklch(0.28_0.09_265)] p-8 text-white shadow-elevated md:grid-cols-[minmax(0,1fr)_auto] md:p-10">
        <div className="min-w-0">
          <Eyebrow tone="gold">Recommendation</Eyebrow>
          <div className="mt-3 font-display text-3xl leading-tight md:text-4xl">
            {v.recommendation}
          </div>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
            Based on {v.confidence}% weighted analytical confidence across market benchmarks,
            statutory review, and Nova™ AI forensic analysis.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.22em] text-white/50">
            <div>
              <div>Invoice Total</div>
              <div className="mt-1 font-display text-lg font-normal normal-case tracking-normal text-white">
                {v.invoiceSubmitted}
              </div>
            </div>
            <div>
              <div>Settlement Goal</div>
              <div className="mt-1 font-display text-lg font-normal normal-case tracking-normal text-white">
                {v.recommendedPayment}
              </div>
            </div>
            <div>
              <div>Financial Risk</div>
              <div className="mt-1 font-display text-lg font-normal normal-case tracking-normal text-white">
                {v.financialRisk}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between gap-4">
          <div className="rounded-xl border border-fasttrack/30 bg-fasttrack/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-fasttrack">
            Total Supported Recovery
          </div>
          <div className="text-right">
            <div className="font-display text-5xl leading-none tabular-nums md:text-6xl">
              {v.potentialSavings.split(" – ")[0]}
              {v.potentialSavings.includes("–") && (
                <span className="block text-xl text-white/60">
                  – {v.potentialSavings.split(" – ")[1]}
                </span>
              )}
            </div>
            {v.savingsPct && (
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-white/60">
                {v.savingsPct}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI + gauge row */}
      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <StatTile
          label="Invoice Total"
          value={v.invoiceSubmitted}
          icon={Wallet}
          accent="navy"
          hint="Amount billed by the vendor"
        />
        <StatTile
          label="Settlement Goal"
          value={v.recommendedPayment}
          icon={BadgeCheck}
          accent="trust"
          hint="After identified adjustments"
        />
        <StatTile
          label="Total Supported Recovery"
          value={v.potentialSavings}
          icon={TrendingDown}
          accent="savings"
          hint={v.savingsPct}
        />
        <div className={`rounded-2xl border p-6 shadow-card ${riskMeta.tone}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] opacity-80">
              Financial Risk
            </div>
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-white/60">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 font-display text-3xl leading-none md:text-4xl">
            {v.financialRisk}
          </div>
          <div className="mt-2 text-[12px] leading-snug opacity-80">
            {riskMeta.caption}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-[240px_minmax(0,1fr)]">
        <div className="grid place-items-center rounded-2xl border border-border bg-card p-6 shadow-card">
          <ConfidenceGauge
            value={v.confidence}
            label={ANALYSIS_CONFIDENCE_LABEL}
            caption="Nova™ · weighted across sources"
          />
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <Eyebrow tone="navy">The Ten-Second Verdict</Eyebrow>
          <p className="mt-3 font-display text-2xl leading-snug text-navy md:text-[26px]">
            {v.recommendation === "Under review"
              ? "This invoice is currently under professional review. A final verdict will publish once analysis completes."
              : `We recommend you ${v.recommendation.toLowerCase()}. This deliverable contains the full evidence base — market benchmarks, statutes, carrier guidelines, and Nova™ AI findings — that supports every dollar of the ${v.potentialSavings} savings estimate.`}
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-border pt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            <div>Forensic Analysis</div>
            <div>Legal &amp; Regulatory</div>
            <div>Market Comparison</div>
          </div>
        </div>
      </div>
    </PackagePage>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 1b) Executive Dashboard                                     */
/* ─────────────────────────────────────────────────────────── */

export type ExecutiveDashboardData = {
  invoiceTotal: number;
  recommendedPayment: number;
  savingsLow: number;
  savingsHigh: number;
  confidence: number;
  severity: { high: number; medium: number; low: number };
  topIssues: Array<{ label: string; savings: number; impact: "High" | "Medium" | "Low" }>;
  evidence: {
    findings: number;
    laws: number;
    regulations: number;
    market: number;
    documents: number;
  };
};

export function ExecutiveDashboard({
  d,
  currency,
}: {
  d: ExecutiveDashboardData;
  currency: (n: number) => string;
}) {
  const totalFindings = d.severity.high + d.severity.medium + d.severity.low;
  const savingsMid = Math.round((d.savingsLow + d.savingsHigh) / 2);
  const savingsPct = d.invoiceTotal > 0 ? (savingsMid / d.invoiceTotal) * 100 : 0;

  return (
    <PackagePage
      id="section-dashboard"
      number="02"
      eyebrow="Section"
      title="Executive Dashboard"
      subtitle="A visual snapshot of the review: how the invoice breaks down, where issues concentrate, and what supporting evidence has been gathered."
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        {/* Left — savings composition */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <Eyebrow tone="navy">Invoice vs Recommended</Eyebrow>
              <div className="mt-2 font-display text-2xl text-navy">
                Where the money goes
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                % of Invoice Total
              </div>
              <div className="mt-1 font-display text-2xl text-savings tabular-nums">
                {savingsPct.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Stacked bar */}
          <div className="mt-6">
            <div className="flex items-baseline justify-between text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              <span>Settlement Goal</span>
              <span>Total Supported Recovery</span>
            </div>
            <div className="mt-2 flex h-10 overflow-hidden rounded-lg border border-border">
              <div
                className="flex items-center justify-end bg-trust px-3 text-[11px] font-semibold text-white"
                style={{
                  width: `${Math.max(0, Math.min(100, 100 - savingsPct))}%`,
                }}
              >
                {currency(d.recommendedPayment)}
              </div>
              <div
                className="flex items-center justify-end bg-savings px-3 text-[11px] font-semibold text-white"
                style={{ width: `${Math.max(0, Math.min(100, savingsPct))}%` }}
              >
                {currency(savingsMid)}
              </div>
            </div>
            <div className="mt-2 flex items-baseline justify-between text-[11px] font-semibold tabular-nums text-navy">
              <span>Invoice Total: {currency(d.invoiceTotal)}</span>
              <span className="text-savings">
                Total Supported Recovery: {currency(d.savingsLow)} – {currency(d.savingsHigh)}
              </span>
            </div>
          </div>

          {/* Top issues */}
          <div className="mt-8">
            <Eyebrow tone="navy">Top Issues Driving Recovery</Eyebrow>
            <div className="mt-4 space-y-3">
              {d.topIssues.length === 0 ? (
                <EmptyStateCard message="No applicable findings for this review." />
              ) : (
                d.topIssues.slice(0, 3).map((t, i) => {
                  const pct =
                    savingsMid > 0
                      ? Math.max(4, Math.min(100, (t.savings / (d.savingsHigh || 1)) * 100))
                      : 0;
                  const dot =
                    t.impact === "High"
                      ? "bg-destructive"
                      : t.impact === "Medium"
                      ? "bg-fasttrack"
                      : "bg-trust";
                  return (
                    <div key={i}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${dot}`}
                            aria-hidden
                          />
                          <span className="truncate font-medium text-navy">
                            {t.label}
                          </span>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="tabular-nums font-semibold text-savings">
                            {currency(t.savings)}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            {t.impact} impact
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full rounded-full bg-savings/70"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right — donut + coverage */}
        <div className="grid gap-6">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Findings by Severity</Eyebrow>
            <div className="mt-4">
              <DonutRing
                size={180}
                centerLabel="Findings"
                centerValue={totalFindings}
                segments={[
                  {
                    label: "High",
                    value: d.severity.high,
                    color: "var(--color-destructive)",
                  },
                  {
                    label: "Medium",
                    value: d.severity.medium,
                    color: "var(--color-fasttrack)",
                  },
                  {
                    label: "Low",
                    value: d.severity.low,
                    color: "var(--color-trust)",
                  },
                ]}
              />
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Evidence Coverage</Eyebrow>
            <ul className="mt-4 grid grid-cols-2 gap-3 text-sm">
              {[
                { label: "Findings", n: d.evidence.findings, Icon: Sparkles },
                { label: "State Laws", n: d.evidence.laws, Icon: Scale },
                { label: "Regulations", n: d.evidence.regulations, Icon: BookOpen },
                { label: "Market Refs", n: d.evidence.market, Icon: BarChart3 },
                { label: "Documents", n: d.evidence.documents, Icon: FileWarning },
                { label: ANALYSIS_CONFIDENCE_LABEL, n: d.confidence, Icon: Gauge, suffix: "%" },
              ].map(({ label, n, Icon, suffix }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 rounded-xl border border-border/70 bg-secondary/40 p-3"
                >
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-card text-trust shadow-card">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {label}
                    </div>
                    <div className="font-display text-lg text-navy tabular-nums">
                      {n}
                      {suffix ?? ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </PackagePage>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 1c) Executive Dashboard (Combined — single premium page)    */
/* ─────────────────────────────────────────────────────────── */

export type ExecutiveDashboardCombinedData = {
  verdict: ExecutiveVerdictData;
  dashboard: ExecutiveDashboardData;
  snapshot: {
    ocrComplete: boolean;
    marketComplete: boolean;
    negotiationReady: boolean;
    humanReviewed: boolean;
  };
  riskIndex: number; // 0-100
};

export function ExecutiveDashboardCombined({
  data,
  currency,
  bare = false,
}: {
  data: ExecutiveDashboardCombinedData;
  currency: (n: number) => string;
  bare?: boolean;
}) {

  const { verdict: v, dashboard: d, snapshot, riskIndex } = data;
  const totalFindings = d.severity.high + d.severity.medium + d.severity.low;
  const savingsMid = Math.round((d.savingsLow + d.savingsHigh) / 2);
  const savingsPct = d.invoiceTotal > 0 ? (savingsMid / d.invoiceTotal) * 100 : 0;

  const priority =
    v.financialRisk === "High"
      ? {
          label: "Critical",
          caption: "Immediate negotiation strongly advised.",
          tone: "border-destructive/30 bg-destructive/5 text-destructive",
          dot: "bg-destructive",
        }
      : v.financialRisk === "Medium"
        ? {
            label: "High",
            caption: "Negotiate before payment is issued.",
            tone: "border-warn/40 bg-warn/5 text-warn",
            dot: "bg-warn",
          }
        : v.financialRisk === "Low"
          ? {
              label: "Moderate",
              caption: "Minor adjustments recommended.",
              tone: "border-fasttrack/30 bg-fasttrack-soft text-fasttrack",
              dot: "bg-fasttrack",
            }
          : {
              label: "Under Review",
              caption: "Verdict pending completion of review.",
              tone: "border-border bg-secondary text-muted-foreground",
              dot: "bg-muted-foreground",
            };

  const riskBand =
    riskIndex >= 70
      ? { label: "Critical", tone: "text-destructive" }
      : riskIndex >= 45
        ? { label: "High", tone: "text-warn" }
        : riskIndex >= 20
          ? { label: "Moderate", tone: "text-fasttrack" }
          : { label: "Low", tone: "text-savings" };

  return (
    <PackagePage
      id="section-dashboard"
      number="01"
      eyebrow="Section"
      title="Executive Dashboard"
      subtitle="The full executive briefing on one page — how much, how risky, what was found, and what to do next."
      bare={bare}

      aside={
        <div className="flex items-center gap-4">
          <div className="hidden text-right md:block">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Certified
            </div>
            <div className="mt-1 font-display text-lg text-navy">ClaimGuard™</div>
          </div>
          <SealMark label="Certified · ClaimGuard" size={96} tone="gold" />
        </div>
      }
    >
      {/* Row 1 — Hero: Savings + Priority */}
      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-savings/25 bg-savings-soft p-7 shadow-card">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-savings text-white">
              <TrendingDown className="h-4 w-4" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-savings">
              Total Supported Recovery
            </div>
          </div>
          <div className="mt-4 font-display text-5xl leading-none tabular-nums text-savings md:text-6xl">
            {currency(d.savingsLow)}
            <span className="text-muted-foreground"> – </span>
            {currency(d.savingsHigh)}
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-savings">
            {savingsPct.toFixed(0)}% reduction
            <span className="text-muted-foreground font-normal">
              of {currency(d.invoiceTotal)} invoiced
            </span>
          </div>
        </div>

        <div className={`rounded-2xl border p-7 shadow-card ${priority.tone}`}>
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-white/70">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] opacity-80">
              Negotiation Priority
            </div>
          </div>
          <div className="mt-4 font-display text-5xl leading-none md:text-6xl">
            {priority.label}
          </div>
          <p className="mt-3 text-sm leading-snug opacity-90">{priority.caption}</p>
        </div>
      </div>

      {/* Row 2 — KPI strip (6 tiles) */}
      <div className="mt-5 grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        <MiniStat
          icon={ReceiptText}
          label="Invoice Total"
          value={currency(d.invoiceTotal)}
        />
        <MiniStat
          icon={BadgeCheck}
          label="Settlement Goal"
          value={currency(d.recommendedPayment)}
          tone="trust"
        />
        <MiniStat
          icon={Percent}
          label="Savings %"
          value={`${savingsPct.toFixed(1)}%`}
          tone="savings"
        />
        <MiniStat
          icon={Gauge}
          label={ANALYSIS_CONFIDENCE_LABEL}
          value={`${d.confidence}%`}
          tone="trust"
        />
        <MiniStat
          icon={Sparkles}
          label="Findings"
          value={`${totalFindings}`}
        />
        <MiniStat
          icon={FileWarning}
          label="Evidence Items"
          value={`${
            d.evidence.findings +
            d.evidence.laws +
            d.evidence.regulations +
            d.evidence.market +
            d.evidence.documents
          }`}
        />
      </div>

      {/* Row 3 — Severity donut + Evidence coverage */}
      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <Eyebrow tone="navy">Findings by Severity</Eyebrow>
          <div className="mt-4">
            <DonutRing
              size={180}
              centerLabel="Findings"
              centerValue={totalFindings}
              segments={[
                { label: "High", value: d.severity.high, color: "var(--color-destructive)" },
                { label: "Medium", value: d.severity.medium, color: "var(--color-fasttrack)" },
                { label: "Low", value: d.severity.low, color: "var(--color-trust)" },
              ]}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <Eyebrow tone="navy">Evidence Coverage</Eyebrow>
          <ul className="mt-4 grid grid-cols-2 gap-3 text-sm">
            {[
              { label: "Charges Reviewed", n: d.evidence.findings, Icon: Sparkles },
              { label: "Applicable Laws", n: d.evidence.laws, Icon: Scale },
              { label: "Regulations", n: d.evidence.regulations, Icon: BookOpen },
              { label: "Market Refs", n: d.evidence.market, Icon: BarChart3 },
              { label: "Documents", n: d.evidence.documents, Icon: FileWarning },
              {
                label: ANALYSIS_CONFIDENCE_LABEL,
                n: d.confidence,
                Icon: Gauge,
                suffix: "%" as const,
              },
            ].map(({ label, n, Icon, suffix }) => (
              <li
                key={label}
                className="flex items-center gap-3 rounded-xl border border-border/70 bg-secondary/40 p-3"
              >
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-card text-trust shadow-card">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {label}
                  </div>
                  <div className="font-display text-lg text-navy tabular-nums">
                    {n}
                    {suffix ?? ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Row 4 — Top Savings Drivers */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-6 shadow-card">
        <div className="flex items-center justify-between">
          <Eyebrow tone="navy">Top Savings Drivers</Eyebrow>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Highest-impact findings
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {d.topIssues.length === 0 ? (
            <EmptyStateCard message="No applicable findings for this review." />
          ) : (
            d.topIssues.slice(0, 3).map((t, i) => {
              const pct =
                d.savingsHigh > 0
                  ? Math.max(6, Math.min(100, (t.savings / d.savingsHigh) * 100))
                  : 0;
              const dot =
                t.impact === "High"
                  ? "bg-destructive"
                  : t.impact === "Medium"
                    ? "bg-fasttrack"
                    : "bg-trust";
              return (
                <div key={i}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
                      <span className="truncate font-medium text-navy">{t.label}</span>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="tabular-nums font-semibold text-savings">
                        {currency(t.savings)}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {t.impact} impact
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full bg-savings/70"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Row 5 — Review Snapshot */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-6 shadow-card">
        <div className="flex items-center justify-between">
          <Eyebrow tone="navy">Review Snapshot</Eyebrow>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            What we completed
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <SnapshotLine done={snapshot.ocrComplete} label="OCR Complete" />
          <SnapshotLine done={snapshot.marketComplete} label="Market Analysis Complete" />
          <SnapshotLine done={snapshot.negotiationReady} label="Negotiation Strategy Generated" />
          <SnapshotLine done={snapshot.humanReviewed} label="Human Reviewed" />
        </div>
      </div>

      {/* Row 6 — Overall Risk Meter */}
      <div id="risk-score" className="mt-5 rounded-2xl border border-border bg-card p-6 shadow-card">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="max-w-md">
            <Eyebrow tone="navy">Overall Invoice Risk</Eyebrow>
            <div className={`mt-2 font-display text-3xl ${riskBand.tone}`}>
              {riskBand.label}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Derived from severity and volume of findings across this invoice.
            </p>
          </div>
          <div className="w-full md:w-96">
            <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
              <span>Critical</span>
            </div>
            <div className="relative h-3 overflow-hidden rounded-full bg-gradient-to-r from-savings via-fasttrack via-warn to-destructive">
              <div
                className="absolute top-1/2 h-5 w-1.5 -translate-y-1/2 rounded-full bg-navy shadow-elevated ring-2 ring-white"
                style={{ left: `calc(${Math.max(0, Math.min(100, riskIndex))}% - 3px)` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Risk index</span>
              <span className={`font-semibold ${riskBand.tone}`}>
                {Math.round(riskIndex)} / 100
              </span>
            </div>
          </div>
        </div>
      </div>
    </PackagePage>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
  tone = "navy",
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone?: "navy" | "trust" | "savings";
}) {
  const iconCls =
    tone === "savings"
      ? "bg-savings-soft text-savings"
      : tone === "trust"
        ? "bg-trust-soft text-trust"
        : "bg-secondary text-navy";
  const valCls =
    tone === "savings" ? "text-savings" : tone === "trust" ? "text-navy" : "text-navy";
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-center gap-2">
        <div className={`grid h-7 w-7 place-items-center rounded-lg ${iconCls}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-[9px] font-semibold uppercase leading-tight tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
      </div>
      <div className={`mt-2 font-display text-xl leading-tight tabular-nums ${valCls}`}>
        {value}
      </div>
    </div>
  );
}

function SnapshotLine({ done, label }: { done: boolean; label: string }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
        done
          ? "border-savings/25 bg-savings-soft text-savings"
          : "border-border bg-secondary text-muted-foreground"
      }`}
    >
      {done ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        <Info className="h-4 w-4" />
      )}
      <span className="font-semibold">{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 2) Carrier Guideline Analysis                               */
/* ─────────────────────────────────────────────────────────── */

export type CarrierGuideline = {
  carrier: string;
  guideline: string;
  requirement: string;
  supportsFinding?: string;
  confidence?: number;
};

export function CarrierGuidelineAnalysis({
  guidelines,
  number,
  bare = false,
  carrierName = null,
  carrierSource = null,
}: {
  guidelines: CarrierGuideline[];
  number: string;
  bare?: boolean;
  /** Carrier of record, when one was explicitly identified in submitted materials. */
  carrierName?: string | null;
  /** Where the carrier identity came from, for lineage display. */
  carrierSource?: string | null;
}) {
  return (
    <CGSection
      id="section-carrier"
      number={number}
      eyebrow="Section"
      title="Carrier Guideline Analysis"
      intro="Insurance-carrier policies and internal guidelines that support the recommended findings."
      bare={bare}
    >
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
        <Eyebrow tone="navy">Carrier identification</Eyebrow>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-secondary/40 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Carrier of record
            </div>
            <p className="mt-1 text-sm font-semibold text-navy">
              {carrierName ?? "Not identified in submitted materials"}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-secondary/40 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Identification source
            </div>
            <p className="mt-1 text-sm text-foreground/80">
              {carrierSource ??
                "No carrier name appears on the invoice, in the extracted document text, or in the claim submission."}
            </p>
          </div>
        </div>
      </div>

      {guidelines.length === 0 ? (

        <>
          <div className="mt-6 rounded-2xl border-2 border-navy/20 bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Evidence validation</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              Carrier-specific guidance is unavailable for this claim
            </div>
            <p className="mt-2 max-w-3xl text-sm text-foreground/80">
              No verified carrier guideline was identified for this claim. This chapter therefore
              makes no carrier-based determination, and no carrier requirement, approval standard,
              or documentation obligation is asserted anywhere in this report.
            </p>
            <p className="mt-3 max-w-3xl text-sm text-foreground/80">
              State law, administrative regulations, industry practice, and AI-generated inference
              are <span className="font-semibold text-navy">not</span> used as substitutes for
              verified carrier guidance. Those layers are reported in their own chapters and stand
              on their own authority; nothing in this chapter treats them as carrier policy.
            </p>
          </div>
          <MethodologyPanel

            title="What we review under carrier guidelines"
            subtitle="Every claim is screened against the carrier's own written policy layer before findings are finalized."
            items={[
              {
                icon: ShieldCheck,
                label: "Policy Bulletins",
                description: "Active claim-handling bulletins and service-provider directives issued by the carrier.",
              },
              {
                icon: ReceiptText,
                label: "Fee Schedules",
                description: "Approved rate cards, storage caps, and administrative fee ceilings referenced in claim files.",
              },
              {
                icon: UserCheck,
                label: "Adjuster Guidelines",
                description: "Internal instructions governing authorizations, reimbursements, and escalation thresholds.",
              },
            ]}
          />
          <MethodologyPanel
            title="Sources within scope of this review"
            subtitle="Carrier guidance is only reported from the source types below, and only where ClaimGuard has indexed coverage at the time of review."
            items={[
              {
                icon: BookOpen,
                label: "Published bulletins",
                description: "Carrier-issued claim-handling bulletins and service-provider directives available at time of review.",
              },
              {
                icon: ReceiptText,
                label: "Filed fee schedules",
                description: "State-filed and carrier-published rate schedules, storage caps, and reimbursement ceilings.",
              },
              {
                icon: Building2,
                label: "Prior claim references",
                description: "Precedent handling patterns from prior ClaimGuard reviews involving comparable carrier-vendor pairings.",
              },
            ]}
          />
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Why no carrier policies applied</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              An absence of carrier evidence — not an absence of investigation
            </div>
            <p className="mt-2 max-w-3xl text-sm text-foreground/80">
              A carrier-specific policy is only surfaced when a published bulletin, filed fee
              schedule, or documented adjuster guideline directly addresses a charge on this
              invoice. That standard was not met on this claim for the reason recorded below.
            </p>
            <ul className="mt-4 grid gap-3">
              {(carrierName
                ? [
                    {
                      icon: Info,
                      label: "No applicable published guidance identified",
                      text: `${carrierName} is the carrier of record on this claim, but no published bulletin, filed fee schedule, or documented adjuster guideline addressing the charges on this invoice was located. The finding remains open rather than fabricated.`,
                    },
                  ]
                : [
                    {
                      icon: AlertTriangle,
                      label: "No carrier of record",
                      text: "No insurance carrier or payer was identified on the invoice, in the extracted document text, or in the claim submission. With no carrier of record, there is no carrier-specific policy layer to apply to these charges.",
                    },
                  ]
              ).map((r) => (
                <li key={r.label} className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-card text-navy shadow-card">
                    <r.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {r.label}
                    </div>
                    <p className="mt-1 text-sm text-foreground/80">{r.text}</p>
                  </div>
                </li>
              ))}
            </ul>

          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">How the review continued</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              A complete investigation was performed on other authoritative layers
            </div>
            <p className="mt-2 max-w-3xl text-sm text-foreground/80">
              The absence of a carrier-specific policy does not weaken the overall review. Three
              independent evidence layers continued to run on this invoice and are documented
              elsewhere in this package. They are reported under their own authority and are not
              presented as, or in place of, carrier guidance.
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {[
                {
                  icon: Landmark,
                  label: "Applicable statutes",
                  text: `State law analysis in ${chapterRef("chapter-04")} remains fully in force and governs notice, lien, and disposition rights.`,
                },
                {
                  icon: BookOpen,
                  label: "Applicable regulations",
                  text: `Administrative rules and industry regulations in ${chapterRef("chapter-06")} govern documentation, itemization, and authorization.`,
                },
                {
                  icon: BarChart3,
                  label: "Market benchmarks",
                  text: `Local market comparison in ${chapterRef("chapter-03")} independently tests each rate against comparable providers.`,
                },


              ].map((it) => (
                <div key={it.label} className="rounded-xl border border-border bg-secondary/40 p-4">
                  <div className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-card text-navy shadow-card">
                      <it.icon className="h-4 w-4" />
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {it.label}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-foreground/80">{it.text}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Analysis Confidence impact</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              Analysis Confidence remains unchanged
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                {
                  icon: Gauge,
                  label: "Statute-backed findings",
                  text: "Analysis Confidence remains unchanged because these findings are supported by applicable statutes rather than carrier policy.",
                },
                {
                  icon: Scale,
                  label: "Market-based findings",
                  text: "Analysis Confidence remains unchanged because independent market benchmarks do not rely on carrier authorization.",
                },
                {
                  icon: Sparkles,
                  label: "Carrier-only findings",
                  text: "No carrier-only findings were asserted because no applicable carrier policy was identified for this claim.",
                },
              ].map((it) => (
                <div key={it.label} className="rounded-xl border border-border bg-secondary/40 p-4">
                  <div className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-card text-navy shadow-card">
                      <it.icon className="h-4 w-4" />
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {it.label}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-foreground/80">{it.text}</p>
                </div>
              ))}
            </div>
          </div>
          <CoverageNote
            icon={Info}
            message="No applicable carrier-specific bulletin or fee schedule was identified for the charges on this invoice. The report's statutory, regulatory, and independent market analyses therefore remain the primary basis for the findings and recommendations."
          />
        </>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {guidelines.map((g, i) => (
            <article
              key={i}
              className="rounded-2xl border border-border bg-card p-6 shadow-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-trust">
                    {g.carrier}
                  </div>
                  <div className="mt-1 font-display text-xl text-navy">{g.guideline}</div>
                </div>
                <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
              </div>
              <p className="mt-3 text-sm text-foreground/80">{g.requirement}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                {g.supportsFinding && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-trust-soft px-2.5 py-1 text-[11px] font-medium text-trust">
                    Supports {g.supportsFinding}
                  </span>
                )}
                {typeof g.confidence === "number" && (
                  <span className="ml-auto text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.confidence}% confidence
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </CGSection>
  );
}

/* Shared supporting panels for short chapters ────────────── */

export function MethodologyPanel({
  title,
  subtitle,
  items,
  eyebrow = "Methodology",
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  items: Array<{
    icon?: React.ComponentType<{ className?: string }>;
    label: string;
    description: string;
  }>;
}) {
  return (
    <div className="mt-10 mb-4 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
      <Eyebrow tone="navy">{eyebrow}</Eyebrow>
      <div className="mt-1 font-display text-xl font-semibold text-navy">{title}</div>
      {subtitle && (
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{subtitle}</p>
      )}
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {items.map((it) => {
          const Icon = it.icon ?? Info;
          return (
            <div
              key={it.label}
              className="rounded-xl border border-border bg-secondary/40 p-4"
            >
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-card text-navy shadow-card">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {it.label}
                </div>
              </div>
              <p className="mt-2 text-sm text-foreground/80">{it.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CoverageNote({
  icon: Icon = Info,
  message,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  message: string;
}) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-card text-navy shadow-card">
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-sm text-foreground/80">{message}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 3) Vendor Intelligence                                      */
/* ─────────────────────────────────────────────────────────── */

export type VendorIntelligenceData = {
  vendor: string;
  riskScore: number | null;
  overchargeFrequencyPct: number | null;
  averageSavings: string | null;
  previousReviews: number | null;
  marketPosition: string | null;
  invoiceType?: string | null;
  serviceAddress?: string | null;
};

/**
 * Future-API-ready extras for Chapter 08. Every field is optional and
 * nullable: a field is rendered ONLY when a real value is supplied, so
 * new providers (Google Business Profile, SoS, licensing authorities,
 * BBB, FMCSA, ClaimGuard history, market intelligence, ordinances,
 * court records) can populate additional rows without a redesign.
 */
export type VendorContextIntel = {
  /** Years the vendor has operated, when a source can establish it. */
  yearsInBusiness?: number | string | null;
  /** e.g. Towing, Vehicle Storage, Heavy Duty Towing, Recovery. */
  servicesListed?: string[] | null;
  /** e.g. Indoor, Outdoor, Secure Lot. */
  storageType?: string | null;
  distanceFromLossLocation?: string | null;
  distanceFromRepairFacility?: string | null;
  distanceFromVehicleOwner?: string | null;
  /** e.g. { count: 8, radiusMiles: 10 } → "8 comparable facilities within 10 miles". */
  comparableFacilities?: { count: number; radiusMiles: number } | null;
};

export type VendorHistoryIntel = {
  invoicesReviewed?: number | null;
  averageNegotiatedSavings?: string | null;
  repeatFindings?: string[] | null;
  historicalSuccessRate?: string | null;
  averageStorageRate?: string | null;
  highestStorageRateReviewed?: string | null;
  averageTowCharge?: string | null;
  averageAdministrativeFee?: string | null;
  averageDailyStorageFee?: string | null;
  mostCommonQuestionableCharges?: string[] | null;
  mostFrequentlyReducedCharges?: string[] | null;
  historicalNegotiatedReduction?: string | null;
  vendorRiskScore?: string | null;
  vendorRiskLevel?: "high" | "low" | null;
  claimGuardConfidenceScore?: string | null;
  averageDaysInStorage?: string | number | null;
  typicalNegotiationOutcome?: string | null;
  commonNegotiationArguments?: string[] | null;
};

export type MarketComparisonRow = {
  label: string;
  invoiceValue: string | null;
  areaAverage: string | null;
  difference: string | null;
  /** Drives the row badge; omit when the posture is not established. */
  position?: "above" | "below" | "at" | "outlier" | null;
};

export type MarketIntel = {
  comparisons?: MarketComparisonRow[] | null;
  vehicleTypeAdjustment?: string | null;
  storageClass?: string | null;
  regionalConfidenceScore?: string | null;
  marketConfidence?: string | null;
  dataSampleSize?: string | number | null;
  nearbyVendorsCompared?: number | null;
  historicalClaimGuardComparisons?: number | null;
  zipCodeMarketAnalyzed?: string | null;
  /** e.g. 25 → "25 Miles". */
  searchRadiusMiles?: number | string | null;
  /** e.g. "July 2026". */
  marketSnapshotDate?: string | null;
  /** ISO timestamp of when the market data was collected. */
  dataCollectedAt?: string | null;

};

export type VendorIntelExtras = {
  context?: VendorContextIntel | null;
  history?: VendorHistoryIntel | null;
  market?: MarketIntel | null;
};


/**
 * Approved source-status vocabulary for the Vendor Intelligence Sources
 * section. Semantics must not be used interchangeably — see prompt spec.
 */
type SourceStatus =
  | "Verified"
  | "Matched"
  | "Partially Verified"
  | "Not Verified"
  | "No Records Identified"
  | "Not Connected"
  | "Unavailable"
  | "Provider Error"
  | "Unsupported"
  | "Invoice-Derived"
  | "Internally Classified"
  | "Public Reference"
  | "Not Applicable"
  | "Reviewed"
  | "High Confidence"
  // SAB-004: vendor investigation coverage bands (source breadth, not confidence)
  | "Broad Coverage"
  | "Partial Coverage"
  | "Limited Coverage"
  | "Partial Match"
  | "Market Average"
  | "Historical Data"
  | "Verified License"
  | "Good Standing"
  | "Expired"
  | "Suspended"
  | "High Risk"
  | "Low Risk"
  | "Market Outlier"
  | "Above Market"
  | "Below Market";

function StatusBadge({ status }: { status: SourceStatus }) {
  const POSITIVE = "bg-emerald-50 text-emerald-800 border-emerald-200";
  const NEUTRAL = "bg-secondary/60 text-foreground/70 border-border";
  const MUTED = "bg-secondary/60 text-foreground/60 border-border";
  const INFO = "bg-sky-50 text-sky-800 border-sky-200";
  const CAUTION = "bg-amber-50 text-amber-800 border-amber-200";
  const ADVERSE = "bg-red-50 text-red-800 border-red-200";
  const tone: Record<SourceStatus, string> = {
    Verified: POSITIVE,
    Matched: POSITIVE,
    "Partially Verified": CAUTION,
    "Not Verified": NEUTRAL,
    "No Records Identified": NEUTRAL,
    "Not Connected": MUTED,
    Unavailable: MUTED,
    "Provider Error": MUTED,
    Unsupported: MUTED,
    "Invoice-Derived": INFO,
    "Internally Classified": INFO,
    "Public Reference": INFO,
    "Not Applicable": MUTED,
    Reviewed: POSITIVE,
    "High Confidence": POSITIVE,
    "Broad Coverage": POSITIVE,
    "Partial Coverage": NEUTRAL,
    "Limited Coverage": NEUTRAL,
    "Partial Match": CAUTION,
    "Market Average": INFO,
    "Historical Data": INFO,
    "Verified License": POSITIVE,
    "Good Standing": POSITIVE,
    Expired: CAUTION,
    Suspended: ADVERSE,
    "High Risk": ADVERSE,
    "Low Risk": POSITIVE,
    "Market Outlier": ADVERSE,
    "Above Market": CAUTION,
    "Below Market": INFO,
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${tone[status]}`}
    >
      {vendorStatusLabel(status)}
    </span>
  );
}


/* ── Vendor Intelligence source model ─────────────────────── */

type SourceField = {
  label: string;
  value: string;
  status?: SourceStatus;
  sourceLabel?: string;
  href?: string;
};

type SourceEntry = {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  /** Table status badge. */
  status: SourceStatus;
  /** One concise sentence shown in the Sources Reviewed table. */
  result: string;
  /** What was reviewed at this source. */
  reviewed: string[];
  /** Public-safe findings statement(s). */
  findings: string[];
  /** How the source affected this review. */
  impact: string;
  /** Detailed field-level evidence (collapsed by default). */
  fields: SourceField[];
  /** Optional extra detail node (license records, actions, coverage). */
  extra?: ReactNode;
  /** When present, the source appears in "Information Not Verified". */
  unverifiedReason?: string | null;
  /** True when an adverse indicator was identified. */
  adverse?: boolean;
  /** True when this source positively verified the vendor. */
  verified?: boolean;
};

const vstr = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = typeof v === "number" ? String(v) : String(v);
  return s.trim() ? s.trim() : null;
};
const vcap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const vtitleize = (s: string) =>
  s
    .split("_")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
const vdate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : null;

function vendorCategoryFromInvoiceType(t?: string | null): string {
  const s = (t ?? "").toLowerCase();
  if (/tow|storage|impound|wrecker|lien/.test(s)) return "Towing Service";
  if (/medical|hospital|clinic/.test(s)) return "Medical Service";
  if (/repair|auto|body/.test(s)) return "Auto Repair";
  if (/legal|attorney|law/.test(s)) return "Legal Services";
  return "";
}

/* Internal ClaimGuard history ─────────────────────────────── */

/** Renders a row only when a real value exists — never a placeholder. */
function intelField(
  label: string,
  value: string | number | null | undefined,
  status?: SourceStatus,
  sourceLabel?: string,
): SourceField | null {
  const s = vstr(value);
  if (!s) return null;
  return { label, value: s, status, sourceLabel };
}

function listValue(v: string[] | null | undefined): string | null {
  if (!v || v.length === 0) return null;
  return v.join(", ");
}

/**
 * Human-readable provider attribution for each evidence source. Adding a
 * future regulatory module (OSHA, EPA, DOT, …) only requires one entry
 * here — no structural change to the evidence table.
 */
const PROVIDER_LABELS: Record<string, string> = {
  google: "Google Places API",
  market: "ClaimGuard Market Intelligence",
  sos: "Secretary of State Business Registry",
  "state-licensing": "State Licensing Authority",
  licensing: "FMCSA (SAFER) Operating Authority",
  bbb: "Better Business Bureau Public Records",
  internal: "ClaimGuard Historical Database",
};

/** Per-provider accent used to color-code evidence rows. */
const PROVIDER_ACCENTS: Record<string, string> = {
  google: "bg-emerald-500",
  market: "bg-blue-500",
  sos: "bg-slate-400",
  "state-licensing": "bg-orange-500",
  licensing: "bg-orange-400",
  bbb: "bg-sky-400",
  internal: "bg-purple-500",
};

/** Relative confidence weight each evidence source contributes. */
const PROVIDER_WEIGHTS: Record<string, number> = {
  google: 25,
  market: 30,
  sos: 20,
  "state-licensing": 15,
  licensing: 15,
  bbb: 15,
  internal: 15,
};




function buildInternalEntry(
  data: VendorIntelligenceData | null,
  history: VendorHistoryIntel | null | undefined,
): SourceEntry {
  const h = history ?? null;
  const src = "ClaimGuard Historical Database";
  const badge: SourceStatus = "Historical Data";

  const fields = [
    intelField("Invoices Reviewed", h?.invoicesReviewed, badge, src),
    intelField("Average Negotiated Savings", h?.averageNegotiatedSavings, badge, src),
    intelField("Historical Negotiated Reduction", h?.historicalNegotiatedReduction, badge, src),
    intelField("Historical Success Rate", h?.historicalSuccessRate, badge, src),
    intelField("Average Storage Rate", h?.averageStorageRate, badge, src),
    intelField("Highest Storage Rate Reviewed", h?.highestStorageRateReviewed, badge, src),
    intelField("Average Daily Storage Fee", h?.averageDailyStorageFee, badge, src),
    intelField("Average Tow Charge", h?.averageTowCharge, badge, src),
    intelField("Average Administrative Fee", h?.averageAdministrativeFee, badge, src),
    intelField("Average Days in Storage", h?.averageDaysInStorage, badge, src),
    intelField("Repeat Findings", listValue(h?.repeatFindings), badge, src),
    intelField(
      "Most Common Questionable Charges",
      listValue(h?.mostCommonQuestionableCharges),
      badge,
      src,
    ),
    intelField(
      "Most Frequently Reduced Charges",
      listValue(h?.mostFrequentlyReducedCharges),
      badge,
      src,
    ),
    intelField(
      "Common Negotiation Arguments",
      listValue(h?.commonNegotiationArguments),
      badge,
      src,
    ),
    intelField("Typical Negotiation Outcome", h?.typicalNegotiationOutcome, badge, src),
    intelField(
      "Vendor Risk Score",
      h?.vendorRiskScore,
      h?.vendorRiskLevel === "high"
        ? "High Risk"
        : h?.vendorRiskLevel === "low"
          ? "Low Risk"
          : badge,
      src,
    ),
    intelField("ClaimGuard Confidence Score", h?.claimGuardConfidenceScore, badge, src),
  ].filter((f): f is SourceField => f !== null);

  const hasHistory = fields.length > 0;

  /**
   * Historical Reliability grades the depth and outcome quality of the
   * internal record. It evolves automatically as more vendor history is
   * collected; with no prior reviews it is truthfully "Pending".
   */
  const historicalReliability = (() => {
    const reviewed = typeof h?.invoicesReviewed === "number" ? h.invoicesReviewed : 0;
    if (!hasHistory || reviewed <= 0) return "Pending";
    if (reviewed < 3) return "Limited Data";
    const rate = Number(String(h?.historicalSuccessRate ?? "").replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(rate) || rate === 0) return "Average";
    if (rate >= 80) return "Excellent";
    if (rate >= 60) return "Good";
    return "Average";
  })();
  if (hasHistory) {
    fields.push({
      label: "Historical Reliability",
      value: historicalReliability,
      status: badge,
      sourceLabel: src,
    });
  }

  // When no prior review history exists the section still presents a
  // structured baseline panel rather than an empty container: each metric
  // is stated truthfully as "None" / "Not yet established".
  const baselineFields: SourceField[] = [
    { label: "Prior invoices reviewed", value: "None", sourceLabel: src },
    { label: "Negotiations completed", value: "None", sourceLabel: src },
    { label: "Average negotiated savings", value: "Not yet established", sourceLabel: src },
    { label: "Vendor risk trend", value: "Not enough historical data", sourceLabel: src },
    { label: "Historical confidence", value: "Pending future reviews", sourceLabel: src },
    { label: "Historical Reliability", value: "Pending", sourceLabel: src },
  ];


  const baselineNote = hasHistory ? undefined : (
    <p className="mt-3 text-xs text-foreground/70">
      ClaimGuard continuously builds vendor intelligence as additional invoices
      and negotiations are processed.
    </p>
  );

  return {
    key: "internal",
    icon: Building2,
    title: "ClaimGuard History",
    description:
      "ClaimGuard’s proprietary review history for this vendor — prior invoices, negotiated outcomes, recurring questionable charges, rate behavior, and risk posture. This intelligence compounds as additional claims are reviewed.",
    status: hasHistory ? "Historical Data" : "No Records Identified",
    result: hasHistory
      ? "Prior ClaimGuard review history identified for this vendor."
      : "First reviewed invoice for this vendor — baseline intelligence established.",
    reviewed: [
      "Prior invoices reviewed and negotiated outcomes",
      "Recurring questionable charge patterns",
      "Historical tow, storage, and administrative rate behavior",
      "Vendor risk posture and negotiation success rate",
    ],
    findings: [
      hasHistory
        ? `ClaimGuard has prior review history for ${data?.vendor || "this vendor"}; the figures below are drawn from that internal record.`
        : "This is the first ClaimGuard review recorded for this vendor; the baseline metrics below will populate as further invoices are processed.",
    ],
    impact: hasHistory
      ? "Historical vendor behavior was used to contextualize the charges on this invoice."
      : "No vendor-history inference was applied. Findings rest on independent evidence.",
    unverifiedReason: null,
    fields: hasHistory ? fields : baselineFields,
    extra: baselineNote,
  };

}

/* Market Intelligence ─────────────────────────────────────── */

function marketPositionBadge(
  position: MarketComparisonRow["position"],
): SourceStatus | undefined {
  switch (position) {
    case "above":
      return "Above Market";
    case "below":
      return "Below Market";
    case "at":
      return "Market Average";
    case "outlier":
      return "Market Outlier";
    default:
      return undefined;
  }
}

/** Human label for a single charge posture. */
function positionLabel(p: MarketComparisonRow["position"]): string | null {
  switch (p) {
    case "outlier":
      return "Significant Outlier";
    case "above":
      return "Above Market";
    case "below":
      return "Below Market";
    case "at":
      return "Market Average";
    default:
      return null;
  }
}

/** Percentile estimate derived from invoice vs. area-average ratios. */
function ratioFor(r: MarketComparisonRow): number | null {
  const parse = (v: string | null) => {
    if (!v) return null;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) && n !== 0 ? n : null;
  };
  const inv = parse(r.invoiceValue);
  const avg = parse(r.areaAverage);
  if (inv == null || avg == null || avg <= 0) return null;
  return inv / avg;
}

function percentileFromRatio(ratio: number): number {
  // Deterministic mapping: at-market ≈ 50th, 2× ≈ 95th+.
  const pct = 50 + (ratio - 1) * 45;
  return Math.max(1, Math.min(99, Math.round(pct)));
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

type MarketSeverity = "adverse" | "caution" | "neutral";

type MarketPositionSummaryRow = {
  label: string;
  value: string;
  severity: MarketSeverity;
};

/** Severity tone from an invoice/area-average ratio, using badge colors. */
function severityFromRatio(ratio: number | null): MarketSeverity {
  if (ratio == null) return "neutral";
  if (ratio >= 1.5) return "adverse";
  if (ratio > 1.1) return "caution";
  return "neutral";
}

/** ClaimGuard badge palettes reused for market severity emphasis. */
const MARKET_SEVERITY_TEXT: Record<MarketSeverity, string> = {
  adverse: "text-red-800",
  caution: "text-amber-800",
  neutral: "text-navy",
};

/**
 * Derives the executive Market Position Summary strictly from the
 * comparison rows already backing the table. Rows with no derivable
 * posture are omitted — never rendered as placeholders.
 */
function buildMarketPositionSummary(
  rows: MarketComparisonRow[],
): MarketPositionSummaryRow[] {
  if (rows.length === 0) return [];
  const out: MarketPositionSummaryRow[] = [];
  const ratios = rows
    .map((r) => ratioFor(r))
    .filter((n): n is number => n != null);
  const avg =
    ratios.length > 0
      ? ratios.reduce((a, b) => a + b, 0) / ratios.length
      : null;

  if (avg != null) {
    out.push({
      label: "Overall Pricing",
      value:
        avg >= 1.5 ? "High" : avg > 1.1 ? "Above Market" : avg < 0.9 ? "Below Market" : "Consistent with Market",
      severity: severityFromRatio(avg),
    });
  }

  const pick = (test: RegExp, label: string) => {
    const row = rows.find((r) => test.test(r.label));
    if (!row) return;
    const ratio = ratioFor(row);
    const value =
      ratio != null
        ? `${ordinal(percentileFromRatio(ratio))} Percentile`
        : positionLabel(row.position);
    if (value)
      out.push({
        label,
        value,
        severity:
          ratio != null
            ? severityFromRatio(ratio)
            : row.position === "outlier"
              ? "adverse"
              : row.position === "above"
                ? "caution"
                : "neutral",
      });
  };
  pick(/storage/i, "Storage");
  pick(/tow/i, "Tow Charges");
  pick(/labor/i, "Labor Rates");

  if (avg != null) {
    out.push({
      label: "Market Percentile",
      value: `${ordinal(percentileFromRatio(avg))} Percentile`,
      severity: severityFromRatio(avg),
    });
  }
  return out;
}

/**
 * Aggregate "Overall Invoice" summary row for the comparison table.
 * Only produced when every component figure is derivable from the rows
 * already displayed — never estimated.
 */
function buildOverallMarketRow(
  rows: MarketComparisonRow[],
): {
  invoice: string;
  average: string;
  difference: string;
  position: MarketComparisonRow["position"];
  invoiceNum: number;
  averageNum: number;
  differenceNum: number;
  ratio: number;
} | null {
  const parse = (v: string | null | undefined) => {
    if (!v) return null;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const pairs = rows
    .map((r) => ({ inv: parse(r.invoiceValue), avg: parse(r.areaAverage) }))
    .filter((p): p is { inv: number; avg: number } => p.inv != null && p.avg != null);
  if (pairs.length === 0) return null;
  const inv = pairs.reduce((a, p) => a + p.inv, 0);
  const avg = pairs.reduce((a, p) => a + p.avg, 0);
  if (avg <= 0) return null;
  const money = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
  const diff = inv - avg;
  const ratio = inv / avg;
  return {
    invoice: money(inv),
    average: money(avg),
    difference: `${diff >= 0 ? "+" : "−"}${money(Math.abs(diff))}`,
    position:
      ratio >= 2 ? "outlier" : ratio > 1.1 ? "above" : ratio < 0.9 ? "below" : "at",
    invoiceNum: inv,
    averageNum: avg,
    differenceNum: diff,
    ratio,
  };
}



function buildMarketEntry(market: MarketIntel | null | undefined): SourceEntry {
  const m = market ?? null;
  const src = "ClaimGuard Market Intelligence";
  const rows = (m?.comparisons ?? []).filter(
    (r) => vstr(r.invoiceValue) || vstr(r.areaAverage) || vstr(r.difference),
  );

  const fields = [
    intelField("Vehicle Type Adjustment", m?.vehicleTypeAdjustment, "Market Average", src),
    intelField("Storage Class", m?.storageClass, "Market Average", src),
    intelField("Regional Confidence Score", m?.regionalConfidenceScore, "High Confidence", src),
    intelField("Market Confidence", m?.marketConfidence, "High Confidence", src),
    intelField("Data Sample Size", m?.dataSampleSize, "Market Average", src),
    intelField(
      "Nearby Vendors Compared",
      m?.nearbyVendorsCompared != null ? `Compared against ${m.nearbyVendorsCompared} nearby vendors` : null,
      "Market Average",
      src,
    ),
    intelField(
      "Historical ClaimGuard Comparisons",
      m?.historicalClaimGuardComparisons,
      "Historical Data",
      src,
    ),
    intelField("ZIP Code Market Analyzed", m?.zipCodeMarketAnalyzed, "Market Average", src),
  ].filter((f): f is SourceField => f !== null);

  const hasMarket = rows.length > 0 || fields.length > 0;

  // Compact transparency metadata — each tile appears only when a real
  // value backs it, so the grid never renders placeholder cells.
  const metaItems = [
    {
      label: "Vendors Compared",
      value: vstr(m?.nearbyVendorsCompared ?? m?.dataSampleSize ?? null),
    },
    { label: "ZIP Code Analyzed", value: vstr(m?.zipCodeMarketAnalyzed) },
    {
      label: "Search Radius",
      value:
        m?.searchRadiusMiles != null && vstr(m.searchRadiusMiles)
          ? /mile/i.test(String(m.searchRadiusMiles))
            ? String(m.searchRadiusMiles)
            : `${m.searchRadiusMiles} Miles`
          : null,
    },
    { label: "Market Snapshot", value: vstr(m?.marketSnapshotDate) },
    {
      label: "Data Collected",
      value: m?.dataCollectedAt
        ? (() => {
            const d = new Date(m.dataCollectedAt as string);
            return Number.isNaN(d.getTime())
              ? null
              : `${d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
          })()
        : null,
    },
  ].filter((i): i is { label: string; value: string } => !!i.value);

  const metaGrid =
    metaItems.length > 0 ? (
      <div className="mt-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Market Analysis Metadata
        </div>
        <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {metaItems.map((i) => (
            <div
              key={i.label}
              className="rounded-xl border border-border bg-secondary/30 p-4"
            >
              <div className="text-[10px] font-semibold uppercase leading-snug tracking-[0.1em] text-muted-foreground">
                {i.label}
              </div>
              <div className="mt-1 font-display text-lg leading-snug text-navy">{i.value}</div>


            </div>
          ))}
        </div>
      </div>
    ) : null;

  const positionRows = buildMarketPositionSummary(rows);
  const overallRow = buildOverallMarketRow(rows);

  const extra =
    rows.length > 0 || metaGrid ? (
      <>
        {positionRows.length > 0 ? (
          <div className="mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Market Position Summary
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {positionRows.map((r) => (
                <div
                  key={r.label}
                  className="rounded-xl border border-border bg-secondary/30 p-4"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {r.label}
                  </div>
                  <div
                    className={`mt-1 font-display text-lg ${MARKET_SEVERITY_TEXT[r.severity]}`}
                  >
                    {r.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {rows.length > 0 ? (
          <div className="mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Invoice vs. Area Market
            </div>
            <div className="mt-2 overflow-x-auto rounded-xl border border-border bg-secondary/30">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    <th className="px-4 py-2 text-left font-semibold">Charge</th>
                    <th className="px-4 py-2 text-right font-semibold">Invoice</th>
                    <th className="px-4 py-2 text-right font-semibold">Area Average</th>
                    <th className="px-4 py-2 text-right font-semibold">Difference</th>
                    <th className="px-4 py-2 text-right font-semibold">Posture</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const badge = marketPositionBadge(r.position);
                    return (
                      <tr key={r.label}>
                        <td className="px-4 py-2.5 text-foreground/80">{r.label}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-foreground">
                          {vstr(r.invoiceValue) ?? "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right text-foreground/80">
                          {vstr(r.areaAverage) ?? "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-foreground">
                          {vstr(r.difference) ?? "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {badge ? <StatusBadge status={badge} /> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {overallRow ? (
                    <tr className="bg-secondary/60">
                      <td className="px-4 py-3 font-semibold text-navy">Overall Invoice</td>
                      <td className="px-4 py-3 text-right font-semibold text-navy">
                        {overallRow.invoice}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-foreground/80">
                        {overallRow.average}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-navy">
                        {overallRow.difference}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {marketPositionBadge(overallRow.position) ? (
                          <StatusBadge status={marketPositionBadge(overallRow.position)!} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ) : null}
                </tbody>

              </table>
            </div>
          </div>
        ) : null}
        {metaGrid}
      </>
    ) : undefined;


  return {
    key: "market",
    icon: TrendingUp,
    title: "Market Intelligence",
    description:
      "Comparison of the charges on this invoice against verified local market pricing — nearby tow and storage providers, regional labor rates, and ClaimGuard’s historical rate database for this market.",
    status: hasMarket ? "Market Average" : "No Records Identified",
    result: hasMarket
      ? "Invoice charges compared against verified local market pricing."
      : "Local market comparison data is not available for this claim.",
    reviewed: [
      "Local tow companies",
      "Local storage facilities",
      "Historical ClaimGuard data",
      "ZIP code market analysis",
      "Regional labor rates",
    ],
    findings: [
      hasMarket
        ? "Charge-level comparisons against the surrounding market are set out below."
        : "No verified local market pricing was available for this ZIP code at the time of review.",
    ],
    impact: hasMarket
      ? "Market position was used to test whether each charge is consistent with local pricing."
      : "No market-position inference was applied to these charges.",
    unverifiedReason: null,
    fields,
    extra,
  };
}


/* Google Business Profile ─────────────────────────────────── */

function buildGoogleEntry(
  data: VendorIntelligenceData | null,
  googleProfile: VendorSnapshot | null,
  context?: VendorContextIntel | null,
): SourceEntry {
  const category = vendorCategoryFromInvoiceType(data?.invoiceType);
  const hasAddress = !!data?.serviceAddress && data.serviceAddress.trim().length > 0;
  const profile = googleProfile?.profile ?? null;
  const matched = googleProfile?.status === "matched" && !!profile;
  const searched =
    !!googleProfile &&
    (googleProfile.status === "no_verified_match" ||
      googleProfile.status === "no_results" ||
      googleProfile.status === "low_confidence");
  const status: SourceStatus = matched
    ? "Verified"
    : searched
      ? "No Records Identified"
      : "Not Connected";

  const rating =
    matched && profile?.rating != null
      ? `${profile.rating.toFixed(1)}${profile.reviewCount != null ? ` (${profile.reviewCount.toLocaleString()} reviews)` : ""}`
      : null;
  const hours = matched && profile?.hours?.length ? profile.hours.join(" · ") : null;
  const reviewCount =
    matched && profile?.reviewCount != null ? profile.reviewCount.toLocaleString() : null;
  const photoCount =
    matched && profile?.photos?.length ? `${profile.photos.length} available` : null;
  const category0 = matched ? vstr(profile?.category) ?? category : category;
  const website = matched ? vstr(profile?.website) : null;
  const locationVerification = matched
    ? vstr(profile?.address) ?? (hasAddress ? "Address confirmed" : null)
    : hasAddress
      ? "Address on invoice"
      : null;

  // Rows appear only when a value was actually retrieved.
  const field = (label: string, value: string | null, sourceLabel?: string): SourceField | null =>
    value ? { label, value, sourceLabel, status: matched ? "Verified" : undefined } : null;

  const ctx = context ?? null;
  const comparable = ctx?.comparableFacilities
    ? `${ctx.comparableFacilities.count} comparable facilities within ${ctx.comparableFacilities.radiusMiles} miles`
    : null;

  // Business status is normalized to plain language; unknown values are
  // suppressed so the row never renders a provider enum.
  const businessStatusLabel = (() => {
    if (!matched) return null;
    const raw = vstr(profile?.businessStatus);
    if (!raw) return null;
    const s = raw.toUpperCase();
    if (s === "OPERATIONAL") return "Open";
    if (s === "CLOSED_TEMPORARILY") return "Temporarily Closed";
    if (s === "CLOSED_PERMANENTLY") return "Permanently Closed";
    return vtitleize(raw.toLowerCase());
  })();

  // Additional categories, when the provider supplies them. The primary
  // category is excluded so it is never duplicated.
  const secondaryCategories = (() => {
    if (!matched) return null;
    const px = (profile ?? {}) as unknown as Record<string, unknown>;
    const raw = px.secondaryCategories ?? px.categories ?? px.types;
    if (!Array.isArray(raw)) return null;
    const primary = (category0 ?? "").toLowerCase();
    const list = raw
      .map((v) => vstr(v))
      .filter((v): v is string => !!v)
      .map((v) => vtitleize(v))
      .filter((v) => v.toLowerCase() !== primary);
    return list.length ? Array.from(new Set(list)) : null;
  })();

  // Services offered: the invoice-context list is authoritative; when the
  // provider itself publishes a services list we fall back to it. Hidden
  // entirely when neither source supplies one.
  const servicesOffered = (() => {
    const fromContext = ctx?.servicesListed;
    if (fromContext && fromContext.length > 0) return listValue(fromContext);
    if (!matched) return null;
    const px = (profile ?? {}) as unknown as Record<string, unknown>;
    const raw = px.services ?? px.servicesOffered;
    if (!Array.isArray(raw)) return null;
    const list = raw
      .map((v) => vstr(v))
      .filter((v): v is string => !!v)
      .map((v) => vtitleize(v));
    return list.length ? Array.from(new Set(list)).join(", ") : null;
  })();





  return {
    key: "google",
    icon: MapPin,
    title: "Google Business Profile",
    description:
      "The vendor’s public business listing — customer-facing identity, services offered, operating location, and proximity to the loss, the repair facility, and comparable providers.",
    status,
    result: matched
      ? "Operating business located and matched to the invoice."
      : searched
        ? "Public listing searched — no confidently matching record identified."
        : "External business-profile enrichment is not currently connected.",
    reviewed: [
      "Public business listing and services offered",
      "Business category, website, and operating hours",
      "Ratings, reviews, and published photos",
      "Location verification and proximity to the loss",
    ],
    findings: [
      matched
        ? `Public business listing matched with ${Math.round((profile?.confidence ?? 0) * 100)}% confidence.`
        : searched
          ? "No confidently matching public business listing was identified."
          : "Public business listing data was not retrieved.",
    ],
    impact: matched
      ? "Used as independent confirmation of vendor identity, services, and location."
      : "Not used as evidence against the vendor.",
    verified: matched,
    unverifiedReason: matched
      ? null
      : searched
        ? "No confidently matching public business listing"
        : "External business-profile source not connected",
    fields: [
      intelField("Business Status", businessStatusLabel, matched ? "Verified" : undefined, "Google Places"),
      category0
        ? {
            label: "Primary Business Category",
            value: category0,
            sourceLabel:
              matched && vstr(profile?.category) ? "Google Places" : "Internally Classified",
            status: (matched && vstr(profile?.category) ? "Verified" : undefined) as
              | SourceStatus
              | undefined,
          }
        : null,
      intelField(
        "Secondary Business Categories",
        listValue(secondaryCategories),
        matched ? "Verified" : undefined,
        "Google Places",
      ),
      field("Website", website, "Google Places"),
      field("Phone", matched ? vstr(profile?.phone) : null, "Google Places"),
      field("Hours", hours, "Google Places"),
      field("Reviews", reviewCount, "Google Places"),
      field("Rating", rating, "Google Places"),
      field("Photos", photoCount, "Google Places"),
      locationVerification
        ? {
            label: "Location Verification",
            value: locationVerification,
            sourceLabel:
              matched && vstr(profile?.address) ? "Google Places" : "Invoice-Derived",
            status: (matched && vstr(profile?.address) ? "Verified" : "Invoice-Derived") as
              | SourceStatus
              | undefined,
          }
        : null,
      intelField("Years in Business", ctx?.yearsInBusiness, undefined, "Google Places"),
      intelField("Services Offered", servicesOffered, undefined, "Google Places"),
      intelField("Storage Type", ctx?.storageType, undefined, "Google Places"),
      intelField("Distance from Loss Location", ctx?.distanceFromLossLocation, undefined, "ClaimGuard Market Intelligence"),
      intelField("Distance from Repair Facility", ctx?.distanceFromRepairFacility, undefined, "ClaimGuard Market Intelligence"),
      intelField("Distance from Vehicle Owner", ctx?.distanceFromVehicleOwner, undefined, "ClaimGuard Market Intelligence"),
      intelField("Nearby Comparable Facilities", comparable, "Market Average", "ClaimGuard Market Intelligence"),
    ].filter((f): f is SourceField => f !== null),

  };
}

/* Secretary of State ──────────────────────────────────────── */

function buildSosEntry(
  sosProfile: import("@/hooks/use-sos-snapshot").SosSnapshot | null,
): SourceEntry {
  const sp = sosProfile?.profile ?? null;
  const disclosure = resolveDisclosure({
    provider: sosProfile?.provider ?? null,
    status: sosProfile?.status ?? null,
    hasProfile: !!sp,
  });
  const matched = disclosure === "Verified";
  const searched = disclosure === "No Records Identified";
  const status: SourceStatus = matched
    ? "Verified"
    : searched
      ? "No Records Identified"
      : disclosure === "Provider Error"
        ? "Provider Error"
        : disclosure === "Unsupported"
          ? "Unsupported"
          : "Not Connected";

  const entityStatusLabel =
    matched && sp?.entityStatus && sp.entityStatus !== "unknown" ? vcap(sp.entityStatus) : null;
  const entityStatusBadge: SourceStatus | undefined =
    sp?.entityStatus === "active"
      ? "Good Standing"
      : sp?.entityStatus === "suspended"
        ? "Suspended"
        : sp?.entityStatus === "dissolved" || sp?.entityStatus === "forfeited"
          ? "High Risk"
          : undefined;

  // Forward-compatible reads: future registry adapters may supply DBA and
  // annual-report data. Rows appear only once a provider populates them.
  const spx = (sp ?? {}) as Record<string, unknown>;
  const dbaValue = matched
    ? Array.isArray(spx.dbaNames)
      ? (spx.dbaNames as unknown[]).map((v) => vstr(v)).filter(Boolean).join(", ") || null
      : vstr(spx.dba)
    : null;
  const annualReportStatus = matched ? vstr(spx.annualReportStatus) : null;

  const confidencePct =
    matched && typeof sp?.confidence === "number"
      ? `${Math.round(sp.confidence * 100)}% Match`
      : null;
  const confidenceBadge: SourceStatus | undefined =
    matched && typeof sp?.confidence === "number"
      ? sp.confidence >= 0.85
        ? "High Confidence"
        : "Partial Match"
      : undefined;

  // Only fields actually returned by a connected provider are rendered.
  const field = (label: string, value: string | null): SourceField | null =>
    value ? { label, value, sourceLabel: "Secretary of State", status: "Verified" } : null;

  const verifiedLabel = vdate(sosProfile?.verifiedAt ?? null);
  const disclosureLine = disclosureSentence(disclosure, "Secretary of State records");

  return {
    key: "sos",
    icon: Landmark,
    title: "Secretary of State",
    description:
      "Official state business-registration records confirming the legal identity, standing, and filing compliance of the company listed on the invoice. Availability and public access vary by state.",
    status,
    result: matched
      ? "Business registration located and matched."
      : searched
        ? "Registry searched — no confidently matching registration located."
        : disclosureLine,
    reviewed: [
      "Legal business name, DBA, and entity type",
      "Entity status, standing, and annual report compliance",
      "Formation date and state of registration",
      "Registered agent and principal address",
    ],
    findings: [
      matched
        ? `Verified through Secretary of State records${verifiedLabel ? ` · Last verified: ${verifiedLabel}` : ""}.`
        : disclosureLine,
    ],
    impact: matched
      ? "Used as independent confirmation of the vendor's legal identity and standing."
      : "Not used as evidence against the vendor.",
    verified: matched,
    unverifiedReason: matched
      ? null
      : searched
        ? "No confidently matching registration"
        : `Business registry — ${vendorStatusLabel(disclosure)}`,
    fields: [
      field("Legal Business Name", matched ? vstr(sp?.legalName) : null),
      intelField("DBA (Doing Business As)", dbaValue, "Verified", "Secretary of State"),
      field("Entity Type", matched ? vstr(sp?.entityType) : null),
      intelField("Entity Status", entityStatusLabel, entityStatusBadge, "Secretary of State"),
      intelField(
        "Standing",
        matched ? vstr(sp?.standing) : null,
        entityStatusBadge === "Good Standing" ? "Good Standing" : undefined,
        "Secretary of State",
      ),
      intelField("Annual Report Status", annualReportStatus, undefined, "Secretary of State"),
      field("Formation Date", matched ? vstr(sp?.formationDate) : null),
      field("State of Registration", matched ? vstr(sp?.stateOfRegistration) : null),
      field("Registered Agent", matched ? vstr(sp?.registeredAgent?.name) : null),
      field("Principal Address", matched ? vstr(sp?.principalAddress) : null),
      intelField("Business Match Confidence", confidencePct, confidenceBadge, "ClaimGuard Match Engine"),
    ].filter((f): f is SourceField => f !== null),

  };
}


/* Better Business Bureau ──────────────────────────────────── */

function buildBbbEntry(
  bbbProfile: import("@/hooks/use-bbb-snapshot").BBBSnapshot | null,
): SourceEntry {
  const bp = bbbProfile?.profile ?? null;
  const status = bbbProfile?.lookupStatus ?? null;
  const isSafeHttps = (u: string | null | undefined): u is string =>
    typeof u === "string" && /^https:\/\//i.test(u);
  const referenceUrl =
    bbbProfile?.coverage?.externalReferenceAvailable && isSafeHttps(bp?.publicBbbUrl ?? null)
      ? (bp!.publicBbbUrl as string)
      : null;
  const hasReference = status === "external_reference_available" && !!referenceUrl;

  const cardStatus: SourceStatus = hasReference
    ? "Public Reference"
    : status === "no_verified_data" || status === "not_found" || status === "multiple_matches"
      ? "No Records Identified"
      : status === "provider_error" || status === "unavailable"
        ? "Provider Error"
        : status === "misconfigured"
          ? "Not Connected"
        : status === "accredited" ||
            status === "not_accredited" ||
            status === "rating_available" ||
            status === "rating_unavailable"
          ? "Verified"
          : "Not Connected";

  const providerLabel =
    bbbProfile?.provider === "external_link_only"
      ? "Public BBB Reference (external link only)"
      : bbbProfile?.provider === "commercial_aggregator"
        ? "Licensed BBB Data Provider"
        : bbbProfile?.provider === "manual_override"
          ? "Verified Manual Override"
          : null;

  const lastCheckedLabel = vdate(
    bbbProfile?.provenance?.verifiedAt ?? bbbProfile?.capturedAt ?? null,
  );

  const verificationStatusLabel = hasReference
    ? "Public reference link available (BBB data not retrieved)"
    : status === "no_verified_data" || status === "not_found"
      ? "Searched — No Match"
      : status === "multiple_matches"
        ? "Ambiguous — Verified Selection Required"
        : status === "provider_error" || status === "unavailable" || status === "misconfigured"
          ? "Verification Unavailable"
          : status === "accredited" ||
              status === "not_accredited" ||
              status === "rating_available" ||
              status === "rating_unavailable"
            ? "Verified"
            : null;

  const cov = bbbProfile?.coverage ?? null;
  const coverageSummary = cov
    ? [
        cov.externalReferenceAvailable
          ? "Public reference available"
          : "Public reference unavailable",
        cov.programmaticDataVerified
          ? "Programmatic verification"
          : "Programmatic verification not performed",
        cov.licensedProviderUsed ? "Licensed provider used" : "Licensed provider not used",
        cov.manualOverrideUsed ? "Manual override applied" : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  void providerLabel;
  void lastCheckedLabel;
  void verificationStatusLabel;
  void coverageSummary;

  // Licensed data fields — displayed only when actually populated by a
  // licensed provider or manual override. Under external_link_only all
  // of these are null and MUST NOT be rendered.
  const licensedFields: SourceField[] = [];
  let adverse = false;
  if (bp) {
    const push = (
      label: string,
      value: string | number | null | undefined,
      badge?: SourceStatus,
    ) => {
      const s = vstr(value);
      if (!s) return;
      licensedFields.push({
        label,
        value: s,
        sourceLabel: "Better Business Bureau",
        status: badge ?? "Verified",
      });
    };
    const bpx = bp as unknown as Record<string, unknown>;
    if (bp.bbbRating) push("BBB Rating", bp.bbbRating);
    if (bp.accreditationStatus && bp.accreditationStatus !== "unknown") {
      push(
        "Accredited",
        vcap(bp.accreditationStatus),
        bp.accreditationStatus === "accredited" ? "Verified" : undefined,
      );
    }
    if (bp.accreditationSince) push("Years Accredited", bp.accreditationSince);
    if (bp.complaintCount !== null) push("Complaint Count", bp.complaintCount);
    if (Array.isArray(bpx.complaintCategories) && bpx.complaintCategories.length > 0) {
      push("Complaint Categories", (bpx.complaintCategories as unknown[]).join(", "));
    } else if (bp.complaintResolutionSummary) {
      push("Complaint Categories", bp.complaintResolutionSummary);
    }
    if (bp.customerReviewCount !== null) push("Customer Review Count", bp.customerReviewCount);
    if (bp.customerReviewRating !== null) push("Average Customer Rating", bp.customerReviewRating);
    if (bp.governmentActions && bp.governmentActions.length > 0) {
      push("Government Actions", bp.governmentActions.join(", "), "High Risk");
      adverse = true;
    }
    if (bp.alertFlags && bp.alertFlags.length > 0) {
      push("Business Alerts", bp.alertFlags.join(", "), "High Risk");
      adverse = true;
    }
  }

  const linkedReferenceField: SourceField | null = referenceUrl
    ? {
        label: "BBB Reference",
        value: referenceUrl,
        sourceLabel: "Better Business Bureau",
        status: "Public Reference",
        href: referenceUrl,
      }
    : null;

  const bbbFields: SourceField[] = [
    ...(linkedReferenceField ? [linkedReferenceField] : []),
    ...licensedFields,
  ];
  if (bbbFields.length === 0 && hasReference) {
    bbbFields.push({
      label: "BBB Reference",
      value: "Reference Available",
      status: "Public Reference",
    });
  }

  return {
    key: "bbb",
    icon: ShieldCheck,
    title: "Better Business Bureau",
    description:
      "Better Business Bureau rating, accreditation, complaint history, customer reviews, and any published government actions or business alerts.",
    status: cardStatus,
    result: hasReference
      ? "Public listing found; complaint data not retrieved."
      : cardStatus === "Verified"
        ? "BBB record retrieved and reviewed."
        : cardStatus === "Provider Error"
          ? "BBB verification temporarily unavailable."
          : "No BBB record identified.",
    reviewed: [
      "Rating and accreditation",
      "Complaint count and categories",
      "Customer reviews",
      "Government actions and business alerts",
    ],
    findings: [
      bbbProfile?.message ?? "Better Business Bureau reference is not currently available.",
    ],
    impact: adverse
      ? "Adverse public indicators were identified and considered in this review."
      : cardStatus === "Verified"
        ? "Better Business Bureau information was reviewed and did not alter ClaimGuard's findings or conclusions."
        : "No verified Better Business Bureau complaint or accreditation information was available during this review. Accordingly, BBB information was not relied upon as evidence in ClaimGuard's findings or conclusions.",
    adverse,
    unverifiedReason: hasReference
      ? "Public profile located, complaint data unavailable"
      : cardStatus === "Verified"
        ? null
        : "No verified BBB complaint data retrieved",
    fields: bbbFields,

  };
}

/* Licensing & Regulatory (federal) ────────────────────────── */

function buildLicensingEntry(
  licensingProfile: import("@/hooks/use-licensing-snapshot").LicensingSnapshot | null,
): SourceEntry {
  const lp = licensingProfile?.profile ?? null;
  const status = licensingProfile?.lookupStatus ?? null;
  const disclosure = resolveDisclosure({
    provider: licensingProfile?.provider ?? null,
    status,
    hasProfile: !!lp,
    // Federal authority is not applicable when the federal registry
    // reports no coverage for this vendor / transaction type.
    notApplicable: status === "no_coverage",
  });
  const matched = disclosure === "Verified";
  const searched = disclosure === "No Records Identified";
  const notApplicable = disclosure === "Not Applicable" || disclosure === "Unsupported";
  const failed = disclosure === "Provider Error";
  const notConnected = disclosure === "Not Connected";
  const cardStatus: SourceStatus = matched
    ? "Verified"
    : searched
      ? "No Records Identified"
      : notApplicable
        ? "Not Applicable"
        : failed
          ? "Provider Error"
          : "Not Connected";


  // Stub / unconnected providers are never named in a customer report.
  const providerLabel = notConnected
    ? null
    : licensingProfile?.provider === "fmcsa"
      ? "FMCSA (SAFER)"
      : licensingProfile?.provider === "state"
        ? "State Licensing"
        : vstr(licensingProfile?.provider ?? null);

  const provenanceMode = licensingProfile?.provenance?.mode ?? null;
  const verificationStatus = matched
    ? provenanceMode === "stale_cache"
      ? "Previously Verified"
      : provenanceMode === "cache"
        ? "Verified (cached)"
        : "Verified"
    : searched
      ? "Searched — No Match"
      : null;
  // A retrieval date is only meaningful when a real query executed.
  const verificationDate =
    matched || searched
      ? vdate(licensingProfile?.provenance?.verifiedAt ?? licensingProfile?.capturedAt ?? null)
      : null;
  const cov = licensingProfile?.coverage ?? null;
  const coverageSummary =
    cov && (matched || searched)
      ? [
          cov.federalChecked ? "Federal checked" : null,
          cov.stateChecked
            ? "State checked"
            : cov.stateCoverageAvailable
              ? "State coverage available"
              : "State coverage not included",
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  // Only fields actually returned by a connected provider are rendered.
  const field = (label: string, value: string | null): SourceField | null =>
    value
      ? {
          label,
          value,
          sourceLabel: providerLabel ?? undefined,
          status: matched ? "Verified" : undefined,
        }
      : null;


  const licenses = lp?.licenses ?? [];
  const actions = lp?.regulatoryActions ?? [];

  const extra =
    licenses.length > 0 || actions.length > 0 ? (
      <div className="mt-4 space-y-4">
        {licenses.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              License Records
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs text-foreground/80">
                <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3">Authority</th>
                    <th className="py-2 pr-3">Jurisdiction</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Number</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((lic, idx) => (
                    <tr
                      key={`${lic.provider}:${lic.authorityType}:${lic.licenseNumber ?? "no-num"}:${idx}`}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="py-2 pr-3 text-foreground">{vstr(lic.issuingAgency) ?? "—"}</td>
                      <td className="py-2 pr-3">{vstr(lic.jurisdiction) ?? "—"}</td>
                      <td className="py-2 pr-3">{vstr(lic.licenseType) ?? "—"}</td>
                      <td className="py-2 pr-3 font-mono">{vstr(lic.licenseNumber) ?? "—"}</td>
                      <td className="py-2 pr-3">{vcap(lic.status)}</td>
                      <td className="py-2 pr-3">{vstr(lic.expirationDate) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {actions.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Regulatory Actions
            </div>
            <ul className="mt-2 space-y-2">
              {actions.map((a, idx) => (
                <li
                  key={`${a.provider}:${a.actionType}:${a.effectiveDate ?? "no-date"}:${idx}`}
                  className="rounded-xl border border-border bg-secondary/40 p-3 text-sm text-foreground/80"
                >
                  <span className="font-semibold text-foreground">
                    {vcap(a.actionType.replace(/_/g, " "))}
                  </span>
                  {a.effectiveDate ? ` · Effective ${a.effectiveDate}` : ""}
                  {a.description ? ` — ${a.description}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    ) : undefined;

  return {
    key: "licensing",
    icon: BadgeCheck,
    title: "FMCSA",
    description:
      "Federal motor-carrier registration and operating authority records for the vendor listed on the invoice. Coverage is federal-only unless a state adapter has been separately connected.",
    status: cardStatus,
    result: matched
      ? "Federal operating authority record identified."
      : searched
        ? "No federal motor-carrier record returned."
        : disclosureSentence(disclosure, "Federal motor-carrier records"),
    reviewed: [
      "Federal motor-carrier registration",
      "Operating authority status",
      "USDOT / MC identifiers",
      "Enforcement and disciplinary history",
    ],
    findings: [
      matched
        ? licensingProfile?.message ?? "Verified through federal motor-carrier records."
        : searched
          ? licensingProfile?.message ??
            "Federal motor-carrier records were searched; no confidently matching record was identified."
          : disclosureSentence(disclosure, "Federal motor-carrier records"),
      matched && actions.length === 0 ? "No regulatory actions identified." : "",
    ].filter(Boolean),
    impact:
      actions.length > 0
        ? "Regulatory history was considered in this review."
        : "Not used as evidence against the vendor.",
    adverse: actions.length > 0,
    verified: matched,
    unverifiedReason: matched ? null : `Federal licensing — ${vendorStatusLabel(disclosure)}`,
    fields: (() => {
      void verificationStatus;
      void verificationDate;
      void coverageSummary;
      const lpx = (lp ?? {}) as unknown as Record<string, unknown>;
      const authorityStatus =
        matched && lp?.fmcsaStatus && lp.fmcsaStatus !== "unknown" ? vcap(lp.fmcsaStatus) : null;
      const authorityBadge: SourceStatus | undefined =
        lp?.fmcsaStatus === "active"
          ? "Verified"
          : lp?.fmcsaStatus === "revoked" || lp?.fmcsaStatus === "suspended"
            ? "High Risk"
            : undefined;
      return [
        intelField("USDOT Number", matched ? vstr(lp?.usdotNumber) : null, undefined, providerLabel ?? undefined),
        intelField("MC Number", matched ? vstr(lp?.mcNumber) : null, undefined, providerLabel ?? undefined),
        intelField("Operating Authority", matched ? vstr(lp?.operatingAuthority) : null, undefined, providerLabel ?? undefined),
        intelField("Authority Status", authorityStatus, authorityBadge, providerLabel ?? undefined),
        intelField("Safety Rating", vstr(lpx.safetyRating), undefined, providerLabel ?? undefined),
        intelField("Inspection History", vstr(lpx.inspectionHistory), undefined, providerLabel ?? undefined),
        intelField("Insurance Status", vstr(lpx.insuranceStatus), undefined, providerLabel ?? undefined),
        intelField(
          "Out-of-Service Orders",
          vstr(lpx.outOfServiceOrders) ?? (matched && actions.length === 0 ? "None identified" : null),
          undefined,
          providerLabel ?? undefined,
        ),
        intelField("Operating Classification", vstr(lpx.operatingClassification), undefined, providerLabel ?? undefined),
        intelField(
          "Overall Status",
          matched && lp?.overallStatus && lp.overallStatus !== "unknown" ? vcap(lp.overallStatus) : null,
          undefined,
          providerLabel ?? undefined,
        ),
        intelField("Primary State", matched ? vstr(lp?.primaryState) : null, undefined, providerLabel ?? undefined),
        matched && vstr(lp?.sourceUrl)
          ? {
              label: "Public Source",
              value: vstr(lp?.sourceUrl) as string,
              sourceLabel: providerLabel ?? undefined,
              status: "Verified" as SourceStatus,
              href: vstr(lp?.sourceUrl) as string,
            }
          : null,
      ].filter((f): f is SourceField => f !== null);
    })(),


    extra,
  };
}

/* State Licensing ─────────────────────────────────────────── */

function buildStateLicensingEntry(
  stateLicensingProfile:
    | import("@/hooks/use-state-licensing-snapshot").StateLicensingSnapshot
    | null,
): SourceEntry {
  // Reads ONLY reviews.state_licensing_snapshot. Never invokes the state
  // licensing provider, registry, cache, or TDLR during render. Lookup
  // status is rendered verbatim (Title-cased) — never reinterpreted.
  const sl = stateLicensingProfile;
  const sp = sl?.profile ?? null;
  const status = sl?.lookupStatus ?? null;
  const disclosure = resolveDisclosure({
    provider: sl?.provider ?? null,
    jurisdiction: sl?.jurisdiction ?? null,
    status,
    hasProfile: !!sp,
    notApplicable: status === "license_not_required",
  });
  const notConnected = disclosure === "Not Connected";
  const matched = disclosure === "Verified";
  const searched = disclosure === "No Records Identified";
  const unavailable = disclosure === "Provider Error";
  const noCoverage = disclosure === "Unsupported";
  const notRequired = disclosure === "Not Applicable";
  const multiple = !notConnected && status === "multiple_candidates";

  const cardStatus: SourceStatus = matched
    ? "Verified"
    : multiple
      ? "Partially Verified"
      : searched
        ? "No Records Identified"
        : unavailable
          ? "Provider Error"
          : notRequired
            ? "Not Applicable"
            : noCoverage
              ? "Unsupported"
              : "Not Connected";

  // Stub adapters and unconnected providers are never named or dated.
  const providerLabel = notConnected
    ? null
    : sl?.provider === "tx_tdlr"
      ? "Texas Department of Licensing and Regulation"
      : sl?.provider === "external_link_only"
        ? "External Reference"
        : sl?.provider === "commercial"
          ? "Commercial Aggregator"
          : sl?.provider
            ? vtitleize(sl.provider)
            : null;
  const jurisdiction = notConnected ? null : vstr(sl?.jurisdiction ?? null);
  const lookupStatusLabel = notConnected || !status ? null : vtitleize(status);

  const cov = sl?.coverage ?? null;
  const coverageSummary =
    cov && !notConnected
      ? [
          cov.stateChecked
            ? "State checked"
            : cov.stateCoverageAvailable
              ? "State coverage available"
              : "State coverage unsupported",
          cov.countyChecked ? "County checked" : null,
          cov.municipalityChecked ? "Municipality checked" : null,
          cov.licenseTypesChecked?.length
            ? `Types checked: ${cov.licenseTypesChecked.map((t) => vtitleize(t)).join(", ")}`
            : null,
          cov.licenseTypesUnsupported?.length
            ? `Types unsupported: ${cov.licenseTypesUnsupported.map((t) => vtitleize(t)).join(", ")}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  // Only fields actually returned by a connected provider are rendered.
  const field = (label: string, value: string | null): SourceField | null =>
    value
      ? {
          label,
          value,
          sourceLabel: providerLabel ?? undefined,
          status: matched ? "Verified" : undefined,
        }
      : null;


  const licenses = sp?.licenses ?? [];
  const actions = sp?.regulatoryActions ?? [];
  const candidates = sl?.candidates ?? [];

  const extra =
    licenses.length > 0 || actions.length > 0 || (multiple && candidates.length > 0) ? (
      <div className="mt-4 space-y-4">
        {licenses.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              State License Records
            </div>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              {licenses.map((lic, idx) => (
                <div
                  key={`${lic.issuingAuthority}:${lic.licenseType}:${lic.licenseNumber ?? "no-num"}:${idx}`}
                  className="rounded-xl border border-border bg-secondary/30 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-display text-base text-navy">
                      {vtitleize(lic.licenseType)}
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      {vcap(lic.status)}
                    </span>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-foreground/80">
                    <dt className="text-muted-foreground">License #</dt>
                    <dd className="font-mono text-foreground break-all">
                      {vstr(lic.licenseNumber) ?? "—"}
                    </dd>
                    <dt className="text-muted-foreground">Authority</dt>
                    <dd className="text-foreground">{vstr(lic.issuingAuthority) ?? "—"}</dd>
                    <dt className="text-muted-foreground">Issue Date</dt>
                    <dd>{vstr(lic.issueDate) ?? "—"}</dd>
                    <dt className="text-muted-foreground">Expiration</dt>
                    <dd>{vstr(lic.expirationDate) ?? "—"}</dd>
                    <dt className="text-muted-foreground">Towing</dt>
                    <dd>{lic.applicableToTowing ? "Yes" : "No"}</dd>
                    <dt className="text-muted-foreground">Storage</dt>
                    <dd>{lic.applicableToStorage ? "Yes" : "No"}</dd>
                  </dl>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {multiple && candidates.length > 0 ? (
          <p className="text-sm text-foreground/80">
            The state licensing lookup returned {candidates.length} close candidates. Analyst
            review is required before a definitive match can be confirmed.
          </p>
        ) : null}
        {actions.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              State Regulatory Actions
            </div>
            <ul className="mt-2 space-y-2">
              {actions.map((a, idx) => (
                <li
                  key={`${a.authority}:${a.actionType}:${a.effectiveDate ?? "no-date"}:${idx}`}
                  className="rounded-xl border border-border bg-secondary/40 p-3 text-sm text-foreground/80"
                >
                  <span className="font-semibold text-foreground">{vtitleize(a.actionType)}</span>
                  {a.effectiveDate ? ` · Effective ${a.effectiveDate}` : ""}
                  {a.summary ? ` — ${a.summary}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    ) : undefined;

  return {
    key: "state-licensing",
    icon: BadgeCheck,
    title: "State Licensing",
    description:
      "Official State Licensing Verification. Reads only the immutable snapshot captured for this claim from the state licensing authority. Coverage is state-specific; no county or municipal licensing is queried unless explicitly indicated.",
    status: cardStatus,
    result: matched
      ? "State licensing record identified and matched."
      : searched
        ? "No licensing record returned."
        : disclosureSentence(disclosure, "State licensing records"),
    reviewed: [
      "State licensing authority records",
      "License type, number, and status",
      "Licensed business names and addresses",
      "State regulatory actions",
    ],
    findings: [
      notConnected
        ? disclosureSentence(disclosure, "State licensing records")
        : sl?.message?.trim()
          ? sl.message
          : noCoverage
            ? "State licensing coverage is not available for this jurisdiction."
            : notRequired
              ? "A state license is not required for this activity in this jurisdiction."
              : multiple
                ? "Multiple close candidates were returned; candidate review required."
                : searched
                  ? "No verified match was identified in official state licensing records."
                  : unavailable
                    ? "Temporary verification unavailable from the state licensing source."
                    : disclosureSentence(disclosure, "State licensing records"),
      matched && actions.length === 0 ? "No state regulatory actions identified." : "",
    ].filter(Boolean),
    impact:
      actions.length > 0
        ? "State regulatory history was considered in this review."
        : "Not used as evidence against the vendor.",
    adverse: actions.length > 0,
    verified: matched,
    unverifiedReason: matched ? null : `State licensing — ${vendorStatusLabel(disclosure)}`,
    fields: (() => {
      void coverageSummary;
      void lookupStatusLabel;
      const primary =
        licenses.find((l) => l.applicableToTowing && l.status === "active") ??
        licenses.find((l) => l.status === "active") ??
        licenses[0] ??
        null;
      const px = (primary ?? {}) as unknown as Record<string, unknown>;
      const statusBadge: SourceStatus | undefined =
        primary?.status === "active"
          ? "Verified License"
          : primary?.status === "expired"
            ? "Expired"
            : primary?.status === "suspended" || primary?.status === "revoked"
              ? "Suspended"
              : undefined;
      const restrictions = Array.isArray(px.restrictions)
        ? (px.restrictions as unknown[]).map((r) => vstr(r)).filter(Boolean).join(", ") || null
        : null;
      const disciplinary =
        primary && primary.disciplinaryStatus !== "unknown"
          ? primary.disciplinaryStatus === "none"
            ? "None identified"
            : vcap(primary.disciplinaryStatus)
          : actions.length > 0
            ? `${actions.length} action${actions.length === 1 ? "" : "s"} on record`
            : null;
      const insuranceVerification =
        primary && primary.insuranceIndicator !== "unknown"
          ? primary.insuranceIndicator === "on_file"
            ? "On file with the licensing authority"
            : "Not on file with the licensing authority"
          : null;
      return [
        intelField("License Number", matched ? vstr(primary?.licenseNumber) : null, statusBadge, providerLabel ?? undefined),
        intelField("License Type", primary ? vtitleize(primary.licenseType) : null, undefined, providerLabel ?? undefined),
        intelField("License Status", primary ? vcap(primary.status) : null, statusBadge, providerLabel ?? undefined),
        intelField("Issue Date", vstr(primary?.issueDate ?? primary?.effectiveDate ?? null), undefined, providerLabel ?? undefined),
        intelField("Expiration Date", vstr(primary?.expirationDate ?? null), undefined, providerLabel ?? undefined),
        intelField("Issuing Authority", vstr(primary?.issuingAuthority ?? null) ?? (matched ? providerLabel : null), undefined, providerLabel ?? undefined),
        intelField("Licensed Business Name", matched ? vstr(sp?.legalName) ?? vstr(sp?.businessName) : null, undefined, providerLabel ?? undefined),
        intelField("Licensed Address", matched ? vstr(sp?.address) : null, undefined, providerLabel ?? undefined),
        intelField("Insurance Required", primary && primary.insuranceIndicator !== "unknown" ? "Yes" : null, undefined, providerLabel ?? undefined),
        intelField("Insurance Verification Status", insuranceVerification, primary?.insuranceIndicator === "on_file" ? "Verified" : undefined, providerLabel ?? undefined),
        intelField("Operating Restrictions", restrictions, undefined, providerLabel ?? undefined),
        intelField("Disciplinary Actions", disciplinary, primary?.disciplinaryStatus === "sanctioned" ? "High Risk" : undefined, providerLabel ?? undefined),
        intelField("Last Inspection Date", vstr(px.lastInspectionDate), undefined, providerLabel ?? undefined),
        intelField("Last Inspection Result", vstr(px.lastInspectionResult), undefined, providerLabel ?? undefined),
        intelField("Jurisdiction", matched ? vstr(sp?.state) : jurisdiction, undefined, providerLabel ?? undefined),
      ].filter((f): f is SourceField => f !== null);
    })(),


    extra,
  };
}

/* ── Presentational building blocks ───────────────────────── */

function SnapshotField({
  label,
  value,
  href,
  note,
}: {
  label: string;
  value: string;
  href?: string | null;
  /** Optional confirmation line rendered beneath the value. */
  note?: string | null;
}) {
  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      {href && /^https:\/\//i.test(href) ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-1 block break-all text-sm font-medium text-navy underline decoration-navy/40 underline-offset-2 hover:decoration-navy"
        >
          {value}
        </a>
      ) : (
        <div className="mt-1 break-words text-sm font-medium text-foreground">{value}</div>
      )}
      {note ? (
        <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          {note}
        </div>
      ) : null}
    </div>
  );
}



function SourceDetailPanel({ entry }: { entry: SourceEntry }) {
  const Icon = entry.icon;
  return (
    <details className="group rounded-2xl border border-border bg-card shadow-card">
      <summary className="flex cursor-pointer list-none items-center gap-3 p-4">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-secondary/60 text-navy shadow-card">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base text-navy">{entry.title}</div>
          <div className="text-xs text-muted-foreground [overflow-wrap:break-word] print:overflow-visible">
            {entry.result}
          </div>
        </div>
        <StatusBadge status={entry.status} />
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-180" />
      </summary>
      <div className="border-t border-border p-5">
        <p className="text-sm text-foreground/80">{entry.description}</p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              What was reviewed
            </div>
            <ul className="mt-2 space-y-1.5">
              {entry.reviewed.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-foreground/80">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-navy" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Findings
            </div>
            <div className="mt-2 space-y-1.5">
              {entry.findings.map((f) => (
                <p key={f} className="text-sm text-foreground/80">
                  {f}
                </p>
              ))}
            </div>
            <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Impact
            </div>
            <p className="mt-1 text-sm text-foreground/80">{entry.impact}</p>
          </div>
        </div>

        {entry.fields.length > 0 ? (
          <details className="group cg-accordion mt-4 rounded-xl border border-border bg-secondary/30">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Evidence Considered ({entry.fields.length})
              </span>
              <ChevronDown
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
              />
            </summary>
            <ul className="cg-accordion-body divide-y divide-border border-t border-border">
              {entry.fields.map((f, fi) => (
                <li
                  key={f.label}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                >
                  <span className="text-sm text-foreground/80">
                    <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Evidence {fi + 1}
                    </span>
                    {f.label}
                  </span>
                  <span className="flex flex-col items-end gap-0.5">
                    <span className="flex items-center gap-2">
                      {f.href && /^https:\/\//i.test(f.href) ? (
                        <a
                          href={f.href}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="break-all text-sm font-medium text-navy underline decoration-navy/40 underline-offset-2 hover:decoration-navy"
                        >
                          {f.value}
                        </a>
                      ) : (
                        <span className="break-all text-sm font-medium text-foreground">
                          {f.value}
                        </span>
                      )}
                      {f.status ? <StatusBadge status={f.status} /> : null}
                    </span>
                    {f.sourceLabel ? (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        Source:{" "}
                        <span className="font-semibold text-foreground/70">{f.sourceLabel}</span>
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {entry.extra}
      </div>
    </details>
  );
}

/**
 * Compact stand-in for a provider that has no live integration yet.
 * Replaces the large expandable card so the chapter never renders an
 * empty "Information Reviewed" container.
 */
function ProviderNotConnectedNotice({ entry }: { entry: SourceEntry }) {
  const Icon = entry.icon;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-4">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-secondary/60 text-navy shadow-card">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-base text-navy">{entry.title}</span>
          <StatusBadge status={entry.status} />
        </div>
        <p className="mt-1 text-sm text-foreground/80">
          Live provider not connected. This data source has not yet been
          connected to a live provider. Information from this source will
          automatically populate once the applicable provider integration is
          enabled.
        </p>
      </div>
    </div>
  );
}

/* ── Investigative question framework ─────────────────────────
 * Vendor Intelligence is organized around the questions a claims
 * professional needs answered — not around the data providers that
 * happen to supply an answer. Each panel states what was investigated,
 * answers the individual questions, explains what it means, and only
 * then lists the sources that support the conclusion.
 */

type InvestigationAnswer = {
  question: string;
  answer: string;
  status?: SourceStatus;
  /** Compact attribution shown beneath the answer instead of a
   *  repeated per-section "Supporting Evidence" block. */
  source?: string;
};

/** Standard wording for anything a connected source could not establish. */
const NOT_YET_VERIFIED = "Independent verification not yet available.";

/**
 * A single investigative module. The chapter renders an array of these,
 * so additional investigations (FMCSA, court records, liens, OSHA, EPA,
 * consumer-protection actions, …) can be appended later without any
 * structural change to the chapter.
 */
type InvestigationModule = {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  headline: string;
  investigated: string;
  answers: InvestigationAnswer[];
  found: string;
  matters: string;
  notice?: string | null;
  /** Optional headline verdict rendered above the answers. */
  verdict?: { label: string; value: string; tone: string; caption?: string } | null;
  /** Prominent headline figures rendered above the detailed answers.
   *  `emphasis` promotes a single figure to the primary financial metric. */
  metrics?: Array<{
    label: string;
    value: string;
    caption?: string;
    tone?: string;
    emphasis?: boolean;
  }> | null;
  /** Count of evidence records behind this investigation (header only). */
  evidenceCount?: number;
  /** Subtle completion stamp shown when the module produced a result. */
  completedAt?: string | null;
  /** When a provider has no live connection, a single expandable panel
   *  replaces the repeated "not yet available" answer cards. */
  pendingProvider?: {
    title: string;
    status: string;
    willVerify: string[];
  } | null;
  /** Secondary measures collapsed behind an expandable disclosure so the
   *  primary findings stay above the fold. */
  secondary?: { title: string; answers: InvestigationAnswer[] } | null;
  /** Related indicators consolidated into a single card instead of one
   *  card per indicator. */
  indicatorGroup?: { title: string; items: InvestigationAnswer[] } | null;
  /** Presentation only: false renders the module in the neutral
   *  unavailable treatment (no success styling, no summary shortcut). */
  available?: boolean;
  /** Customer-facing investigation state (presentation only). */
  state?: InvestigationState;
  /** Presentation only: short section name shown above the question. */
  title?: string;
  details?: ReactNode;

};



/** Plain-English glossary (presentation only). Adds a one-line
 *  explanation beneath jargon-heavy finding labels. No value, status,
 *  or calculation is affected. */
const PLAIN_ENGLISH: Array<[RegExp, string]> = [
  [/variance/i, "The difference between what was billed and the regional market amount."],
  [/percentile/i, "Where this price sits compared with other providers in the region — a higher percentile means a higher price."],
  [/benchmark/i, "A verified price for the same service charged by comparable providers nearby."],
  [/market position/i, "Whether the billed price is above, at, or below what the local market charges."],
  [/coverage/i, "How much of the information we tried to collect the searched sources actually returned."],
  [/comparable vendors/i, "How many nearby providers were used to establish the market price."],
];

function plainEnglishFor(question: string): string | null {
  const hit = PLAIN_ENGLISH.find(([re]) => re.test(question));
  return hit ? hit[1] : null;
}

function AnswerCard({
  a,
  muted = false,
  index,
}: {
  a: InvestigationAnswer;
  muted?: boolean;
  /** 1-based finding number, shown to make long lists scannable. */
  index?: number;
}) {
  const plain = plainEnglishFor(a.question);
  return (
    <div
      className={`rounded-xl border p-4 ${
        muted ? "border-border/60 bg-muted/40" : "border-border bg-secondary/30"
      }`}
    >
      {index != null ? (
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-navy/70">
          Finding {index}
        </div>
      ) : null}
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {a.question}
      </div>
      {plain ? (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{plain}</p>
      ) : null}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <span
          className={`font-display text-base leading-snug ${
            muted ? "text-muted-foreground" : "text-navy"
          }`}
        >
          {a.answer}
        </span>
        {a.status ? <StatusBadge status={a.status} /> : null}
      </div>
      {a.source ? (
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Source: <span className={muted ? "text-muted-foreground" : "text-foreground/70"}>{a.source}</span>
        </div>
      ) : null}
    </div>
  );
}



function InvestigationPanel({
  id,
  available = true,
  state,
  title,
  icon: Icon,
  eyebrow,
  headline,
  investigated,
  answers,
  found,
  matters,
  notice,
  verdict,
  metrics,
  completedAt,
  pendingProvider,
  secondary,
  indicatorGroup,
  children,
  open,
  onOpenChange,
  findingsCount = 0,
  evidenceCount = 0,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  headline: string;
  investigated: string;
  answers: InvestigationAnswer[];
  found: string;
  matters: string;
  /** Rendered when a question set could not be answered by any source. */
  notice?: string | null;
  verdict?: { label: string; value: string; tone: string; caption?: string } | null;
  metrics?: Array<{
    label: string;
    value: string;
    caption?: string;
    tone?: string;
    emphasis?: boolean;
  }> | null;
  completedAt?: string | null;
  pendingProvider?: {
    title: string;
    status: string;
    willVerify: string[];
  } | null;
  secondary?: { title: string; answers: InvestigationAnswer[] } | null;
  indicatorGroup?: { title: string; items: InvestigationAnswer[] } | null;
  id?: string;
  available?: boolean;
  state?: InvestigationState;
  title?: string;
  children?: ReactNode;
  /** Controlled accordion state — only one investigation is open at a time. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  findingsCount?: number;
  evidenceCount?: number;


}) {
  const muted = !available;
  const invState: InvestigationState =
    state ?? (available ? "verified" : "awaiting_provider");
  const hasTechnical =
    !!pendingProvider ||
    (muted && answers.length > 0) ||
    (muted && !!secondary && secondary.answers.length > 0) ||
    (muted && !!indicatorGroup && indicatorGroup.items.length > 0);

  // ---- Presentation only: badges that actually appear in this panel ----
  const allAnswers: InvestigationAnswer[] = [
    ...answers,
    ...(secondary?.answers ?? []),
    ...(indicatorGroup?.items ?? []),
  ];
  const legendEntries = legendEntriesFor([
    investigationStatusLabel(invState),
    ...allAnswers.map((a) => a.status),
    ...allAnswers.map((a) => (a.source ? "Evidence Source" : null)),
  ]);
  const conclusionSources = Array.from(
    new Set(allAnswers.map((a) => a.source).filter(Boolean) as string[]),
  );
  const conclusionLimitation =
    invState === "awaiting_provider"
      ? pendingProvider
        ? `${pendingProvider.title}: ${pendingProvider.status}. No adverse inference was made.`
        : "This investigation requires a provider that is not currently connected. No adverse inference was made."
      : null;


  const technicalBlocks = (
    <>
      {pendingProvider ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              className={`font-display text-base ${muted ? "text-muted-foreground" : "text-navy"}`}
            >
              {pendingProvider.title}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700">
              {pendingProvider.status}
            </span>
          </div>
          <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {pendingProvider.willVerify.map((v) => (
              <li
                key={v}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {muted && answers.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {answers.map((a) => (
            <AnswerCard key={a.question} a={a} muted />
          ))}
        </div>
      ) : null}

      {muted && indicatorGroup && indicatorGroup.items.length > 0 ? (
        <div className="rounded-2xl border border-border/60 bg-muted/40 p-4 md:p-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {indicatorGroup.title}
          </div>
          <div className="mt-3 space-y-2">
            {indicatorGroup.items.map((it) => (
              <div
                key={it.question}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2 last:border-0 last:pb-0"
              >
                <span className="text-sm text-muted-foreground">{it.question}</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{it.answer}</span>
                  {it.status ? <StatusBadge status={it.status} /> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {muted && secondary && secondary.answers.length > 0 ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {secondary.title}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {secondary.answers.map((a) => (
              <AnswerCard key={a.question} a={a} muted />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );

  const headerSummary = (
    <div className="flex items-start gap-4">
      <div
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
          available
            ? "bg-secondary/60 text-navy shadow-card"
            : "bg-muted text-muted-foreground/70"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Eyebrow tone="navy">
            <span className={muted ? "text-muted-foreground" : undefined}>{eyebrow}</span>
          </Eyebrow>
          <InvestigationStatusBadge state={invState} />
        </div>
        {title ? (
          <div
            className={`mt-2 font-display text-[26px] leading-[1.12] tracking-tight md:text-[32px] ${
              available ? "text-navy" : "text-muted-foreground"
            }`}
          >
            {title}
          </div>
        ) : null}
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Question Answered
          </div>
          <div
            className={`mt-1 text-base font-semibold leading-snug outline-none md:text-lg ${
              available ? "text-foreground/90" : "text-muted-foreground"
            }`}
            data-inv-heading="true"
            tabIndex={-1}
          >
            {headline}
          </div>
        </div>
        {!muted && verdict ? (
          <div className="mt-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Executive Finding
            </div>
            <div
              className={`mt-0.5 text-[15px] font-semibold leading-snug ${verdict.tone}`}
            >
              {verdict.value}
            </div>
          </div>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <AccordionMetric
            muted={muted}
            label={`${findingsCount} Finding${findingsCount === 1 ? "" : "s"}`}
          />
          <AccordionMetric
            muted={muted}
            label={`${evidenceCount} Evidence Record${evidenceCount === 1 ? "" : "s"}`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <ReportAccordion
      id={id}
      open={open}
      onOpenChange={onOpenChange}
      tone={available ? "default" : "muted"}
      className="mt-6"
      summaryClassName="p-5 md:p-7"
      bodyClassName="p-6 md:p-9"
      data-available={available ? "true" : "false"}
      data-mode={invState}
      summary={headerSummary}
    >
      <div className="-mt-7">

      <InvestigationLegend entries={legendEntries} className="mt-7" />



      {/* ---- Mode 2: one prominent coverage-boundary explanation ---- */}
      {muted ? (
        <div className="mt-7 rounded-2xl border border-border/60 bg-muted/60 p-5 md:p-6">
          <div className="flex items-center gap-2">
            <InvestigationStatusIcon state={invState} />
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {investigationStatusLabel(invState)}
            </div>
          </div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {investigationStatusMeaning(invState)}
          </p>
        </div>
      ) : null}

      {!muted && verdict ? (
        <div className="mt-7 rounded-2xl border-2 border-navy/25 bg-secondary/50 p-6 shadow-card md:p-7">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy">
            Executive Finding
          </div>
          <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {verdict.label}
          </div>
          <div
            className={`mt-2 font-display text-3xl leading-tight md:text-4xl ${verdict.tone}`}
          >
            {verdict.value}
          </div>
          {verdict.caption ? (
            <div className="mt-1.5 text-sm font-semibold text-foreground/75">
              {verdict.caption}
            </div>
          ) : null}
        </div>
      ) : null}

      {!muted && metrics && metrics.length > 0 ? (
        <div className="mt-7">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Supporting Metrics
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((m) => (
              <div
                key={m.label}
                className={`rounded-2xl border p-4 md:p-5 ${
                  m.emphasis
                    ? "border-navy/25 bg-secondary/60 shadow-card sm:col-span-2"
                    : "border-border bg-secondary/40"
                }`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {m.label}
                </div>
                <div
                  className={`mt-1 font-display leading-tight ${
                    m.emphasis ? "text-4xl md:text-5xl" : "text-2xl"
                  } ${m.tone ?? "text-navy"}`}
                >
                  {m.value}
                </div>
                {m.caption ? (
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    {m.caption}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-7 rounded-xl border border-border/70 bg-secondary/20 p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Evidence Reviewed
        </div>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {investigated}
        </p>
      </div>


      {/* ---- Detailed findings (verified mode renders inline) ---- */}
      {!muted && answers.length > 0 ? (
        <div className="mt-7">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Detailed Findings
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {answers.map((a, ai) => (
              <AnswerCard key={a.question} a={a} index={ai + 1} />
            ))}
          </div>
        </div>
      ) : null}


      {!muted && indicatorGroup && indicatorGroup.items.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-border bg-secondary/30 p-4 md:p-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {indicatorGroup.title}
          </div>
          <div className="mt-3 space-y-2">
            {indicatorGroup.items.map((it) => (
              <div
                key={it.question}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2 last:border-0 last:pb-0"
              >
                <span className="text-sm text-foreground/85">{it.question}</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-navy">{it.answer}</span>
                  {it.status ? <StatusBadge status={it.status} /> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!muted && secondary && secondary.answers.length > 0 ? (
        <details className="mt-5 rounded-2xl border border-border bg-secondary/30 p-4">
          <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {secondary.title}
          </summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {secondary.answers.map((a) => (
              <AnswerCard key={a.question} a={a} />
            ))}
          </div>
        </details>
      ) : null}

      {/* ---- Collapsed technical provider details ---- */}
      {hasTechnical ? (
        <details
          className={`mt-6 rounded-2xl border p-4 md:p-5 ${
            muted
              ? "border-border/60 bg-muted/40"
              : "border-dashed border-border bg-secondary/30"
          }`}
        >
          <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Technical Provider Details
          </summary>
          <div className="mt-4 space-y-4">{technicalBlocks}</div>
        </details>
      ) : null}

      {notice ? (
        <div
          className={`mt-6 rounded-xl border p-4 text-sm ${
            muted
              ? "border-border/60 bg-muted/40 text-muted-foreground"
              : "border-dashed border-border bg-secondary/30 text-foreground/80"
          }`}
        >
          {notice}
        </div>
      ) : null}

      <ConclusionBasis
        className="mt-7"
        evidenceReviewed={investigated}
        sources={conclusionSources}
        state={invState}
        limitation={conclusionLimitation}
      />

      <div

        className={`mt-8 grid gap-5 rounded-2xl border p-5 md:grid-cols-2 md:p-6 ${
          muted ? "border-border/60 bg-muted/40" : "border-navy/20 bg-secondary/40"
        }`}
      >
        <div>
          <div
            className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
              muted ? "text-muted-foreground" : "text-navy"
            }`}
          >
            Result
          </div>
          <p
            className={`mt-1.5 text-[15px] font-medium leading-7 ${
              muted ? "text-muted-foreground" : "text-foreground/90"
            }`}
          >
            {found}
          </p>
        </div>
        <div className="md:border-l md:border-border/70 md:pl-6">
          <div
            className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
              muted ? "text-muted-foreground" : "text-navy"
            }`}
          >
            Why This Matters
          </div>
          <p
            className={`mt-1.5 text-[15px] font-medium leading-7 ${
              muted ? "text-muted-foreground" : "text-foreground/90"
            }`}
          >
            {matters}
          </p>
        </div>
      </div>


      {children ? (
        <div className={`mt-6 space-y-3 ${muted ? "opacity-70 grayscale" : ""}`}>
          {children}
        </div>
      ) : null}


      {completedAt ? (
        <div className="mt-6 border-t border-border/60 pt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Completed · <span className="text-foreground/60">{completedAt}</span>
        </div>
      ) : (
        <div className="mt-6 border-t border-border/60 pt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {investigationStatusLabel(invState)} ·{" "}
          <span className="text-foreground/60">
            {invState === "awaiting_provider"
              ? "Required provider not connected"
              : investigationStatusMeaning(invState)}
          </span>
        </div>
      )}
      </div>
    </ReportAccordion>

  );
}


type TimelineStep = { label: string; state: "complete" | "pending" };

/** Compact investigative workflow timeline. */
function InvestigationTimeline({ steps }: { steps: TimelineStep[] }) {
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <Eyebrow tone="navy">Investigation Timeline</Eyebrow>
      <div className="mt-1 font-display text-lg text-navy">
        Workflow followed for this vendor investigation
      </div>
      <ol className="mt-5 space-y-0">
        {steps.map((s, i) => (
          <li key={s.label} className="flex items-start gap-4">
            <div className="flex flex-col items-center">
              {s.state === "complete" ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-700" />
              ) : (
                <Clock className="h-5 w-5 shrink-0 text-amber-600" />
              )}
              {i < steps.length - 1 ? (
                <span className="my-1 h-8 w-px bg-border" aria-hidden />
              ) : null}
            </div>
            <div className="-mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 pb-5 last:pb-0">
              <span className="text-[15px] font-medium leading-6 text-foreground/90">
                {s.label}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                  s.state === "complete"
                    ? "border-emerald-600/30 bg-emerald-50 text-emerald-700"
                    : "border-amber-600/30 bg-amber-50 text-amber-700"
                }`}
              >
                {s.state === "complete" ? "Completed" : "Pending"}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function VendorIntelligence({

  data,
  number,
  bare = false,
  googleProfile = null,
  sosProfile = null,
  licensingProfile = null,
  bbbProfile = null,
  stateLicensingProfile = null,
  context = null,
  history = null,
  market = null,
}: {
  data: VendorIntelligenceData | null;
  number: string;
  bare?: boolean;
  googleProfile?: VendorSnapshot | null;
  sosProfile?: import("@/hooks/use-sos-snapshot").SosSnapshot | null;
  licensingProfile?: import("@/hooks/use-licensing-snapshot").LicensingSnapshot | null;
  bbbProfile?: import("@/hooks/use-bbb-snapshot").BBBSnapshot | null;
  stateLicensingProfile?: import("@/hooks/use-state-licensing-snapshot").StateLicensingSnapshot | null;
} & VendorIntelExtras) {
  const hasData =
    !!data &&
    (data.riskScore !== null ||
      data.overchargeFrequencyPct !== null ||
      data.averageSavings !== null ||
      data.previousReviews !== null ||
      data.marketPosition !== null);

  const internal = buildInternalEntry(data, history);
  const marketEntry = buildMarketEntry(market);
  const google = buildGoogleEntry(data, googleProfile, context);
  const sos = buildSosEntry(sosProfile);
  const bbb = buildBbbEntry(bbbProfile);
  const licensing = buildLicensingEntry(licensingProfile);
  const stateLicensing = buildStateLicensingEntry(stateLicensingProfile);
  // FMCSA is shown only when the vendor actually qualifies for federal
  // motor-carrier oversight; otherwise the section is hidden entirely.
  const fmcsaApplicable =
    licensing.status !== "Not Applicable" && licensing.status !== "Not Connected";
  const entries: SourceEntry[] = [
    google,
    marketEntry,
    sos,
    stateLicensing,
    ...(fmcsaApplicable ? [licensing] : []),
    internal,
    bbb,
  ];



  const gp = googleProfile?.status === "matched" ? googleProfile.profile : null;
  const sp = sosProfile?.status === "matched" ? sosProfile.profile : null;
  const publicVerified = !!gp || !!sp;
  const anyAdverse = entries.some((e) => e.adverse);

  const businessName = gp?.businessName ?? sp?.legalName ?? data?.vendor ?? "Not identified";
  const category =
    vstr(gp?.category) ?? vendorCategoryFromInvoiceType(data?.invoiceType) ?? "Not identified";
  /* Invoice-derived addresses frequently repeat the city/state/ZIP that
     was already captured in the street line ("1450 Loop Edinburg Tx 78539,
     Edinburg TX 78539"). The duplicate tail is removed for display only —
     the stored value is never rewritten. */
  const dedupeAddress = (raw: string): string => {
    const parts = raw
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    const kept: string[] = [];
    const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    for (const part of parts) {
      const already = kept.some(
        (k) => norm(k) === norm(part) || norm(k).endsWith(` ${norm(part)}`),
      );
      if (!already) kept.push(part);
    }
    return kept.join(", ");
  };
  const address = (() => {
    const raw =
      vstr(gp?.address) ?? vstr(sp?.principalAddress) ?? vstr(data?.serviceAddress);
    return raw ? dedupeAddress(raw) : "Not identified";
  })();
  const phone = vstr(gp?.phone) ?? "Not identified";
  const website = vstr(gp?.website);
  // Business Status is shown only when Google Business Profile supplies it.
  const businessStatus = (() => {
    const raw = vstr(gp?.businessStatus);
    if (!raw) return null;
    const s = raw.toUpperCase();
    if (s === "OPERATIONAL") return "Open";
    if (s === "CLOSED_TEMPORARILY") return "Temporarily Closed";
    if (s === "CLOSED_PERMANENTLY") return "Permanently Closed";
    return vtitleize(raw.toLowerCase());
  })();



  const overall = anyAdverse
    ? { label: "Adverse Findings Identified", tone: "text-red-700", dot: "bg-red-500" }
    : publicVerified
      ? { label: "Verified Business", tone: "text-emerald-700", dot: "bg-emerald-500" }
      : { label: "Limited Information Available", tone: "text-amber-700", dot: "bg-amber-500" };


  const unverified = entries.filter((e) => !!e.unverifiedReason);

  // A source earns a full expandable detail card only when it carries
  // meaningful retrieved information. Unconnected providers collapse
  // into a compact notice beneath the investigation they support.
  const hasMeaningfulDetail = (e: SourceEntry) =>
    e.status !== "Not Connected" && (e.fields.length > 0 || !!e.extra);


  /* Executive Investigation Summary — every value derives from the
     evidence already assembled above; no static assertions. */
  const identityVerified = !!google.verified || !!sos.verified;
  const marketRows = (market?.comparisons ?? []).filter(
    (r) => vstr(r.invoiceValue) || vstr(r.areaAverage),
  );
  const marketPositionRows = buildMarketPositionSummary(marketRows);
  const marketPositionValue =
    marketPositionRows.find((r) => r.label === "Overall Pricing")?.value ??
    vstr(data?.marketPosition) ??
    null;
  const priorReviews = history?.invoicesReviewed ?? null;
  const historyValue =
    priorReviews != null && priorReviews > 0
      ? `${priorReviews} Prior Review${priorReviews === 1 ? "" : "s"}`
      : "No Prior Reviews";
  const regulatoryAdverse = [sos, stateLicensing, licensing].some((e) => e.adverse);
  /* Presentation only: a regulatory source counts as "searched" when it
     actually executed a lookup. Unconnected, unsupported, errored, or
     inapplicable sources never produce a "None Identified" conclusion. */
  const SEARCHED_STATUSES: SourceStatus[] = ["Verified", "No Records Identified"];
  const regulatorySearched = [sos, stateLicensing, licensing, bbb].some((e) =>
    SEARCHED_STATUSES.includes(e.status),
  );
  const regulatoryValue = regulatoryAdverse
    ? "Findings Identified"
    : regulatorySearched
      ? "No Evidence Found"
      : "Awaiting Provider";

  const riskValue = anyAdverse
    ? "Elevated"
    : identityVerified
      ? "Low"
      : "Moderate";

  /* Confidence engine — weighted, per-investigation, partial credit.
     Each investigation carries its own weight and its own completeness
     (0..1). A provider that is missing therefore reduces confidence ONLY
     by the share of the investigation it would have contributed to, never
     by the whole investigation and never across the whole chapter. */
  const hasInternalHistory = (history?.invoicesReviewed ?? 0) > 0;

  // Licensing is "resolved" either when authority was located OR when no
  // license is legally required for the billed category — both are
  // definitive conclusions, so both earn full credit.
  const licensingApplies = /tow|storage|impound|wrecker|recover|repair|salvage|lien/i.test(
    `${data?.invoiceType ?? ""} ${category}`,
  );
  const reputationSample = gp?.reputation ?? null;

  const investigationWeights: Array<{
    label: string;
    weight: number;
    completeness: number;
  }> = [
    {
      label: "Business Identity",
      weight: 15,
      completeness: google.verified ? 1 : data?.vendor ? 0.35 : 0,
    },
    {
      label: "Business Registration",
      weight: 15,
      completeness: sos.verified ? 1 : 0,
    },
    {
      label: "Licensing & Authority",
      weight: 12,
      completeness:
        stateLicensing.verified || licensing.verified
          ? 1
          : !licensingApplies
            ? 1
            : 0,
    },
    {
      label: "Public Reputation",
      weight: 12,
      completeness: bbb.verified
        ? 1
        : reputationSample
          ? Math.min(1, 0.55 + (reputationSample.sampleSize ?? 0) / 20)
          : google.verified
            ? 0.25
            : 0,
    },
    {
      label: "Market Intelligence",
      weight: 16,
      completeness: marketRows.length > 0 ? 1 : 0,
    },
    {
      label: "Invoice Verification",
      weight: 18,
      completeness: data?.vendor ? 1 : 0,
    },
    { label: "Internal Validation", weight: 8, completeness: 1 },
    {
      label: "Historical Intelligence",
      weight: 4,
      completeness: hasInternalHistory ? 1 : 0,
    },
  ];

  const contributorWeights = investigationWeights
    .filter((c) => c.completeness > 0)
    .map((c) => ({
      label: c.label,
      weight: Math.round(c.weight * c.completeness),
      active: true,
    }));
  const contributorWeightTotal =
    contributorWeights.reduce((s, c) => s + c.weight, 0) || 1;
  const weightedContributors = contributorWeights.map((c) => ({
    label: c.label,
    share: Math.round((c.weight / contributorWeightTotal) * 100),
  }));
  const contributors = contributorWeights.map((c) => c.label);

  const achievedWeight = investigationWeights.reduce(
    (s, c) => s + c.weight * c.completeness,
    0,
  );
  const totalWeight =
    investigationWeights.reduce((s, c) => s + c.weight, 0) || 1;
  /* SAB-004: this is INVESTIGATION COVERAGE — the share of vendor
     intelligence sources that returned a usable result. It is not the
     report-wide analysis confidence and must never be labelled "Confidence".
     No clamping: a genuinely thin investigation reports a low number. */
  const coverageScore = Math.max(
    0,
    Math.min(
      100,
      Math.round((achievedWeight / totalWeight) * 100) +
        (identityVerified ? 4 : 0) -
        (anyAdverse ? 8 : 0),
    ),
  );

  const coverageBand =
    coverageScore >= 85 ? "Broad Coverage" : coverageScore >= 65 ? "Partial Coverage" : "Limited Coverage";

  /* Completion stamp for finished investigation modules. Derived from the
     immutable evidence snapshots so the value is stable for a frozen
     report; omitted entirely when no snapshot timestamp exists. */
  const investigationCompletedAt = (() => {
    const raw =
      googleProfile?.capturedAt ??
      bbbProfile?.capturedAt ??
      sosProfile?.capturedAt ??
      stateLicensingProfile?.capturedAt ??
      licensingProfile?.capturedAt ??
      null;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "UTC",
    });
    return `${date} · ${time} UTC`;
  })();


  // The two headline metrics are rendered as hero tiles; the remaining
  // findings stay in the existing summary card grid.
  const heroMetrics: Array<{
    label: string;
    value: string;
    caption: string;
    tone: string;
    meter?: number;
    explain?: string;
  }> = [
    {
      label: "Overall Vendor Risk",
      value: riskValue.toUpperCase(),
      caption:
        riskValue === "Low"
          ? "No material adverse indicators identified"
          : riskValue === "Elevated"
            ? "Adverse indicators identified and detailed below"
            : "Independent confirmation was limited",
      tone:
        riskValue === "Low"
          ? "text-emerald-700"
          : riskValue === "Elevated"
            ? "text-red-700"
            : "text-amber-700",
      explain:
        "In plain terms: how much concern the documented evidence raises about this vendor. It does not change any dollar figure in this report.",
    },
    {
      label: "Investigation Coverage",
      value: `${coverageScore}%`,
      caption: coverageBand,
      tone: coverageScore >= 85 ? "text-emerald-700" : "text-navy",
      meter: coverageScore,
      explain:
        "In plain terms: how much of the information we tried to collect was actually returned by the sources searched. A lower figure means less was available, not that anything negative was found.",
    },

  ];

  /* Accordion state — only one investigation is expanded at a time.
     Presentation only; no report data is derived from this value. */
  const [openInvestigation, setOpenInvestigation] = useState<string | null>(null);

  /* In-page navigation from a summary card to its detailed
     investigation. Presentation only: expands, scrolls, moves focus, and
     never changes the route or any report state. */
  const goToInvestigation = (targetId: string) => {
    setOpenInvestigation(targetId);
    if (typeof document === "undefined") return;
    const el = document.getElementById(targetId);
    if (!el) return;
    // Let the accordion paint its expanded body before scrolling to it.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      const heading = el.querySelector<HTMLElement>("[data-inv-heading]");
      (heading ?? el).focus?.({ preventScroll: true });
    });
  };

  /* Presentation-only availability map. `target` points at the detailed
     investigation anchor; `available: false` renders the neutral,
     non-interactive treatment with no success styling. */
  const summaryRows: Array<{
    label: string;
    value: string;
    positive?: boolean;
    explain?: string;
    available: boolean;
    target?: string;
    unavailableNote?: string;
  }> = [
    {
      label: "Vendor Identity",
      value: identityVerified ? "Successfully Verified" : "Invoice-Derived",
      positive: identityVerified,
      available: identityVerified,
      target: "vendor-investigation-trust",
      unavailableNote:
        "Independent identity confirmation was not returned by the connected sources.",
      explain:
        "Whether we could confirm the business on the invoice is a real, identifiable company.",
    },
    {
      label: "Public Business Presence",
      value: google.verified ? "Confirmed" : "Awaiting Provider",
      positive: !!google.verified,
      available: !!google.verified,
      target: "vendor-investigation-trust",
      unavailableNote:
        "No matching public business listing was returned by the connected sources.",
      explain:
        "Whether the vendor appears in public business listings with details matching the invoice.",
    },
    ...(marketPositionValue
      ? [
          {
            label: "Compared With Regional Providers",
            value: marketPositionValue,
            available: marketRows.length > 0,
            target: "vendor-investigation-market",
            explain:
              "Where this vendor's billed charges sit compared with other providers in the same region.",
          },
        ]
      : []),
    {
      label: "ClaimGuard Historical Data",
      value: historyValue,
      available: true,
      target: "vendor-investigation-history",
      explain:
        "Whether ClaimGuard has reviewed invoices from this vendor before, which would reveal repeat billing patterns.",
    },
    {
      label: "Regulatory Findings",
      value: regulatoryValue,
      positive: regulatorySearched && !regulatoryAdverse,
      available: regulatorySearched,
      target: "vendor-investigation-reputation",
      unavailableNote:
        "Regulatory records were not available from the currently connected sources.",
      explain:
        "Whether any complaint, enforcement, or consumer-alert record was located for this vendor.",
    },
  ];



  const reportMeta = [
    { label: "Report Engine", value: "ClaimGuard Intelligence Engine v1.0" },
    {
      label: "Market Database",
      value:
        vstr(market?.marketSnapshotDate) ??
        new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    },
    { label: "API Version", value: "v3" },
  ];

  /* ── Investigative question sets ───────────────────────────
   * Every answer below is read from evidence already derived by the
   * source builders. No answer is invented: when no connected source
   * establishes a value, the question is answered honestly ("Not
   * independently verified") rather than left blank or implied.
   */
  const fieldOf = (e: SourceEntry, label: string): SourceField | null =>
    e.fields.find((f) => f.label === label) ?? null;
  const answerOf = (
    question: string,
    e: SourceEntry,
    label: string,
    fallback: string,
    fallbackStatus?: SourceStatus,
  ): InvestigationAnswer => {
    const f = fieldOf(e, label);
    return f
      ? { question, answer: f.value, status: f.status }
      : { question, answer: fallback, status: fallbackStatus };
  };

  const sig = gp?.matchSignals ?? null;
  const signalAnswer = (
    question: string,
    score: number | null | undefined,
    yes: string,
    partial: string,
    none: string,
  ): InvestigationAnswer => {
    if (typeof score !== "number")
      return { question, answer: none, status: "Not Verified" };
    if (score >= 0.6) return { question, answer: yes, status: "Matched" };
    if (score > 0) return { question, answer: partial, status: "Partial Match" };
    return { question, answer: none, status: "Not Verified" };
  };

  const billedCategory = vendorCategoryFromInvoiceType(data?.invoiceType);
  const catTokens = (s: string) =>
    s.toLowerCase().split(/[^a-z]+/).filter((t) => t.length > 3);
  const categoryMatches =
    !!vstr(gp?.category) && !!billedCategory
      ? catTokens(gp!.category as string).some((t) =>
          catTokens(billedCategory).includes(t),
        )
      : false;

  const INVOICE_SRC = "Submitted invoice";
  const GOOGLE_SRC = "Google Business Profile";
  const SOS_SRC = "Secretary of State records";
  const LICENSE_SRC = "State licensing authority";
  const FMCSA_SRC = "FMCSA operating authority records";
  const BBB_SRC = "Better Business Bureau";
  const CG_SRC = "ClaimGuard historical database";
  const MARKET_SRC = "ClaimGuard market intelligence";

  const servicesField = fieldOf(google, "Services Offered");

  /* Investigation 1 — Can I trust this business? */
  const trustAnswers: InvestigationAnswer[] = [
    {
      question: "Business identity",
      answer: businessName,
      status: publicVerified ? "Verified" : "Invoice-Derived",
      source: publicVerified ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      ...signalAnswer(
        "Invoice match",
        sig?.nameMatch,
        "Matched to the located business",
        "Partial match — name similarity below threshold",
        NOT_YET_VERIFIED,
      ),
      source: gp ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      question: "Active operation",
      answer:
        businessStatus === "Open"
          ? "Currently operating"
          : businessStatus ?? NOT_YET_VERIFIED,
      status:
        businessStatus === "Open"
          ? "Verified"
          : businessStatus
            ? "High Risk"
            : "Not Verified",
      source: businessStatus ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      ...signalAnswer(
        "Business location",
        sig?.addressMatch,
        `Confirmed — ${address}`,
        `Partial match — ${address}`,
        address !== "Not identified" ? `${address} (invoice-derived)` : NOT_YET_VERIFIED,
      ),
      source: gp ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      ...signalAnswer(
        "Business contact information",
        sig?.phoneMatch,
        `Confirmed — ${phone}`,
        `Partial match — ${phone}`,
        phone !== "Not identified" ? `${phone} (invoice-derived)` : NOT_YET_VERIFIED,
      ),
      source: gp ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      question: "Services provided",
      answer:
        servicesField?.value ??
        (categoryMatches
          ? `${category} — consistent with the billed services`
          : category !== "Not identified"
            ? category
            : NOT_YET_VERIFIED),
      status: categoryMatches ? "Matched" : servicesField ? "Reviewed" : "Invoice-Derived",
      source: servicesField || vstr(gp?.category) ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      question: "Independent verification",
      answer: publicVerified
        ? "Confirmed against public business records"
        : NOT_YET_VERIFIED,
      status: publicVerified ? "Verified" : "Not Verified",
      source: publicVerified ? [GOOGLE_SRC, sos.verified ? SOS_SRC : null].filter(Boolean).join(" · ") : INVOICE_SRC,
    },
    {
      question: "Public website",
      answer: website ?? NOT_YET_VERIFIED,
      status: website ? "Verified" : "Not Verified",
      source: website ? GOOGLE_SRC : INVOICE_SRC,
    },
  ];

  /* Investigation 2 — Is the business legally established? (registration)
     Investigation 3 — Can this business legally perform this work? (licensing)
     The two are deliberately independent: a registered entity is not
     necessarily licensed, and licensing data arrives from a different
     class of provider. Keeping them separate lets each populate on its
     own timeline as integrations are enabled. */
  const licenseRequired = /tow|storage|impound|wrecker|recover|repair|salvage|lien/i.test(
    `${data?.invoiceType ?? ""} ${category}`,
  );
  const licenseStatusField = fieldOf(stateLicensing, "License Status");
  const licenseStatusText = (licenseStatusField?.value ?? "").toLowerCase();

  /* Reason-specific unavailability wording.
     A missing value is never reported as a generic placeholder: the
     report states WHY the value is absent (provider not connected,
     provider down, jurisdiction not covered, or genuinely no record). */
  const registrationState =
    vstr(sp?.stateOfRegistration) ?? null;
  /* The registration gap sentence and the Secretary of State row in the
     source table must never disagree. Both resolve from the same
     disclosure vocabulary, so a stub or unconnected provider can never be
     narrated as a completed search that found nothing. */
  const registrationGap: { text: string; status: SourceStatus } = (() => {
    const where = registrationState ? ` for ${registrationState}` : " for this state";
    const disclosure = resolveDisclosure({
      provider: sosProfile?.provider ?? null,
      status: sosProfile?.status ?? null,
      hasProfile: !!sp,
    });
    switch (disclosure) {
      case "No Records Identified":
        return {
          text: "No matching registration located in the state business registry.",
          status: "No Records Identified",
        };
      case "Provider Error":
        return {
          text: "Business registry provider was reachable, but the verification request did not complete.",
          status: "Provider Error",
        };
      case "Unsupported":
        return {
          text: `The business registry provider does not cover this record type${where}.`,
          status: "Unsupported",
        };
      default:
        return {
          text: `Live business registry verification was not performed${where} because the applicable provider is not currently connected.`,
          status: "Not Connected",
        };
    }
  })();

  const regAnswer = (
    question: string,
    label: string,
  ): InvestigationAnswer =>
    sos.verified
      ? {
          ...answerOf(question, sos, label, registrationGap.text, registrationGap.status),
          source: SOS_SRC,
        }
      : {
          question,
          answer: registrationGap.text,
          status: registrationGap.status,
          source: SOS_SRC,
        };

  const registrationAnswers: InvestigationAnswer[] = [
    {
      question: "Registered entity",
      answer: sos.verified
        ? fieldOf(sos, "Legal Name")?.value ?? businessName
        : registrationGap.text,
      status: sos.verified ? "Verified" : registrationGap.status,
      source: SOS_SRC,
    },
    regAnswer("Entity status", "Entity Status"),
    regAnswer("Good standing", "Standing"),
    regAnswer("Formation date", "Formation Date"),
    regAnswer("Registered agent", "Registered Agent"),
    regAnswer("Business type", "Entity Type"),
  ];

  /* Licensing gap wording — distinguishes "not required", "jurisdiction
     not covered by the connected registry", "provider down", and
     "searched, no record". */
  const licensingGap: { text: string; status: SourceStatus } = (() => {
    const st = stateLicensingProfile?.lookupStatus ?? null;
    if (!licenseRequired)
      return {
        text: "Licensing not required for this business category.",
        status: "Not Applicable",
      };
    switch (st) {
      case "not_found":
      case "no_verified_match":
      case "multiple_candidates":
        return {
          text: "Searched the state licensing registry — no license record located for this business.",
          status: "No Records Identified",
        };
      case "no_coverage":
        return {
          text: "The connected state licensing registry does not publish this license category.",
          status: "Unsupported",
        };
      case "unavailable":
      case "provider_error":
      case "timeout":
      case "quota_exceeded":
        return {
          text: "Licensing provider temporarily unavailable.",
          status: "Unavailable",
        };
      default:
        return {
          text: "Live licensing registry not connected for this jurisdiction.",
          status: "Not Connected",
        };
    }
  })();

  /* What the connected licensing registry actually publishes. Stated so a
     "no record" result is never mistaken for "unlicensed operator". */
  const licensingCoverageNote: string | null = (() => {
    const cov = stateLicensingProfile?.coverage ?? null;
    if (!cov) return null;
    const pretty = (t: string) =>
      t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const checked = (cov.licenseTypesChecked ?? []).map(pretty);
    const unsupported = (cov.licenseTypesUnsupported ?? []).map(pretty);
    if (!checked.length && !unsupported.length) return null;
    const authority =
      (cov.authoritiesChecked ?? []).map((a) => a.name).filter(Boolean)[0] ??
      "the connected state registry";
    const parts: string[] = [];
    if (checked.length)
      parts.push(`${authority} publishes: ${checked.join(", ")}`);
    if (unsupported.length)
      parts.push(`not published in this registry: ${unsupported.join(", ")}`);
    return `${parts.join(" · ")}.`;
  })();




  const licenseAnswers: InvestigationAnswer[] = [
    {
      question: "License required",
      answer: licenseRequired
        ? "This service category is license-regulated in most jurisdictions"
        : "No licensing requirement established for this service category",
      status: licenseRequired ? "Reviewed" : "Not Applicable",
      source: INVOICE_SRC,
    },
    {
      question: "License number",
      answer: stateLicensing.verified
        ? fieldOf(stateLicensing, "License Number")?.value ?? licensingGap.text
        : licensingGap.text,
      status: stateLicensing.verified ? "Verified License" : licensingGap.status,
      source: LICENSE_SRC,
    },
    {
      question: "License status",
      answer: stateLicensing.verified
        ? licenseStatusField?.value ?? "License located"
        : licensingGap.text,
      status: stateLicensing.verified
        ? /suspend|revok/.test(licenseStatusText)
          ? "Suspended"
          : "Verified License"
        : licensingGap.status,
      source: LICENSE_SRC,
    },
    {
      question: "License expiration",
      answer: stateLicensing.verified
        ? fieldOf(stateLicensing, "Expiration Date")?.value ?? licensingGap.text
        : licensingGap.text,
      status: stateLicensing.verified ? "Reviewed" : licensingGap.status,
      source: LICENSE_SRC,
    },
    {
      question: "Operating authority",
      answer: fmcsaApplicable
        ? fieldOf(licensing, "Operating Authority")?.value ??
          (licensingProfile?.lookupStatus === "misconfigured"
            ? "Federal operating-authority provider not connected."
            : licensingProfile?.lookupStatus === "not_found"
              ? "Searched federal motor-carrier records — no operating authority located."
              : "Federal operating-authority provider temporarily unavailable.")
        : "Not applicable to this service category",
      status: fmcsaApplicable
        ? fieldOf(licensing, "Operating Authority")
          ? "Verified"
          : licensingProfile?.lookupStatus === "not_found"
            ? "No Records Identified"
            : licensingProfile?.lookupStatus === "misconfigured"
              ? "Not Connected"
              : "Unavailable"
        : "Not Applicable",
      source: fmcsaApplicable ? FMCSA_SRC : INVOICE_SRC,
    },
    {
      question: "Required permit",
      answer: stateLicensing.verified
        ? fieldOf(stateLicensing, "License Type")?.value ?? licensingGap.text
        : licensingGap.text,
      status: stateLicensing.verified ? "Reviewed" : licensingGap.status,
      source: LICENSE_SRC,
    },
    ...(!stateLicensing.verified && licensingCoverageNote
      ? [
          {
            question: "Registry coverage",
            answer: licensingCoverageNote,
            status: "Reviewed" as SourceStatus,
            source: LICENSE_SRC,
          } as InvestigationAnswer,
        ]
      : []),

    ...(fmcsaApplicable
      ? [
          {
            question: "Insurance filing",
            answer:
              fieldOf(licensing, "Insurance on File")?.value ?? NOT_YET_VERIFIED,
            status: fieldOf(licensing, "Insurance on File")
              ? "Verified"
              : ("Not Verified" as SourceStatus),
            source: FMCSA_SRC,
          } as InvestigationAnswer,
        ]
      : []),
    ...(stateLicensing.verified
      ? [
          {
            ...answerOf(
              "Disciplinary actions",
              stateLicensing,
              "Disciplinary Actions",
              "None identified",
              "No Records Identified",
            ),
            source: LICENSE_SRC,
          },
        ]
      : []),
  ];


  /* Investigation — Historical behavior.
     The full slate of historical measures is always rendered so the
     layout is stable; each measure fills in automatically as ClaimGuard
     history accumulates for this vendor. */
  const NO_HISTORY = "No historical data available";
  const histAnswer = (question: string, label: string): InvestigationAnswer =>
    hasInternalHistory
      ? { ...answerOf(question, internal, label, NO_HISTORY, "No Records Identified"), source: CG_SRC }
      : { question, answer: NO_HISTORY, status: "No Records Identified", source: CG_SRC };

  // Primary historical measures render by default; the remaining
  // analytics collapse into an expandable disclosure.
  const historyAnswers: InvestigationAnswer[] = [
    {
      question: "Prior ClaimGuard reviews",
      answer: hasInternalHistory ? "Prior reviews on file" : NO_HISTORY,
      status: hasInternalHistory ? "Historical Data" : "No Records Identified",
      source: CG_SRC,
    },
    histAnswer("Average negotiated savings", "Average Negotiated Savings"),
    histAnswer("Negotiation success rate", "Historical Success Rate"),
    histAnswer("Repeat billing patterns", "Repeat Findings"),
    histAnswer("Historical risk trend", "Vendor Risk Score"),
    histAnswer("Most common disputed charge", "Most Disputed Charge"),
  ];

  const historySecondaryAnswers: InvestigationAnswer[] = [
    histAnswer("Total invoices reviewed", "Invoices Reviewed"),
    histAnswer("Total dollars billed", "Total Billed"),
    histAnswer("Total dollars negotiated", "Total Negotiated"),
    histAnswer("Average storage rate", "Average Storage Rate"),
    histAnswer("Escalation history", "Escalation History"),
    histAnswer("Historical reliability", "Historical Reliability"),
  ];



  /* Investigation 4 — Public reputation.
     Written as customer-facing decision questions. The answer set is
     provider-agnostic: additional reputation providers (state consumer
     protection, review aggregators, enforcement registries) can supply
     the same questions without changing this structure. */
  const bbbAccredited = fieldOf(bbb, "Accredited");
  const bbbRating = fieldOf(bbb, "BBB Rating");
  const bbbYears = fieldOf(bbb, "Years in Business");
  const bbbComplaints = fieldOf(bbb, "Complaint Count");
  const bbbCategories = fieldOf(bbb, "Complaint Categories");
  const bbbSeverity = fieldOf(bbb, "Complaint Severity");
  const bbbTrend = fieldOf(bbb, "Complaint Trend");
  const bbbGovActions = fieldOf(bbb, "Government Actions");
  const bbbAlerts = fieldOf(bbb, "Business Alerts");
  /* BBB retrieval state. The distinction matters: a public profile link
     is NOT the same as retrieved BBB data, and neither is the same as
     "no adverse findings". Never report absence of data as absence of
     findings. */
  const bbbRetrieved = !!bbbProfile?.coverage?.programmaticDataVerified;
  const bbbReferenceOnly =
    !bbbRetrieved && bbbProfile?.lookupStatus === "external_reference_available";
  const bbbGap: { text: string; status: SourceStatus } = bbbReferenceOnly
    ? {
        text: "Public BBB profile reference located; rating and complaint records were not retrieved.",
        status: "Public Reference",
      }
    : bbbProfile?.lookupStatus === "not_found" ||
        bbbProfile?.lookupStatus === "no_verified_data"
      ? {
          text: "Searched — no BBB profile located for this business.",
          status: "No Records Identified",
        }
      : bbbProfile?.lookupStatus === "provider_error" ||
          bbbProfile?.lookupStatus === "unavailable"
        ? {
            text: "Reputation provider temporarily unavailable.",
            status: "Unavailable",
          }
        : {
            text: "Live reputation data provider not connected; BBB records were not retrieved.",
            status: "Not Connected",
          };
  /* Every unanswered reputation question states what specifically was not
     established, then the reason — the same gap sentence is never pasted
     verbatim as the answer to unrelated questions. */
  const bbbGapFor = (subject: string): string =>
    `${subject} not established — ${bbbGap.text.charAt(0).toLowerCase()}${bbbGap.text.slice(1)}`;
  /* Absence of an adverse record is only reportable when data was
     actually retrieved; otherwise the gap wording is used. */
  const noAdverse = (
    label: string,
    subject?: string,
  ): { text: string; status: SourceStatus } =>
    bbbRetrieved
      ? { text: label, status: "No Records Identified" }
      : { text: subject ? bbbGapFor(subject) : bbbGap.text, status: bbbGap.status };

  /* Reputation provider fallback: the BBB record is preferred where it
     exists, but reputation is NOT reported as unavailable while a live
     public review record exists. Google Business Profile ratings, review
     volume, sentiment, trend, and recurring themes are retrieved on every
     matched vendor and answer the same investigative questions. */
  const rep = gp?.reputation ?? null;
  /* Independent formation date from the state registry, when registration
     was verified — a stronger basis for "years in business" than review age. */
  const sosFormationDate = sos.verified
    ? (fieldOf(sos, "Formation Date")?.value ?? null)
    : null;
  const googleRating = typeof gp?.rating === "number" ? gp.rating : null;
  const googleReviewCount =
    typeof gp?.reviewCount === "number" ? gp.reviewCount : null;
  const hasPublicReviews = googleRating != null || !!rep;
  const listOf = (xs: string[] | undefined | null): string | null =>
    xs && xs.length ? xs.join(" · ") : null;

  const reputationAnswers: InvestigationAnswer[] = [
    {
      question: "How do other customers rate this business?",
      answer:
        bbbRating?.value ??
        (googleRating != null
          ? `${googleRating.toFixed(1)} out of 5.0${
              googleReviewCount != null
                ? ` across ${googleReviewCount.toLocaleString("en-US")} public reviews`
                : ""
            }`
          : bbbGapFor("Public rating")),
      status: bbbRating
        ? bbbRating.status
        : googleRating != null
          ? "Verified"
          : bbbGap.status,
      source: bbbRating ? BBB_SRC : googleRating != null ? GOOGLE_SRC : BBB_SRC,
    },
    ...(rep
      ? [
          {
            question: "What is the overall customer sentiment?",
            answer: `Reviews are ${rep.sentiment} — ${rep.positiveSharePct}% of the sampled reviews rated 4★ or better and ${rep.negativeSharePct}% rated 2★ or lower (sample of ${rep.sampleSize}).`,
            status: "Verified" as SourceStatus,
            source: GOOGLE_SRC,
          } as InvestigationAnswer,
          {
            question: "Is the reputation improving or deteriorating?",
            answer: rep.trend
              ? `Recent reviews are ${rep.trend} relative to older reviews in the sample.`
              : "Review sample is too small to establish a reliable trend.",
            status: rep.trend ? "Verified" : ("No Records Identified" as SourceStatus),
            source: GOOGLE_SRC,
          } as InvestigationAnswer,
          ...(listOf(rep.commonComplaints)
            ? [
                {
                  question: "What do dissatisfied customers complain about?",
                  answer: `Recurring themes in negative reviews: ${listOf(rep.commonComplaints)}.`,
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(listOf(rep.commonCompliments)
            ? [
                {
                  question: "What do satisfied customers praise?",
                  answer: `Recurring themes in positive reviews: ${listOf(rep.commonCompliments)}.`,
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(rep.reviewGrowthTrend
            ? [
                {
                  question: "Is review activity growing or slowing?",
                  answer: `Public review volume is ${rep.reviewGrowthTrend} — the most recent twelve months compared against the twelve months before it.`,
                  status: "Verified" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(rep.mostRecentReviewDate
            ? [
                {
                  question: "How current is the public record?",
                  answer:
                    `Most recent public review dated ${rep.mostRecentReviewDate}` +
                    (rep.oldestReviewDate
                      ? `; sampled record spans back to ${rep.oldestReviewDate}.`
                      : ".") +
                    (rep.totalPhotos
                      ? ` The public profile carries ${rep.totalPhotos} published photo${rep.totalPhotos === 1 ? "" : "s"}.`
                      : ""),
                  status: "Verified" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(listOf(rep.mentionedServices)
            ? [
                {
                  question: "What services is this vendor publicly known for?",
                  answer: `Reviewers specifically describe: ${listOf(rep.mentionedServices)}.`,
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(listOf(rep.positiveKeywords) || listOf(rep.negativeKeywords)
            ? [
                {
                  question: "Which words recur most in customer language?",
                  answer: [
                    listOf(rep.positiveKeywords)
                      ? `Positive reviews: ${listOf(rep.positiveKeywords)}.`
                      : null,
                    listOf(rep.negativeKeywords)
                      ? `Negative reviews: ${listOf(rep.negativeKeywords)}.`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" "),
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(rep.consistencyScore != null
            ? [
                {
                  question: "How consistent is the customer experience?",
                  answer: `Reputation consistency score ${rep.consistencyScore}/100 — ${
                    rep.consistencyScore >= 70
                      ? "ratings cluster tightly, so the average is a reliable read."
                      : "ratings are polarized, so the average understates the spread of customer experience."
                  }`,
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
          ...(rep.summary
            ? [
                {
                  question: "Summary of public sentiment",
                  answer: rep.summary,
                  status: "Reviewed" as SourceStatus,
                  source: GOOGLE_SRC,
                } as InvestigationAnswer,
              ]
            : []),
        ]
      : []),
    {
      question: "Is the business accredited?",
      answer: bbbAccredited?.value ?? bbbGapFor("Accreditation status"),
      status: bbbAccredited ? bbbAccredited.status : bbbGap.status,
      source: BBB_SRC,
    },
    {
      question: "How long has it been in business?",
      answer:
        bbbYears?.value ??
        (sosFormationDate
          ? `Registered since ${sosFormationDate} per the state business registry.`
          : rep?.yearsInBusiness
            ? `Publicly visible for at least ${rep.yearsInBusiness} year${rep.yearsInBusiness === 1 ? "" : "s"} based on the earliest retrieved public review.`
            : bbbGapFor("Time in business")),
      status: bbbYears
        ? bbbYears.status
        : sosFormationDate
          ? "Verified"
          : rep?.yearsInBusiness
            ? "Reviewed"
            : bbbGap.status,
      source: bbbYears
        ? BBB_SRC
        : sosFormationDate
          ? SOS_SRC
          : rep?.yearsInBusiness
            ? GOOGLE_SRC
            : BBB_SRC,
    },

    {
      question: "How many complaints have been filed?",
      answer:
        bbbComplaints?.value ??
        noAdverse("No complaint records identified", "Complaint history").text,
      status: bbbComplaints
        ? bbbComplaints.status
        : noAdverse("No complaint records identified", "Complaint history").status,
      source: BBB_SRC,
    },
    {
      question: "Does public reputation increase risk on this invoice?",
      answer: bbb.adverse
        ? "Yes — adverse public indicators were factored into this review"
        : bbbRetrieved
          ? "No — public reputation did not increase invoice risk"
          : rep
            ? rep.sentiment === "predominantly negative"
              ? "Yes — public review sentiment is predominantly negative and was factored into this review"
              : "No — public review sentiment did not raise invoice risk; no adverse regulatory record was retrieved"
            : "Not established — no reputation data was retrieved, so no reputation-based risk was applied",
      status: bbb.adverse
        ? "High Risk"
        : bbbRetrieved
          ? "Low Risk"
          : rep
            ? rep.sentiment === "predominantly negative"
              ? "High Risk"
              : "Low Risk"
            : bbbGap.status,
      source: bbb.adverse || bbbRetrieved ? BBB_SRC : rep ? GOOGLE_SRC : BBB_SRC,
    },

  ];



  /* Related risk indicators are consolidated into a single card so the
     investigation does not repeat one card per indicator. Each retains
     its own status. */
  const reputationRiskIndicators: InvestigationAnswer[] = [
    {
      question: "Government enforcement",
      answer:
        bbbGovActions?.value ??
        noAdverse("None identified", "Government enforcement record").text,
      status: bbbGovActions
        ? "High Risk"
        : noAdverse("None identified", "Government enforcement record").status,
      source: BBB_SRC,
    },
    {
      question: "Fraud indicators",
      answer: bbb.adverse
        ? "Adverse public indicators identified"
        : noAdverse("None identified", "Fraud indicator record").text,
      status: bbb.adverse
        ? "High Risk"
        : noAdverse("None identified", "Fraud indicator record").status,
      source: BBB_SRC,
    },
    {
      question: "Consumer alerts",
      answer: bbbAlerts?.value ?? noAdverse("None identified", "Consumer alert record").text,
      status: bbbAlerts
        ? "High Risk"
        : noAdverse("None identified", "Consumer alert record").status,
      source: BBB_SRC,
    },
    {
      question: "Complaint trends",
      answer: bbbTrend?.value ?? noAdverse("No trend identified", "Complaint trend").text,
      status: bbbTrend
        ? bbbTrend.status
        : noAdverse("No trend identified", "Complaint trend").status,
      source: BBB_SRC,
    },
    {
      question: "Complaint severity",
      answer:
        bbbSeverity?.value ??
        bbbCategories?.value ??
        noAdverse("No pattern identified", "Complaint severity").text,
      status: bbbSeverity
        ? bbbSeverity.status
        : bbbCategories
          ? bbbCategories.status
          : noAdverse("No pattern identified", "Complaint severity").status,
      source: BBB_SRC,
    },
  ];

  const reputationRisk = bbb.adverse
    ? { value: "High", tone: "text-red-700" }
    : bbbComplaints || bbbRating
      ? { value: "Moderate", tone: "text-amber-700" }
      : bbbRetrieved
        ? { value: "Low", tone: "text-emerald-700" }
        : { value: "Not established", tone: "text-slate-600" };


  /* Investigation 5 — Market reasonableness and financial guidance */
  const marketAnswerFor = (test: RegExp, question: string): InvestigationAnswer => {
    const row = marketRows.find((r) => test.test(r.label));
    if (!row)
      return { question, answer: "No comparable benchmark available", source: MARKET_SRC };
    const ratio = ratioFor(row);
    const posture = positionLabel(row.position);
    const answer =
      ratio != null
        ? ratio > 1.1
          ? `Above the regional average (${ordinal(percentileFromRatio(ratio))} percentile)`
          : ratio < 0.9
            ? "Below the regional average"
            : "Consistent with the regional average"
        : posture ?? "Reviewed against regional benchmarks";
    return {
      question,
      answer,
      source: MARKET_SRC,
      status:
        ratio != null && ratio >= 1.5
          ? "Market Outlier"
          : ratio != null && ratio > 1.1
            ? "Above Market"
            : ratio != null && ratio < 0.9
              ? "Below Market"
              : "Market Average",
    };
  };
  const overallMarket = buildOverallMarketRow(marketRows);
  const overallPercentile = marketPositionRows.find(
    (r) => r.label === "Market Percentile",
  );
  const money = (n: number) =>
    n.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  // Negotiation opportunity is the amount billed above the regional
  // average; the range brackets a conservative-to-full recovery.
  const opportunity =
    overallMarket && overallMarket.differenceNum > 0
      ? overallMarket.differenceNum
      : 0;
  const marketAnswers: InvestigationAnswer[] =
    marketRows.length > 0
      ? [
          marketAnswerFor(/storage/i, "Are storage charges reasonable?"),
          marketAnswerFor(/tow/i, "Are towing charges reasonable?"),
          marketAnswerFor(/labor/i, "Are labor rates reasonable?"),
          {
            /* Benchmark-level figures. `buildOverallMarketRow` sums the
               invoiceValue / areaAverage of the benchmarked comparison rows
               only — it is NOT a whole-invoice total and must never borrow the
               frozen report-wide labels (Estimated Fair Payment, Total
               Supported Recovery, Market-Based Recovery, Settlement Goal). */
            question: "Benchmarked charges — billed",
            answer: overallMarket?.invoice ?? "Not established",
            source: INVOICE_SRC,
          },
          {
            question: "Benchmarked charges — market amount",
            answer: overallMarket?.average ?? "Not established",
            source: MARKET_SRC,
          },
          {
            question: "Benchmarked charges — variance",
            answer: overallMarket?.difference ?? "Not established",
            status: overallMarket
              ? overallMarket.position === "outlier"
                ? "Market Outlier"
                : overallMarket.position === "above"
                  ? "Above Market"
                  : overallMarket.position === "below"
                    ? "Below Market"
                    : "Market Average"
              : undefined,
            source: MARKET_SRC,
          },
          {
            question: "Benchmarked charges — amount above benchmark",
            answer:
              opportunity > 0
                ? `${money(opportunity)} above the regional average`
                : "No amount above the regional benchmark indicated",
            status: opportunity > 0 ? "Above Market" : "Market Average",
            source: MARKET_SRC,
          },
          {
            question: "Percentile ranking",
            answer: overallPercentile?.value ?? "Not established",
            source: MARKET_SRC,
          },
          {
            question: "Regional comparison",
            answer: vstr(market?.zipCodeMarketAnalyzed) ?? "Regional benchmarks applied",
            source: MARKET_SRC,
          },
          {
            question: "Comparable vendors analyzed",
            answer:
              market?.nearbyVendorsCompared != null
                ? `${market.nearbyVendorsCompared}`
                : "Not stated",
            source: MARKET_SRC,
          },
          {
            question: "Market analysis coverage",
            answer: vstr(market?.marketConfidence) ?? "Not stated",
            source: MARKET_SRC,
          },
        ]
      : [];

  /* Investigation 6 — Executive recommendation */
  const scrutinyRecommended =
    anyAdverse ||
    (overallMarket
      ? overallMarket.position === "above" || overallMarket.position === "outlier"
      : false);

  const recommendation = (() => {
    if (anyAdverse && overallMarket?.position === "outlier")
      return {
        label: "Escalate for Management Review",
        tone: "text-red-700",
        caption:
          "Adverse public information was identified alongside charges that are a significant outlier versus the regional market.",
      };
    if (anyAdverse)
      return {
        label: "Additional Investigation Recommended",
        tone: "text-amber-700",
        caption:
          "Adverse public information was identified and should be resolved before this invoice is settled.",
      };
    if (overallMarket?.position === "outlier" || overallMarket?.position === "above")
      return {
        label: "Proceed with Negotiation",
        tone: "text-amber-700",
        caption:
          "The vendor verified cleanly, but billed charges exceed the regional benchmark and support a documented reduction request.",
      };
    if (!publicVerified)
      return {
        label: "Additional Investigation Recommended",
        tone: "text-amber-700",
        caption:
          "The vendor could not yet be independently confirmed against public business records.",
      };
    return {
      label: "Proceed",
      tone: "text-emerald-700",
      caption:
        "The vendor verified cleanly and billed charges are consistent with the regional market.",
    };
  })();

  const recommendationPriority = (() => {
    if (anyAdverse && overallMarket?.position === "outlier")
      return { label: "High", tone: "text-red-700" };
    if (anyAdverse || overallMarket?.position === "outlier")
      return { label: "Elevated", tone: "text-amber-700" };
    if (overallMarket?.position === "above" || !publicVerified)
      return { label: "Standard", tone: "text-navy" };
    return { label: "Routine", tone: "text-emerald-700" };
  })();



  const recommendationAnswers: InvestigationAnswer[] = [
    {
      question: "Can I trust this business?",
      answer: publicVerified
        ? "Yes — independently confirmed"
        : "Established from invoice documentation only",
      status: publicVerified ? "Verified" : "Invoice-Derived",
      source: publicVerified ? GOOGLE_SRC : INVOICE_SRC,
    },
    {
      question: "Can it legally perform this work?",
      answer:
        stateLicensing.verified || licensing.verified
          ? "Licensing or operating authority located"
          : !licenseRequired
            ? "No license legally required for this business category"
            : NOT_YET_VERIFIED,
      status:
        stateLicensing.verified || licensing.verified
          ? "Verified License"
          : !licenseRequired
            ? "Not Applicable"
            : "Not Verified",
      source: stateLicensing.verified ? LICENSE_SRC : FMCSA_SRC,
    },
    {
      question: "Concerning historical behavior?",
      answer: hasInternalHistory
        ? "Prior ClaimGuard reviews were compared against this invoice"
        : "No prior ClaimGuard history on record",
      status: hasInternalHistory ? "Historical Data" : "No Records Identified",
      source: CG_SRC,
    },
    {
      question: "Does public information raise concern?",
      answer: `${reputationRisk.value} public reputation risk`,
      status: bbb.adverse ? "High Risk" : "Low Risk",
      source: BBB_SRC,
    },
    {
      question: "Are the billed charges reasonable?",
      answer: overallMarket
        ? overallMarket.position === "outlier"
          ? "No — a significant outlier versus the regional market"
          : overallMarket.position === "above"
            ? "Partially — charges exceed the regional average"
            : "Yes — consistent with the regional market"
        : "Regional comparison not established",
      status: overallMarket
        ? overallMarket.position === "outlier"
          ? "Market Outlier"
          : overallMarket.position === "above"
            ? "Above Market"
            : "Market Average"
        : undefined,
      source: MARKET_SRC,
    },
    {
      question: "Overall vendor risk",
      answer: riskValue,
      status: riskValue === "Low" ? "Low Risk" : anyAdverse ? "High Risk" : "Reviewed",
      source: "ClaimGuard Intelligence Engine",
    },
    {
      question: "Vendor investigation coverage",
      answer: `${coverageScore}% of intelligence sources returned results — ${coverageBand}`,
      status: coverageBand,
      source: "ClaimGuard Intelligence Engine",
    },
    {
      /* Same benchmark-level figure as Investigation 06 — labelled by its
         actual scope, never with the frozen report-wide vocabulary. */
      question: "Benchmarked charges — amount above benchmark",
      answer:
        opportunity > 0
          ? money(opportunity)
          : "No additional billing concerns identified through Vendor Intelligence.",
      status: opportunity > 0 ? "Above Market" : "Market Average",
      source: MARKET_SRC,
    },
  ];

  const detailFor = (e: SourceEntry) =>
    hasMeaningfulDetail(e) ? (
      <SourceDetailPanel key={e.key} entry={e} />
    ) : (
      <ProviderNotConnectedNotice key={e.key} entry={e} />
    );

  /* Investigative modules, in claims-decision order. Future modules
     (FMCSA, USDOT, corporate filings, court records, liens,
     bankruptcies, OSHA, EPA, consumer-protection or state enforcement
     actions) are added by appending to this array — the chapter needs
     no structural change. */
  const investigationModules: InvestigationModule[] = [
    {
      key: "trust",
      title: "Business Identity",
      available: publicVerified,
      state: publicVerified ? "verified" : "awaiting_provider",
      icon: Building2,
      headline: "Can I trust this business?",
      investigated:
        "Whether the vendor named on the invoice is a real, currently operating business, and whether the invoice details match that business.",
      answers: trustAnswers,
      found: publicVerified
        ? `${businessName} was located as an operating business and matched to the submitted invoice.`
        : `${businessName} was established from the submitted invoice; independent public confirmation is not yet available.`,
      matters:
        "A vendor that cannot be independently identified — or whose public details conflict with the invoice — warrants closer review of who performed the work and who is entitled to payment.",
      completedAt: publicVerified ? investigationCompletedAt : null,
      details: detailFor(google),
      evidenceCount: google.fields.length,

    },
    {
      key: "registration",
      title: "Business Verification",
      available: sos.verified,
      state: sos.verified ? "verified" : "awaiting_provider",
      icon: Landmark,
      headline: "Is the business legally established?",
      investigated:
        "Whether the billing entity is a registered business in good standing, including entity type, formation, and registered agent of record.",
      answers: sos.verified ? registrationAnswers : [],
      pendingProvider: sos.verified
        ? null
        : {
            title: "Secretary of State verification",
            status: registrationGap.status,
            willVerify: registrationAnswers.map((a) => a.question),
          },

      notice: sos.verified ? null : registrationGap.text,
      found: sos.verified
        ? "A corporate registration record was located and reviewed for status, standing, and entity details."
        : registrationGap.text,
      matters:
        "Registration and standing determine whether the billing entity may lawfully contract, invoice, and pursue collection remedies for the work performed.",
      completedAt: sos.verified ? investigationCompletedAt : null,
      details: detailFor(sos),
      evidenceCount: sos.fields.length,

    },
    {
      key: "licensing",
      title: "Licensing & Authority",
      available: stateLicensing.verified || licensing.verified || !licensingApplies,
      state:
        stateLicensing.verified || licensing.verified
          ? "verified"
          : licensingApplies
            ? "awaiting_provider"
            : "no_evidence",
      icon: BadgeCheck,
      headline: "Can this business legally perform this work?",
      investigated:
        "Whether the billed services require a license, permit, or operating authority, and whether the vendor holds one that is current and in good standing.",
      answers:
        stateLicensing.verified || licensing.verified ? licenseAnswers : [],
      pendingProvider:
        stateLicensing.verified || licensing.verified
          ? null
          : {
              title: "State licensing verification",
              status: licensingGap.status,
              willVerify: licenseAnswers.map((a) => a.question),
            },

      notice:
        stateLicensing.verified || licensing.verified
          ? null
          : licensingCoverageNote
            ? `${licensingGap.text} ${licensingCoverageNote}`
            : licensingGap.text,
      found:
        stateLicensing.verified || licensing.verified
          ? "Licensing or operating-authority records were located and reviewed for status, expiration, and disciplinary history."
          : licensingGap.text,

      matters:
        "Licensing and operating authority determine whether the work performed was lawfully authorized, and whether lien or storage remedies are available to the vendor.",
      completedAt:
        stateLicensing.verified || licensing.verified
          ? investigationCompletedAt
          : null,
      details: (

        <>
          {detailFor(stateLicensing)}
          {fmcsaApplicable ? detailFor(licensing) : null}
        </>
      ),
      evidenceCount:
        stateLicensing.fields.length +
        (fmcsaApplicable ? licensing.fields.length : 0),
    },

    {
      key: "history",
      title: "Historical Intelligence",
      available: true,
      state: hasInternalHistory ? "verified" : "informational",
      icon: Gauge,
      headline: "Has this vendor demonstrated concerning historical behavior?",
      investigated:
        "Prior ClaimGuard reviews involving this vendor, historical billing patterns, recurring concerns, negotiated outcomes, and risk trend over time.",
      /* When there is no prior ClaimGuard file on this vendor, every
         answer resolves to the same "no records" state. Rather than repeat
         that card a dozen times, a single plain-English notice explains it
         and the individual data points remain available, unchanged, in the
         collapsed detail group below. */
      answers: hasInternalHistory ? historyAnswers : [],
      notice: hasInternalHistory
        ? null
        : "ClaimGuard has not previously reviewed an invoice from this vendor, so there is no historical pattern to apply to this claim. Every historical data point below therefore returns no records — this is a limit of the file history, not a finding against the vendor. Historical intelligence begins accumulating with this review.",
      secondary: {
        title: hasInternalHistory
          ? "Historical analytics"
          : "Historical data points reviewed",
        answers: hasInternalHistory
          ? historySecondaryAnswers
          : [...historyAnswers, ...historySecondaryAnswers],
      },

      found: hasInternalHistory
        ? "Prior ClaimGuard reviews of this vendor were available and were compared against the current invoice."
        : "This is the first ClaimGuard review on record for this vendor, so no historical pattern could be applied. This section expands automatically as additional reviews accumulate.",
      matters:
        "Repeat billing patterns across invoices identify systemic overcharging that a single invoice cannot establish on its own.",
      completedAt: hasInternalHistory ? investigationCompletedAt : null,
      details: detailFor(internal),
      evidenceCount: internal.fields.length,

    },
    {
      key: "reputation",
      title: "Public Reputation",
      available: bbb.verified || !!reputationSample,
      state:
        bbb.verified || !!reputationSample
          ? bbb.adverse
            ? "verified"
            : "no_evidence"
          : "awaiting_provider",
      icon: ShieldCheck,
      headline: "Does publicly available information increase vendor risk?",
      investigated:
        "Public complaint history, accreditation and rating, published enforcement actions, and any public fraud indicators associated with this vendor.",
      answers: reputationAnswers,
      indicatorGroup: {
        title: "Public risk indicators",
        items: reputationRiskIndicators,
      },

      verdict: {
        label: "Overall Public Reputation Risk",
        value: reputationRisk.value,
        tone: reputationRisk.tone,
        caption: bbb.adverse
          ? "Adverse public indicators were identified and factored into this review."
          : "No adverse public information was identified that increases invoice risk.",
      },
      found: bbb.adverse
        ? "Adverse public reputation indicators were identified and considered in this review."
        : "No material public complaint history affecting this review was identified.",
      matters:
        "A documented pattern of complaints or enforcement activity supports a stronger negotiating posture; the absence of records is never treated as an adverse finding.",
      completedAt: bbb.verified ? investigationCompletedAt : null,
      details: detailFor(bbb),
      evidenceCount: bbb.fields.length,

    },
    {
      key: "market",
      title: "Market Intelligence",
      available: marketRows.length > 0,
      state: marketRows.length > 0 ? "verified" : "awaiting_provider",
      icon: Scale,
      headline: "Are the billed charges reasonable?",
      investigated:
        "Each major charge category compared with verified regional benchmarks, and the resulting financial exposure on this invoice.",
      answers: marketAnswers,
      notice:
        marketRows.length > 0
          ? null
          : "Regional benchmark comparison was not available for this claim, so no market-based conclusion was drawn.",
      verdict: null,
      metrics: overallMarket
        ? [
            /* Benchmark-level scope, not report-wide. See the note on
               marketAnswers above: these labels deliberately avoid the frozen
               report-wide financial vocabulary. Wording only — no value
               shown here is recalculated. */
            {
              label: "Additional Vendor Billing Identified",
              value:
                opportunity > 0 ? money(opportunity) : "None identified",
              caption:
                opportunity > 0
                  ? "How much more this vendor billed than comparable providers charge for the same reviewed services."
                  : "This investigation did not identify any additional billing concerns beyond those already documented in the Market Benchmark chapter.",
              tone: opportunity > 0 ? "text-amber-700" : "text-emerald-700",
              emphasis: true,
            },
            {
              label: "Charges Included In Vendor Market Review",
              value: overallMarket.invoice,
              caption:
                "Total vendor charges included in this market comparison.",
            },
            {
              label: "Comparable Market Value",
              value: overallMarket.average,
              caption:
                "Expected market value for comparable services in this region.",
            },
          ]
        : null,
      found: overallMarket
        ? `The benchmarked charges on this invoice total ${overallMarket.invoice} and were compared against a regional average of ${overallMarket.average} (${overallMarket.difference}). These are benchmark-level figures for the compared charges only, not report-wide totals.`
        : "No regional benchmark comparison was established for this vendor.",
      matters:
        "Market position converts individual line items into a defensible, evidence-backed reduction argument during negotiation.",
      completedAt: marketRows.length > 0 ? investigationCompletedAt : null,
      details: detailFor(marketEntry),
      evidenceCount: marketEntry.fields.length,

    },
    {
      key: "recommendation",
      title: "Investigation Conclusion",
      available: true,
      state: "informational",
      icon: BadgeCheck,
      headline: "Should this invoice receive additional scrutiny?",
      investigated:
        "The combined outcome of every investigation above, translated into a single recommended course of action for this invoice.",
      answers: recommendationAnswers,
      verdict: {
        label: "Executive Recommendation",
        value: recommendation.label,
        tone: recommendation.tone,
        caption: recommendation.caption,
      },
      metrics: [
        {
          label: "Additional Vendor Billing Identified",
          value: opportunity > 0 ? money(opportunity) : "None identified",
          caption:
            opportunity > 0
              ? "How much more this vendor billed than comparable providers charge for the same reviewed services."
              : "This investigation did not identify any additional billing concerns beyond those already documented in the Market Benchmark chapter.",
          tone: opportunity > 0 ? "text-amber-700" : "text-emerald-700",
          emphasis: true,
        },
        {
          label: "Priority Level",
          value: recommendationPriority.label,
          caption: "How urgently this invoice should be reviewed further.",
          tone: recommendationPriority.tone,
        },
        {
          label: "Investigation Coverage",
          value: `${coverageScore}%`,
          caption:
            "Share of the information we attempted to collect that the searched sources actually returned.",
        },
        {
          label: "Overall Vendor Risk",
          value: riskValue,
          caption:
            "How much concern the documented evidence raises about this vendor.",
          tone:
            riskValue === "Low"
              ? "text-emerald-700"
              : riskValue === "Elevated"
                ? "text-red-700"
                : "text-amber-700",
        },
      ],

      found: scrutinyRecommended
        ? "One or more findings support additional scrutiny before this invoice is settled."
        : "No vendor-specific finding supports additional scrutiny beyond the standard review.",
      matters:
        "The recommendation consolidates identity, legal authority, historical behavior, public reputation, and market position into the single decision a claims professional has to make.",
      completedAt: investigationCompletedAt,
    },


  ];

  /* Investigation status milestones. Completed milestones read as
     successes; anything awaiting a provider integration reads as a
     neutral pending status — never as an adverse finding. */
  const timelineSteps: TimelineStep[] = [
    {
      label: "Business verified",
      state: publicVerified ? "complete" : "pending",
    },
    { label: "Invoice matched", state: data?.vendor ? "complete" : "pending" },
    {
      label: "Market analysis completed",
      state: marketRows.length > 0 ? "complete" : "pending",
    },
    { label: "Historical review completed", state: "complete" },
    { label: "Public reputation reviewed", state: "complete" },
    {
      label: "Registration verification",
      state: sos.verified ? "complete" : "pending",
    },
    {
      label: "Licensing verification",
      state: stateLicensing.verified || licensing.verified ? "complete" : "pending",
    },
    { label: "Vendor Investigation Complete", state: "complete" },
  ];

  /* Coverage summary — instantly shows a claims professional what was
     actually established during this investigation. "Awaiting Live
     Provider" means the software intentionally skipped a source that is
     not yet connected; it is never an adverse finding, and sources with
     no data to return are excluded from the coverage calculation. */
  type CoverageState =
    | "Verified"
    | "Coverage Boundary"
    | "Not Available"
    | "Complete";
  /* Field-level completeness. Each investigation declares the fields it
     attempts to establish; coverage is the share of those fields actually
     established, so partial retrieval is credited instead of being
     collapsed into a binary verified / not-verified state. Fields that no
     connected provider can supply are excluded from the denominator so a
     provider gap never reads as an adverse finding. */
  const has = (v: unknown): boolean =>
    typeof v === "string" ? v.trim().length > 0 : v != null && v !== false;

  const coverageItems: Array<{
    label: string;
    state: CoverageState;
    fields: Array<{ label: string; got: boolean }>;
  }> = [
    {
      label: "Business Identity",
      state: publicVerified ? "Verified" : "Coverage Boundary",
      fields: [
        { label: "Business name", got: has(gp?.businessName) },
        { label: "Address", got: has(gp?.address) },
        { label: "Phone", got: has(gp?.phone) },
        { label: "Website", got: has(gp?.website) },
        { label: "Operating status", got: has(gp?.businessStatus) },
        { label: "Category", got: has(gp?.category) },
      ],
    },
    {
      label: "Registration",
      state: sos.verified ? "Verified" : "Coverage Boundary",
      fields: [
        { label: "Legal entity name", got: has(sp?.legalName) },
        { label: "Entity type", got: has(sp?.entityType) },
        { label: "Entity status", got: has(sp?.entityStatus) },
        { label: "Good standing", got: has(sp?.standing) },
        { label: "Formation date", got: has(sp?.formationDate) },
        { label: "Registered agent", got: has(sp?.registeredAgent?.name) },
        { label: "Principal address", got: has(sp?.principalAddress) },
        { label: "State of registration", got: has(sp?.stateOfRegistration) },
      ],
    },
    {
      label: "Licensing",
      state:
        stateLicensing.verified || licensing.verified
          ? "Verified"
          : fmcsaApplicable || licenseRequired
            ? "Coverage Boundary"
            : "Not Available",
      fields: [
        { label: "Requirement determination", got: true },
        { label: "License number", got: !!stateLicensing.verified },
        { label: "License status", got: !!stateLicensing.verified },
        { label: "Expiration", got: !!stateLicensing.verified },
        { label: "Operating authority", got: !!licensing.verified },
      ],
    },
    {
      label: "Public Reputation",
      state:
        bbbRetrieved || hasPublicReviews
          ? "Complete"
          : "Coverage Boundary",
      fields: [
        { label: "Public rating", got: googleRating != null },
        { label: "Review volume", got: googleReviewCount != null },
        { label: "Customer sentiment", got: !!rep?.sentiment },
        { label: "Rating trend", got: !!rep?.ratingTrend },
        { label: "Review growth trend", got: !!rep?.reviewGrowthTrend },
        { label: "Recurring themes", got: !!(rep && (rep.commonComplaints.length || rep.commonCompliments.length)) },
        { label: "Keyword intelligence", got: !!(rep && (rep.positiveKeywords.length || rep.negativeKeywords.length)) },
        { label: "Services mentioned", got: !!rep?.mentionedServices.length },
        { label: "Consistency score", got: rep?.consistencyScore != null },
        { label: "Most recent review", got: !!rep?.mostRecentReviewDate },
        { label: "Profile photos", got: !!rep?.totalPhotos },
        { label: "Years in business", got: !!(sosFormationDate || rep?.yearsInBusiness) },
        { label: "Sentiment summary", got: !!rep?.summary },
        { label: "BBB record", got: bbbRetrieved },
      ],

    },
    {
      label: "Historical Reviews",
      state: hasInternalHistory ? "Complete" : "Not Available",
      fields: [{ label: "Prior ClaimGuard reviews", got: hasInternalHistory }],
    },
    {
      label: "Market Analysis",
      state: marketRows.length > 0 ? "Complete" : "Not Available",
      fields: [
        { label: "Benchmark comparison", got: marketRows.length > 0 },
      ],
    },
  ];
  /* Coverage denominator excludes investigations with nothing to return
     ("Not Available") and those whose provider is not connected, so an
     unconnected registry never counts against the investigation. */
  const coverageScored = coverageItems.filter(
    (c) => c.state !== "Not Available" && c.state !== "Coverage Boundary",
  );

  const scoredFields = coverageScored.flatMap((c) => c.fields);
  const coveragePct = scoredFields.length
    ? Math.round(
        (scoredFields.filter((f) => f.got).length / scoredFields.length) * 100,
      )
    : 0;
  /* Headline coverage is expressed as investigations established out of
     investigations attempted. A percentage computed only over the
     participating sources reads as "100% verified" when a single source
     participated, which overstates the investigation; the fraction cannot. */
  const coverageEstablished = coverageItems.filter(
    (c) => c.state === "Verified" || c.state === "Complete",
  ).length;
  const coverageTotal = coverageItems.length;
  const coverageBarPct = coverageTotal
    ? Math.round((coverageEstablished / coverageTotal) * 100)
    : 0;

  /** Per-investigation completeness, shown on each coverage tile. */
  const completenessOf = (c: (typeof coverageItems)[number]): number | null =>
    c.state === "Not Available" || c.fields.length === 0
      ? null
      : Math.round(
          (c.fields.filter((f) => f.got).length / c.fields.length) * 100,
        );






  return (

    <CGSection
      id="section-vendor"
      number={number}
      eyebrow="Section"
      title="Vendor Intelligence"
      intro="Outcome of the vendor investigation for this claim, followed by what was and was not verified."
      bare={bare}
    >
      {!hasData ? (
        <>
          {/* 1) Vendor Snapshot — the investigation result reads first, then
              the business profile is grouped into scannable sections. */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <Eyebrow tone="navy">Vendor Snapshot</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              Executive overview of the identified vendor.
            </div>

            {/* Focal point — the outcome of the whole investigation. */}
            <div className="mt-5 rounded-2xl border-2 border-navy/20 bg-secondary/50 p-6 shadow-card md:p-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Overall Investigation Result
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className={`h-3.5 w-3.5 shrink-0 rounded-full ${overall.dot}`} aria-hidden />
                <span
                  className={`font-display text-3xl leading-tight md:text-[2.5rem] ${overall.tone}`}
                >
                  {overall.label}
                </span>
              </div>
              <p className="mt-3 max-w-3xl text-[15px] leading-7 text-foreground/80">
                {anyAdverse
                  ? "Adverse public information was identified through the sources reviewed and is documented in detail below."
                  : publicVerified
                    ? "No material adverse public information, regulatory concerns, or historical ClaimGuard intelligence were identified that would materially affect this invoice review. The vendor was independently matched to public business records."
                    : "The vendor was identified from the submitted invoice documentation. Independent public confirmation was limited for the sources reviewed; no adverse information was identified, and unavailable information was not treated as evidence against the vendor."}
              </p>
            </div>

            {/* Business profile — grouped so identity, contact and
                verification read as three distinct blocks. */}
            <div className="mt-6 space-y-5">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy">
                  Business Identity
                </div>
                <div className="mt-2 grid gap-3 md:grid-cols-3">
                  <SnapshotField label="Business Name" value={businessName} />
                  <SnapshotField
                    label="Business Category"
                    value={category || "Not identified"}
                  />
                  {businessStatus ? (
                    <SnapshotField
                      label="Business Status"
                      value={businessStatus}
                      note={businessStatus === "Open" ? "Currently Operating" : null}
                    />
                  ) : null}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy">
                  Contact &amp; Location
                </div>
                <div className="mt-2 grid gap-3 md:grid-cols-3">
                  <SnapshotField label="Phone Number" value={phone} />
                  <SnapshotField label="Address" value={address} />
                  <SnapshotField
                    label="Website"
                    value={website ?? "Not identified"}
                    href={website}
                  />
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy">
                  Verification &amp; ClaimGuard Record
                </div>
                <div className="mt-2 grid gap-3 md:grid-cols-3">
                  <SnapshotField
                    label="Invoice Verified"
                    value={
                      data?.vendor
                        ? "Yes — issuing party on the submitted invoice"
                        : "Not identified"
                    }
                  />
                  <SnapshotField
                    label="Public Business Verified"
                    value={
                      publicVerified ? "Yes — matched to public records" : "Not confirmed"
                    }
                  />
                  <SnapshotField label="ClaimGuard History" value={historyValue} />
                </div>
              </div>
            </div>

          </div>

          {/* Investigation Coverage Notice — displayed once per chapter.
              Presentation only; explains the Awaiting Provider state so the
              explanation is never repeated on individual investigations. */}
          {investigationModules.some((m) => m.state === "awaiting_provider") ? (
            <InvestigationCoverageNotice className="mt-6" />
          ) : null}

          {/* 1b) Executive Investigation Summary — presentation only:
              Section A (overall result), Section B (completed), and
              Section C (unavailable). No value is recomputed here. */}
          {(() => {
            const completedRows = summaryRows.filter((r) => r.available);
            const unavailableRows = summaryRows.filter((r) => !r.available);
            const completedCount = investigationModules.filter(
              (m) => m.available !== false,
            ).length;
            const totalCount = investigationModules.length;
            const refFor = (target?: string) => {
              const idx = target
                ? investigationModules.findIndex(
                    (m) => `vendor-investigation-${m.key}` === target,
                  )
                : -1;
              return idx >= 0
                ? `Investigation ${String(idx + 1).padStart(2, "0")}`
                : null;
            };
            return (
          <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <div className="font-display text-[28px] leading-[1.1] tracking-tight text-navy md:text-[34px]">
              Executive Investigation Summary
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground md:text-base">
              At-a-glance outcome of the vendor investigation
            </p>
            <div className="mt-5 h-px w-full bg-border" />

            {/* Section A — Overall Investigation Result */}
            <div className="mt-7">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Overall Investigation Result
              </div>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                {heroMetrics.map((m) => (
                  <div
                    key={m.label}
                    className="rounded-2xl border border-border bg-secondary/40 p-5 md:p-6"
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {m.label}
                    </div>
                    <div
                      className={`mt-1.5 font-display text-4xl leading-none md:text-5xl ${m.tone}`}
                    >
                      {m.value}
                    </div>
                    {typeof m.meter === "number" ? (
                      <>
                        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border">
                          <div
                            className="h-full rounded-full bg-navy"
                            style={{
                              width: `${Math.max(0, Math.min(100, m.meter))}%`,
                            }}
                          />
                        </div>
                        <div className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-navy">
                          {coverageBand}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {completedCount} of {totalCount} investigations
                          successfully completed
                        </div>
                        <p className="mt-2 text-xs leading-5 text-muted-foreground">
                          Investigations that could not be completed are
                          coverage limits, not adverse findings against this
                          vendor.
                        </p>
                      </>
                    ) : (
                      <div className="mt-2 text-sm font-semibold text-foreground/75">
                        {m.caption}
                      </div>
                    )}
                    {m.explain ? (
                      <p className="mt-3 border-t border-border pt-3 text-xs leading-5 text-muted-foreground">
                        {m.explain}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
              <p className="mt-4 rounded-xl border border-border bg-secondary/30 p-4 text-sm leading-6 text-foreground/85">
                {`Across ${totalCount} investigations, ${completedCount} returned usable results and the documented evidence places this vendor at ${riskValue.toLowerCase()} overall risk.`}
              </p>
            </div>

            {/* Section B — Completed Investigations */}
            {completedRows.length > 0 ? (
              <div className="mt-8 border-t border-border pt-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy">
                  Completed Investigations
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {completedRows.map((r) => {
                    const ref = refFor(r.target);
                    const body = (
                      <>
                        <div className="flex items-start gap-2.5">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                          <div className="min-w-0">
                            <div className="font-display text-base leading-snug text-navy">
                              {r.label}
                            </div>
                            <div className="mt-0.5 text-sm font-semibold text-foreground/80">
                              {r.value}
                            </div>
                            {r.explain ? (
                              <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                                {r.explain}
                              </p>
                            ) : null}
                            {ref ? (
                              <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy">
                                See {ref}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </>
                    );
                    return r.target ? (
                      <button
                        key={r.label}
                        type="button"
                        onClick={() => goToInvestigation(r.target!)}
                        aria-label={`Go to ${r.label} investigation`}
                        className="cursor-pointer rounded-xl border border-border bg-secondary/30 p-4 text-left transition hover:border-navy/30 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 print:hover:border-border print:hover:bg-secondary/30"
                      >
                        {body}
                      </button>
                    ) : (
                      <div
                        key={r.label}
                        className="rounded-xl border border-border bg-secondary/30 p-4"
                      >
                        {body}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Section C — Unavailable Investigations */}
            {unavailableRows.length > 0 ? (
              <div className="mt-8 border-t border-border pt-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Awaiting Provider
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  These investigations could not be completed because the
                  required provider is not connected. Nothing negative was
                  found.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {unavailableRows.map((r) => (
                    <div
                      key={r.label}
                      aria-disabled="true"
                      className="cursor-default rounded-xl border border-border/60 bg-muted/40 p-4"
                    >
                      <div className="flex items-start gap-2.5">
                        <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <div className="font-display text-base leading-snug text-muted-foreground">
                            {r.label}
                          </div>
                          <div className="mt-0.5 text-sm font-semibold text-muted-foreground">
                            {r.value}
                          </div>
                          {r.unavailableNote ? (
                            <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                              {r.unavailableNote}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
            );
          })()}

          {/* 1c) Investigation Coverage — what was actually verified */}
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <Eyebrow tone="navy">Investigation Coverage</Eyebrow>
                <div className="mt-1 font-display text-lg text-navy">
                  What was verified during this investigation
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Sources Established
                </div>
                <div className="font-display text-2xl leading-tight text-navy">
                  {coverageEstablished} of {coverageTotal}
                </div>
                {scoredFields.length > 0 && (
                  <div className="text-[10px] text-muted-foreground">
                    {coveragePct}% of attempted fields retrieved
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-navy"
                style={{ width: `${coverageBarPct}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-foreground/70">
              Sources established counts the investigations that returned usable
              records, measured against every investigation attempted. A source
              marked <strong className="font-semibold text-navy">Awaiting Provider</strong> could
              not be reached because the required provider is not connected, so no
              lookup was performed. Sources with no records to return are
              excluded from the field-level percentage. Neither condition is an
              adverse finding about this vendor.
            </p>
            {[
              {
                title: "Verified Sources",
                blurb:
                  "These sources returned records and directly support the conclusions in this chapter.",
                items: coverageItems.filter(
                  (c) => c.state === "Verified" || c.state === "Complete",
                ),
              },
              {
                title: "Awaiting Provider",
                blurb:
                  "These sources were not available for this claim, so nothing was concluded from them. This is a limit of the search, never a finding against the vendor.",
                items: coverageItems.filter(
                  (c) => c.state !== "Verified" && c.state !== "Complete",
                ),
              },
            ]
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <div key={group.title} className="mt-5">
                  <div className="flex items-center gap-2">
                    {group.title === "Verified Sources" ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" />
                    ) : (
                      <Clock className="h-4 w-4 shrink-0 text-amber-600" />
                    )}
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy">
                      {group.title} ({group.items.length})
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground/70">{group.blurb}</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.items.map((c) => (
                      <div
                        key={c.label}
                        className="rounded-xl border border-border bg-secondary/30 p-4"
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          {c.label}
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          {c.state === "Verified" || c.state === "Complete" ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" />
                          ) : c.state === "Coverage Boundary" ? (
                            <Clock className="h-4 w-4 shrink-0 text-amber-600" />
                          ) : (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-border" />
                          )}
                          <span
                            className={`font-display text-base leading-snug ${
                              c.state === "Verified" || c.state === "Complete"
                                ? "text-emerald-700"
                                : c.state === "Coverage Boundary"
                                  ? "text-amber-700"
                                  : "text-foreground/70"
                            }`}
                          >
                            {c.state === "Coverage Boundary"
                              ? "Awaiting Provider"
                              : c.state === "Not Available"
                                ? "No Evidence Found"
                                : c.state}
                          </span>
                        </div>
                        {completenessOf(c) !== null && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                              <span>Data completeness</span>
                              <span className="text-navy">{completenessOf(c)}%</span>
                            </div>
                            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-border">
                              <div
                                className="h-full rounded-full bg-navy"
                                style={{ width: `${completenessOf(c)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

          </div>





          {/* 2) Investigations — each module answers a claims decision.
              Providers appear only as compact per-finding attribution.
              Additional modules can be appended to investigationModules
              without any structural change to this chapter. */}
          {investigationModules.map((m, i) => {
            const invId = `vendor-investigation-${m.key}`;
            const findingsCount =
              m.answers.length +
              (m.secondary?.answers.length ?? 0) +
              (m.indicatorGroup?.items.length ?? 0);
            return (
            <InvestigationPanel
              key={m.key}
              id={invId}
              open={openInvestigation === invId}
              onOpenChange={(next) =>
                setOpenInvestigation(next ? invId : null)
              }
              findingsCount={findingsCount}
              evidenceCount={m.evidenceCount ?? 0}
              available={m.available !== false}
              state={m.state}
              title={m.title}
              icon={m.icon}
              eyebrow={`Investigation ${String(i + 1).padStart(2, "0")}`}
              headline={m.headline}
              investigated={m.investigated}
              answers={m.answers}
              found={m.found}
              matters={m.matters}
              notice={m.notice}
              verdict={m.verdict}
              metrics={m.metrics}
              completedAt={m.completedAt}
              pendingProvider={m.pendingProvider}
              secondary={m.secondary}
              indicatorGroup={m.indicatorGroup}



            >
              {m.details}
            </InvestigationPanel>
            );
          })}

          {/* 3) Investigation timeline — the workflow that produced the
              conclusions above. */}
          <InvestigationTimeline steps={timelineSteps} />


          {/* 4) Supporting evidence sources — reference index only */}
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <Eyebrow tone="navy">Supporting Evidence Sources</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              Sources consulted in support of the investigations above
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">What it was used for</th>
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2 pr-4">Coverage Weight</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const Icon = e.icon;
                    const weight = PROVIDER_WEIGHTS[e.key] ?? 10;
                    const purpose =
                      (
                        {
                          google: "Confirm the business exists and matches the invoice",
                          sos: "Confirm the billing entity is legally registered",
                          licensing: "Confirm federal operating authority",
                          stateLicensing: "Confirm state licensing for the billed work",
                          bbb: "Check complaints, alerts and enforcement history",
                          internal: "Compare against prior ClaimGuard reviews of this vendor",
                          market: "Compare billed charges against regional pricing",
                        } as Record<string, string>
                      )[e.key] ?? "Supporting verification";
                    return (
                      <tr key={e.key} className="border-b border-border/60 last:border-0 align-top">
                        <td className="py-3 pr-4">
                          <span className="flex items-center gap-2 font-medium text-navy">
                            <span
                              className={`h-4 w-1 shrink-0 rounded-full ${
                                PROVIDER_ACCENTS[e.key] ?? "bg-border"
                              }`}
                            />
                            <Icon className="h-4 w-4 shrink-0" />
                            {e.title}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-foreground/70">{purpose}</td>
                        <td className="py-3 pr-4 text-foreground/70">
                          {PROVIDER_LABELS[e.key] ?? "ClaimGuard Intelligence"}
                        </td>
                        <td className="py-3 pr-4 text-foreground/80">{weight}%</td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={e.status} />
                        </td>
                        <td className="py-3 text-foreground/80">{e.result}</td>
                      </tr>
                    );
                  })}

                </tbody>

              </table>
            </div>
          </div>






          {/* 6) Unavailable Verification Sources */}
          {unverified.length > 0 ? (
            <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
              <Eyebrow tone="navy">Unavailable Verification Sources</Eyebrow>
              <div className="mt-1 font-display text-lg text-navy">
                Sources that could not be confirmed or are not yet connected
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pr-4">Source</th>
                      <th className="py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unverified.map((e) => (
                      <tr key={e.key} className="border-b border-border/60 last:border-0">
                        <td className="py-2.5 pr-4 font-medium text-navy">{e.title}</td>
                        <td className="py-2.5 text-foreground/80">{e.unverifiedReason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-foreground/70">
                Unavailable information was not treated as evidence against the vendor.
              </p>
            </div>
          ) : null}





          {/* 7) Investigation Impact */}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: BookOpen,
                label: "Evidence Used",
                text: publicVerified
                  ? "Vendor identity was corroborated against public business records in addition to the submitted invoice documentation and regional market intelligence. No unsupported assumptions contributed to these conclusions."
                  : "Vendor identity was established from the submitted invoice documentation and evaluated against regional market intelligence. No independent public business record was retrieved for this vendor, and no unsupported assumptions were substituted in its place.",
              },
              {
                icon: Scale,
                label: "Risk Assessment",
                text: anyAdverse
                  ? "Adverse vendor-specific indicators were identified and are detailed above. Financial conclusions remain based on the verified invoice documentation and regional market comparisons."
                  : publicVerified
                    ? "No material vendor-specific indicators were identified that independently increase invoice risk. Financial conclusions are based on verified invoice documentation, public records, and regional market comparisons."
                    : "No material vendor-specific risk indicators were identified in the records that were retrieved. Financial conclusions rest on the verified invoice documentation and regional market comparisons; the absence of public records was not treated as adverse.",
              },
              {
                icon: Gauge,
                label: "Investigation Coverage",
                text: `ClaimGuard assigns a ${coverageBand} determination based on ${contributors.length} contributing evidence stream${contributors.length === 1 ? "" : "s"} (${contributors.join(", ")}). Sources that did not participate carry no weight and neither raise nor lower this determination.`,
              },

            ].map((it) => (
              <div
                key={it.label}
                className="rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <div className="flex items-center gap-2">
                  <div className="grid h-8 w-8 place-items-center rounded-lg bg-secondary/60 text-navy shadow-card">
                    <it.icon className="h-4 w-4" />
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {it.label}
                  </div>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{it.text}</p>
              </div>
            ))}
          </div>

          {/* 7b) Confidence Contributors — weighted so the confidence
              determination is explainable rather than assertive. */}
          {weightedContributors.length > 0 ? (
            <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
              <Eyebrow tone="navy">Investigation Coverage Contributors</Eyebrow>
              <div className="mt-1 font-display text-lg text-navy">
                How the {coverageScore}% source coverage determination was weighted
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {weightedContributors.map((c) => (
                  <div key={c.label} className="rounded-xl border border-border bg-secondary/30 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-sm text-foreground/85">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" />
                        {c.label}
                      </span>
                      <span className="font-display text-base text-navy">
                        {c.share}%
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
                      <div
                        className="h-full rounded-full bg-navy"
                        style={{ width: `${c.share}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground/70">
                Weights reflect the relative contribution of each participating
                source. Sources that did not participate carry no weight and are
                omitted entirely.
              </p>
            </div>
          ) : null}

          {/* 7c) Investigation Integrity Statement — what evidence did and
              did not inform the conclusions above. */}
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <Eyebrow tone="navy">Investigation Integrity Statement</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              What informed these conclusions — and what did not
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-secondary/30 p-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Independent evidence used
                </div>
                <ul className="mt-2 space-y-2 text-sm text-foreground/85">
                  {[
                    "Submitted invoice documentation and extracted line items",
                    ...contributors.filter((c) => c !== "Internal Validation"),
                    "Documented public records located during this investigation",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Not used in these conclusions
                </div>
                <ul className="mt-2 space-y-2 text-sm text-foreground/85">
                  {[
                    "Assumptions generated without a documented source",
                    "Unverified or anonymous third-party commentary",
                    "Sources awaiting a live provider connection",
                    "The absence of a record, which is never treated as adverse",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>



          {/* 8) Conclusion panel — answers the three questions a decision
              maker actually asks, then closes with the corroboration note. */}
          <div className="mt-6 rounded-2xl border-2 border-navy/20 bg-secondary/50 p-6 shadow-card md:p-8">
            <Eyebrow tone="navy">Vendor Investigation Conclusion</Eyebrow>
            <div className="mt-1 font-display text-lg text-navy">
              The three questions this investigation answers
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {[
                {
                  q: "Can this vendor be trusted?",
                  a: publicVerified
                    ? "Yes — the vendor's identity was independently verified and public business information matched the submitted invoice."
                    : "Partially — the vendor's identity was established from the submitted invoice, but independent public confirmation was limited for the sources reviewed.",
                  tone: publicVerified ? "text-emerald-700" : "text-amber-700",
                },
                {
                  q: "Does this vendor present risk?",
                  a: anyAdverse
                    ? `Yes — adverse public information was identified and is detailed above. Overall vendor risk: ${riskValue}.`
                    : `No adverse public information materially affected this review. Overall vendor risk: ${riskValue}.`,
                  tone: anyAdverse ? "text-red-700" : "text-emerald-700",
                },
                {
                  q: "Does this change the money?",
                  a: "No — the vendor investigation did not alter any financial conclusion in this report. All amounts remain those established by the invoice documentation and regional market comparison.",
                  tone: "text-navy",
                },
              ].map((item) => (
                <div
                  key={item.q}
                  className="rounded-xl border border-border bg-card p-5 shadow-card"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {item.q}
                  </div>
                  <p className={`mt-2 text-sm leading-6 ${item.tone}`}>{item.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-foreground/70">
              {coverageEstablished > 1
                ? `Corroborated across ${coverageEstablished} independent investigations that returned records.`
                : "Corroborated by one investigation that returned records; the remaining sources sit outside currently connected coverage, which is not an adverse finding."}
            </p>
          </div>

          {/* 9) Investigation Methodology */}
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <Eyebrow tone="navy">Methodology &amp; Standards</Eyebrow>
            <ul className="mt-4 space-y-2 text-sm text-foreground/85">
              {[
                "Independent evidence only.",
                "Missing information is never treated as adverse evidence.",
                "Conclusions rely only on documented findings.",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-navy" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {reportMeta.map((m) => (
                <div
                  key={m.label}
                  className="rounded-xl border border-border bg-secondary/30 p-4"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {m.label}
                  </div>
                  <div className="mt-1 text-sm font-medium text-navy">{m.value}</div>
                </div>
              ))}
            </div>

            {/* Appendix — status badge reference, collapsed by default so it
                never consumes a full page of the report. */}
            <details className="mt-5 rounded-xl border border-border bg-secondary/30 p-4">
              <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Appendix — Status Badge Reference
              </summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(
                  [
                    ["Verified", "Information successfully retrieved from a connected provider."],
                    ["High Confidence", "Supported by multiple independent data sources."],
                    ["Regional Market Database", "Analysis based on verified regional comparison data."],
                    ["Better Business Bureau Listing", "A public BBB listing was identified, but detailed records were not retrieved."],
                    ["No Records Identified", "Search completed but no matching records were found."],
                    ["Not Connected", "A live provider integration has not yet been enabled."],
                  ] as Array<[SourceStatus, string]>
                ).map(([status, text]) => (
                  <div
                    key={status}
                    className="rounded-xl border border-border bg-card p-3"
                  >
                    <StatusBadge status={status} />
                    <p className="mt-2 text-xs text-foreground/75">{text}</p>
                  </div>
                ))}
              </div>
            </details>
          </div>


          {/* 10) Chapter completion summary — investigative checklist close */}
          <div className="mt-6 rounded-2xl border border-emerald-600/25 bg-emerald-50/60 p-5 shadow-card md:p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              <div>
                <div className="font-display text-base text-navy">
                  Vendor Intelligence Completed Successfully
                </div>
                <div className="mt-1 text-sm text-foreground/80">
                  Source coverage: {coverageBand}
                </div>
                <div className="mt-1 text-sm text-foreground/80">
                  {anyAdverse
                    ? "Adverse findings above remain relevant to negotiation strategy."
                    : "No unresolved vendor-specific investigative findings remain."}
                </div>
              </div>
            </div>
          </div>

          {/* 11) Subtle report progress indicator.
              The chapter position comes from the master chapter registry,
              not the local `number` prop: in bare mode the wrapper supplies
              numbering, so the prop is a placeholder and would render the
              wrong position ("Chapter 1 of 12") in the footer. */}
          {(() => {
            const registryNumber =
              getChapterByInternalId("vendor")?.displayNumber ?? Number.NaN;
            const idx = Number.isFinite(registryNumber)
              ? registryNumber
              : Number.parseInt(String(number), 10);
            if (!Number.isFinite(idx) || idx <= 0) return null;
            const pct = Math.round((idx / TOTAL_CHAPTERS) * 100);
            return (
              <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                <span className="font-semibold uppercase tracking-[0.16em]">
                  Report Progress
                </span>
                <span>
                  Chapter {idx} of {TOTAL_CHAPTERS}
                </span>
                <div className="h-1 w-28 overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full rounded-full bg-navy"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span>{pct}% Complete</span>
              </div>
            );
          })()}


          <CoverageNote
            icon={Info}
            message="Vendor intelligence becomes more valuable as additional invoices from the same vendor are reviewed. ClaimGuard does not infer vendor characteristics or risk without documented evidence."
          />

        </>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <VendorTile
            label="Vendor Risk Score"
            value={data!.riskScore !== null ? `${data!.riskScore}` : "—"}
            hint={data!.vendor}
          />
          <VendorTile
            label="Overcharge Frequency"
            value={
              data!.overchargeFrequencyPct !== null
                ? `${data!.overchargeFrequencyPct}%`
                : "—"
            }
            hint="Last 12 months"
          />
          <VendorTile
            label="Average Savings"
            value={data!.averageSavings ?? "—"}
            hint="Historical mean"
          />
          <VendorTile
            label="Previous Reviews"
            value={
              data!.previousReviews !== null ? `${data!.previousReviews}` : "—"
            }
            hint="ClaimGuard file count"
          />
          {data!.marketPosition && (
            <VendorTile
              label="Compared With Regional Providers (Vendor Sources)"
              value={data!.marketPosition}
              hint="Vendor-source coverage only"
            />
          )}
        </div>
      )}
    </CGSection>
  );
}

function VendorTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl text-navy">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 4) Supporting Evidence Index                                */
/* ─────────────────────────────────────────────────────────── */

export type EvidenceCounts = {
  stateLaws: number;
  regulations: number;
  carrierGuidelines: number;
  vendorHistory: number;
  marketData: number;
  photos: number;
  gpsEvidence: number;
  communications: number;
  aiAnalysis: number;
  humanReview: number;
  missingDocs: number;
  timeline: number;
};

const INDEX_ITEMS: Array<{
  key: keyof EvidenceCounts;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  anchor: string;
}> = [
  { key: "stateLaws", label: "State Laws", ...chapterIndexTarget("state-law") },
  { key: "regulations", label: "Regulations", ...chapterIndexTarget("regulation") },
  { key: "carrierGuidelines", label: "Carrier Guidelines", ...chapterIndexTarget("carrier") },
  { key: "marketData", label: "Local Market Data", ...chapterIndexTarget("market") },
  { key: "vendorHistory", label: "Vendor History", ...chapterIndexTarget("vendor") },
  { key: "photos", label: "Photos", ...chapterIndexTarget("case-brief") },
  { key: "gpsEvidence", label: "GPS / Distance", ...chapterIndexTarget("case-brief") },
  { key: "timeline", label: "Timeline Analysis", ...chapterIndexTarget("timeline") },
  { key: "communications", label: "Communications", ...chapterIndexTarget("supporting-evidence") },
  { key: "aiAnalysis", label: "ClaimGuard Analysis", ...chapterIndexTarget("executive-decision") },
  { key: "humanReview", label: "Human Review", ...chapterIndexTarget("conclusion") },
  { key: "missingDocs", label: "Missing Documentation", ...chapterIndexTarget("supporting-evidence") },
];

function chapterIndexTarget(internalId: string) {
  const chapter = getChapterByInternalId(internalId);
  return { icon: chapter.icon, anchor: `#${chapter.anchorId}` };
}

export function EvidenceIndexTiles({
  counts,
  activeOnly = false,
}: {
  counts: EvidenceCounts;
  activeOnly?: boolean;
}) {
  const items = activeOnly
    ? INDEX_ITEMS.filter(({ key }) => (counts[key] ?? 0) > 0)
    : INDEX_ITEMS;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map(({ key, label, icon: Icon, anchor }) => {
          const n = counts[key] ?? 0;
          const active = n > 0;
          return (
            <a
              key={key}
              href={anchor}
              className={`group flex items-center gap-3 rounded-2xl border p-4 transition ${
                active
                  ? "border-border bg-card shadow-card hover:border-trust hover:shadow-elevated"
                  : "border-dashed border-border bg-secondary/30"
              }`}
            >
              <div
                data-card-padding="tight"
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                  active
                    ? "bg-trust-soft text-trust"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <Icon className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-navy">{label}</div>
                <div className="text-[11px] text-muted-foreground">
                  {active ? `${n} item${n === 1 ? "" : "s"}` : "No items"}
                </div>
              </div>
              {active && (
                <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:text-trust" />
              )}
            </a>
          );
        })}
      </div>
      <div className="mt-6 flex items-center justify-between rounded-2xl border border-border bg-secondary/50 p-4 text-sm">
        <span className="font-semibold text-navy">Total supporting evidence items</span>
        <span className="font-display text-2xl text-navy">{total}</span>
      </div>
    </>
  );
}

export function SupportingEvidenceIndex({
  counts,
  number,
}: {
  counts: EvidenceCounts;
  number: string;
}) {
  const packageIdentity = usePdfPackageIdentity();
  return (
    <CGSection
      id="section-evidence-index"
      number={number}
      eyebrow="Section"
      title="Supporting Evidence Index"
      intro={`Every verified source that contributed to this ${packageIdentity.title}. Click any category to jump to its section.`}

    >
      <EvidenceIndexTiles counts={counts} />
    </CGSection>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 5) Version History                                          */
/* ─────────────────────────────────────────────────────────── */

export type VersionEntry = {
  version: string;
  date: string;
  changes: string;
  publishedBy: string;
};

export function VersionHistory({
  entries,
  number,
  title = "Version History",
  intro = "This Claim Intelligence Package™ is a living document. New evidence or updated recommendations may result in a new version.",
  eyebrow,
}: {
  entries: VersionEntry[];
  number?: string;
  title?: string;
  intro?: string;
  eyebrow?: string;
}) {
  return (
    <CGSection
      id="section-version"
      number={number}
      eyebrow={eyebrow ?? (number ? "Section" : undefined)}
      title={title}
      intro={intro}
    >

      {entries.length === 0 ? (
        <EmptyStateCard message="No version history recorded for this package." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <table className="w-full text-left text-sm">
            <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">Version</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Changes</th>
                <th className="px-4 py-3 font-semibold">Published By</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-navy">{e.version}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.date}</td>
                  <td className="px-4 py-3 text-foreground/80">{e.changes}</td>
                  <td className="px-4 py-3 text-foreground/80">{e.publishedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CGSection>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 6) How To Use This Package                                  */
/* ─────────────────────────────────────────────────────────── */

const HOW_TO_STEPS = [
  {
    icon: ShieldCheck,
    title: "Understand the Invoice",
    body: "See exactly what you were billed for, line by line, in plain language.",
  },
  {
    icon: BadgeCheck,
    title: "Compare Against the Market",
    body: "Every charge is measured against real regional rates and legal limits.",
  },
  {
    icon: BarChart3,
    title: "Verify the Vendor",
    body: "Confirm who billed you, their licensing, and their billing history.",
  },
  {
    icon: Scale,
    title: "Review the Supporting Evidence",
    body: "Statutes, regulations, carrier guidelines and source documents behind every conclusion.",
  },
  {
    icon: UserCheck,
    title: "Take Action with Confidence",
    body: "A defensible settlement position you can put in front of any counterparty.",
  },
];

export function HowToUsePackage({ number }: { number?: string }) {
  return (
    <CGSection
      id="section-howto"
      number={number}
      eyebrow={number ? "Section" : undefined}
      title="How To Use This Package"
      intro="Designed so you can act in ten seconds, or drill down to every underlying source."
    >
      {/* 3 + 2 balanced layout: a 6-column rail lets every card span 2 columns
          (identical widths), with the final pair offset by one column so the
          second row sits centered under the first. */}
      <div className="grid gap-4 md:grid-cols-2 lg:auto-rows-fr lg:grid-cols-6">
        {HOW_TO_STEPS.map(({ icon: Icon, title, body }, i) => (
          <div
            key={i}
            className={`flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-card lg:col-span-2 ${
              i === 3 ? "lg:col-start-2" : ""
            }`}
          >
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-trust">
              Step {i + 1}
            </div>
            <div className="mt-3 grid h-10 w-10 place-items-center rounded-xl bg-trust-soft text-trust">
              <Icon className="h-5 w-5" />
            </div>
            <div className="mt-3 text-pretty font-display text-lg leading-tight text-navy">
              {title}
            </div>
            <p className="mt-1 text-pretty text-sm text-muted-foreground">{body}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-trust-soft p-5 text-sm text-navy">
        <span className="font-semibold">This package is designed to help you</span>{" "}
        make a confident decision and negotiate successfully.
      </div>
    </CGSection>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 7) Thank You                                                */
/* ─────────────────────────────────────────────────────────── */

